import React, { useState, useRef, useCallback, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FlowChartContext } from '../../app/providers/FlowChartContext';
import { TicketContext } from '../../app/providers/TicketContext';
import Editor from "@monaco-editor/react";
import mxgraph from 'mxgraph';
import gridIcon from '../../assets/toolbar-icon/grid.gif';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';
// FlowChartTabBar kaldırıldı — tab geçişi artık code editor tab'larından yapılıyor
import FlowChartToolbar from './components/FlowChartToolbar';
import FlowChartSidebar from './components/FlowChartSidebar';
import FlowChartGraphArea from './components/FlowChartGraphArea';
import FlowChartCodeEditorPanel from './components/FlowChartCodeEditorPanel';
import FloatingCodeEditor from './components/FloatingCodeEditor';
import FlowChartModals from './components/FlowChartModals';
import FlowChartFunctionWindows from './components/FlowChartFunctionWindows';
import FlowChartPromptWindows from './components/FlowChartPromptWindows';
import FlowChartStatusBar from './components/FlowChartStatusBar';
import FlowChartTerminalDock from './components/FlowChartTerminalDock';
import useTabManager, { hashContents } from './hooks/useTabManager';
import useIncludeManager from './hooks/useIncludeManager';
import useFunctionWindows from './hooks/useFunctionWindows';
import useAIHelpers from './hooks/useAIHelpers';
import {
  generateSemanticGraphWithAutoRepair,
  compileSemanticGraphToMxData,
} from './utils/semanticFlowchartAI';

const { mxGraph, mxClient, mxRubberband, mxEvent, mxUtils, mxPoint, mxConnectionConstraint, mxConstants, mxShape, mxCellRenderer, mxCellOverlay, mxImage } = mxgraph();

// Custom Triangle Shape for mxGraph
function mxTriangleShape() {
  mxShape.call(this);
}
mxUtils.extend(mxTriangleShape, mxShape);
mxTriangleShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  c.begin();
  c.moveTo(w / 2, 0);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.close();
  c.fillAndStroke();
};
mxTriangleShape.prototype.getLabelBounds = function(rect) {
  const bounds = new mxPoint(rect.x, rect.y);
  bounds.width = rect.width;
  bounds.height = rect.height;
  bounds.y += rect.height * 0.25;
  bounds.height *= 0.75;
  return bounds;
};

// Custom Predefined Process Shape (double border rectangle) for mxGraph
function mxPredefinedProcessShape() {
  mxShape.call(this);
}
mxUtils.extend(mxPredefinedProcessShape, mxShape);
mxPredefinedProcessShape.prototype.paintVertexShape = function(c, x, y, w, h) {
  c.translate(x, y);
  const inset = 8;
  // Outer rectangle
  c.rect(0, 0, w, h);
  c.fillAndStroke();
  // Inner rectangle (double border effect)
  c.begin();
  c.moveTo(inset, inset);
  c.lineTo(w - inset, inset);
  c.lineTo(w - inset, h - inset);
  c.lineTo(inset, h - inset);
  c.close();
  c.stroke();
};
mxPredefinedProcessShape.prototype.getLabelBounds = function(rect) {
  const bounds = new mxPoint(rect.x, rect.y);
  bounds.width = rect.width;
  bounds.height = rect.height;
  bounds.x += 8;
  bounds.y += 8;
  bounds.width -= 16;
  bounds.height -= 16;
  return bounds;
};

// Attach icon oluşturma fonksiyonu - mxGraph overlay için
// Base64 encoded SVG icon (yeşil daire içinde paperclip)
const createAttachIcon = () => {
  // SVG icon'u base64'e çevir - paperclip icon (basit ve görünür)
  const svgString = `
    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="9" fill="#4caf50" stroke="white" stroke-width="1.5"/>
      <path d="M8 6.5c-1.5 0-2.5 1-2.5 2.5v4c0 1.5 1 2.5 2.5 2.5s2.5-1 2.5-2.5v-3c0-0.5 0.5-1 1-1s1 0.5 1 1v3c0 1.5-1 2.5-2.5 2.5s-2.5-1-2.5-2.5v-4c0-1.5 1-2.5 2.5-2.5" 
            stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>
  `.trim();
  
  const base64Svg = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  
  // mxImage kullanarak icon oluştur
  return new mxImage(base64Svg, 20, 20);
};

/**
 * FlowChartDevelopmentScreen - mxGraph Implementation
 * 
 * Modern, kaliteli ve stabil bir flowchart development environment.
 * mxGraph kullanarak tüm özellikleri destekler:
 * - 8 handle noktası (4 köşe + 4 kenar orta) connection constraint'ler ile
 * - Editable node ve edge labels
 * - Design Mode toggle
 * - Shape ekleme (triangle, cylinder, preProcess, rectangle, rhombus, startCircle, stopCircle)
 * - Node ve Edge silme
 * - Code Editor (Monaco Editor)
 * - Save/Load functionality
 * - Context menu
 * - Node resizing
 * - Edge connection ve update
 * - Keyboard shortcuts
 * - Waypoint/breakpoint desteği (mxGraph'te edge'ler zaten bendable)
 */
const FlowChartDevelopmentScreen = ({ 
  workflowData, 
  onClose
}) => {
  
  // ========== STATE MANAGEMENT ==========
  const [isDesignMode, setIsDesignMode] = useState(() => {
    try {
      const saved = localStorage.getItem('flowchart-design-mode');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  /* ── Çoklu code editor tab ── */
  const [editorTabs, setEditorTabs] = useState([]);          // [{ shapeId, shapeLabel }]
  const [floatingEditors, setFloatingEditors] = useState([]); // [{ id, shapeId, shapeLabel, code, x, y, w, h }]
  const [showTerminal, setShowTerminal] = useState(false);
  const [code, setCode] = useState("");
  const [isSavingScript, setIsSavingScript] = useState(false);
  const [saveScriptStatus, setSaveScriptStatus] = useState(null);
  const [terminalInput, setTerminalInput] = useState('');
  const [terminalLines, setTerminalLines] = useState(() => ([
    { id: 'ready', type: 'system', text: '$ ready' },
    { id: 'hint', type: 'muted', text: '# AI terminal hazır' },
  ]));
  const [isTerminalBusy, setIsTerminalBusy] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [isApplyingChange, setIsApplyingChange] = useState(false);
  const [shapeColor, setShapeColor] = useState('#FFFF00');
  const [defaultText, setDefaultText] = useState('Yeni Şekil');
  const [selectedShapeLabel, setSelectedShapeLabel] = useState('');
  // Code editor'ın kendi shape takibi — sol tıktan BAĞIMSIZ
  // Sadece sağ-tık "Code Editor" ve editör tab tıklamasıyla güncellenir
  const [codeEditorShapeId, setCodeEditorShapeId] = useState(null);
  const [codeEditorShapeLabel, setCodeEditorShapeLabel] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  // Move Request takibi: { state: 'idle'|'pending'|'success'|'error', startTime, endTime, durationMs, message }
  const [moveRequestStatus, setMoveRequestStatus] = useState({ state: 'idle' });
  const [selectedShapeColor, setSelectedShapeColor] = useState('#FFFF00');
  const [rulerState, setRulerState] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const navigate = useNavigate();
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const graphContainerRef = useRef(null);
  const graphRef = useRef(null);
  const rubberbandRef = useRef(null);
  const workflowIdRef = useRef(workflowData?.workflow?.id || null);
  // Viewport reset fix: aktif tab değişimini takip et (save'de tabs güncellense de graph yeniden init'lenmemeli)
  const graphInitActiveTabRef = useRef(null);
  // Copy-paste clipboard (vertex'lerin kopyası)
  const clipboardRef = useRef([]);
  // Son eklenen şeklin koordinatları (yeni şekil buraya yakın eklensin)
  const lastAddedShapePositionRef = useRef(null);
  const shapeCodeCacheRef = useRef({});
  const promptCacheRef = useRef({});
  const isUpdatingEditorRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const previousSelectedShapeRef = useRef(null);
  const previousActiveTabIdRef = useRef(null);
  const handleSaveRef = useRef(null);
  // Sağ-tık "Code Editor" ile explicit olarak açıldığında true yapılır;
  // useEffect tab eklemeyi bu flag olmadan yapmaz (sol tıkla otomatik açılmaz)
  const openCodeEditorExplicitlyRef = useRef(false);
  // Editör içeriğinin güncellenmesine izin verilen durumlar:
  // sağ-tık "Code Editor" veya editör tab'ına tıklama. Sol tıkla değil.
  const editorInitiatedShapeChangeRef = useRef(false);
  // Flowchart <-> code editor izolasyonu için runtime debugger
  const debugLogRef = useRef([]);
  const debugSeqRef = useRef(0);
  const debugMaxEntries = 4000;
  const debugContextRef = useRef({
    activeTabId: null,
    selectedShape: null,
    codeEditorShapeId: null,
  });
  const pushDebugLog = useCallback((type, payload = {}) => {
    const ctx = debugContextRef.current || {};
    const entry = {
      seq: ++debugSeqRef.current,
      at: new Date().toISOString(),
      type,
      activeTabId: ctx.activeTabId || null,
      selectedShape: ctx.selectedShape || null,
      codeEditorShapeId: ctx.codeEditorShapeId || null,
      ...payload,
    };
    const arr = debugLogRef.current;
    arr.push(entry);
    if (arr.length > debugMaxEntries) {
      arr.splice(0, arr.length - debugMaxEntries);
    }
    // PERFORMANS: Sadece anomaly'leri console'a yaz.
    // Her keystroke'ta console.log çağrısı DevTools açıkken ciddi FPS düşüşüne
    // sebep oluyordu. Tüm log geçmişine erişmek için:
    // window.__FLOWCHART_DEBUG__.getLogs() kullanılabilir.
    if (String(type || '').includes('anomaly')) {
      // eslint-disable-next-line no-console
      console.warn('[FlowDebug]', entry);
    }
  }, []);
  const onBeforeTabLeaveCallback = useCallback(() => {
    // Tab degisimindeki otomatik kayit sessiz calisir (toast yok)
    if (handleSaveRef.current) handleSaveRef.current({ showToastFeedback: false, force: true, source: 'auto-tab-switch' });
  }, []);
  useEffect(() => {
    try {
      window.__FLOWCHART_DEBUG__ = {
        getLogs: () => [...debugLogRef.current],
        clear: () => { debugLogRef.current = []; debugSeqRef.current = 0; },
        exportJson: () => {
          const body = JSON.stringify(debugLogRef.current, null, 2);
          const blob = new Blob([body], { type: 'application/json;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `flowchart-debug-${Date.now()}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
      };
    } catch (_e) { /* ignore */ }
    return () => {
      try { delete window.__FLOWCHART_DEBUG__; } catch (_e) { /* ignore */ }
    };
  }, []);
  const { saveWorkflow, selectedShape, setSelectedShape, saveScript, requestFiles } = useContext(FlowChartContext);
  const { selectedTicketId, SendEvent } = useContext(TicketContext);
  const {
    aiStatus,
    setAiStatus,
    showAIPrompt,
    setShowAIPrompt,
    aiPrompt,
    setAiPrompt,
    isGeneratingCode,
    aiMode,
    setAiMode,
    selectedCode,
    setSelectedCode,
    getOpenAIKey,
    getAICodeSuggestions,
    generateCodeFromPrompt,
    handleGenerateCode,
    handleOpenAIPrompt,
  } = useAIHelpers({
    code,
    setCode,
    selectedShape: codeEditorShapeId,
    editorRef,
    monacoRef,
    shapeCodeCacheRef,
    isUpdatingEditorRef,
  });

  const {
    showIncludeModal,
    setShowIncludeModal,
    selectedIncludeShape,
    setSelectedIncludeShape,
    includeFoldersData,
    setIncludeFoldersData,
    isLoadingIncludeData,
    selectedIncludePath,
    setSelectedIncludePath,
    selectedFolder,
    setSelectedFolder,
    includeFilesData,
    setIncludeFilesData,
    isLoadingFiles,
    selectedFile,
    setSelectedFile,
    shapeIncludePaths,
    setShapeIncludePaths,
    shapeIncludeFolders,
    setShapeIncludeFolders,
    shapeOriginalLabels,
    setShapeOriginalLabels,
    shapesWithInclude,
    setShapesWithInclude,
    includeError,
    setIncludeError,
    showIncludeInfoModal,
    setShowIncludeInfoModal,
    includeInfoData,
    setIncludeInfoData,
    shapeIncludePathsRef,
    shapeIncludeFoldersRef,
  } = useIncludeManager({ selectedTicketId, navigate, fetchWrapper });
  const [showSaveLog, setShowSaveLog] = useState(false);
  const [saveLogEntries, setSaveLogEntries] = useState([]);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [pendingSavePreview, setPendingSavePreview] = useState(null);
  const [codePanelWidth, setCodePanelWidth] = useState(50); // Code panel genişliği (yüzde)
  const [isResizing, setIsResizing] = useState(false);
  const [functionLibrary, setFunctionLibrary] = useState(null); // Function kütüphanesi verisi
  const [isLoadingFunctionLibrary, setIsLoadingFunctionLibrary] = useState(false); // Function library yüklenirken
  const [showFunctionLibraryModal, setShowFunctionLibraryModal] = useState(false); // Function library modal açık/kapalı
  const [functionLibraryError, setFunctionLibraryError] = useState(null); // Function library hata mesajı
  const [toast, setToast] = useState(null); // { type, message, details? }
  const toastTimerRef = useRef(null);
  const showToast = useCallback((type, message, details = null) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message, details });
    toastTimerRef.current = setTimeout(() => setToast(null), details ? 8000 : 4000);
  }, []);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsModalData, setDetailsModalData] = useState(null);
  const [isLoadingFxGroups, setIsLoadingFxGroups] = useState(false); // Fx groups yüklenirken
  const [showFxGroupsModal, setShowFxGroupsModal] = useState(false); // Fx groups modal açık/kapalı
  const [fxGroups, setFxGroups] = useState([]); // Fx groups listesi
  const [fxGroupsError, setFxGroupsError] = useState(null); // Fx groups hata mesajı
  const [selectedFxGroup, setSelectedFxGroup] = useState(null); // Seçilen Fx Group
  const [functions, setFunctions] = useState([]); // Functions listesi (fname ve fdesc içeren)
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false); // Functions yüklenirken
  const [functionsError, setFunctionsError] = useState(null); // Functions hata mesajı
  const [selectedFunction, setSelectedFunction] = useState(null); // Seçilen function
  const [showFunctionsModal, setShowFunctionsModal] = useState(false); // Functions modal açık/kapalı
  const {
    functionWindows,
    setFunctionWindows,
    draggingWindowId,
    setDraggingWindowId,
    dragOffset,
    setDragOffset,
    resizingWindowId,
    setResizingWindowId,
    resizeStart,
    setResizeStart,
    resizeDirection,
    setResizeDirection,
    functionWindowEditorRefs,
  } = useFunctionWindows();

  const {
    functionWindows: promptWindows,
    setFunctionWindows: setPromptWindows,
    draggingWindowId: draggingPromptWindowId,
    setDraggingWindowId: setDraggingPromptWindowId,
    dragOffset: promptDragOffset,
    setDragOffset: setPromptDragOffset,
    resizingWindowId: resizingPromptWindowId,
    setResizingWindowId: setResizingPromptWindowId,
    resizeStart: promptResizeStart,
    setResizeStart: setPromptResizeStart,
    resizeDirection: promptResizeDirection,
    setResizeDirection: setPromptResizeDirection,
  } = useFunctionWindows();
  const [fileSearchQuery, setFileSearchQuery] = useState(''); // File list arama sorgusu
  const [selectedFileCategory, setSelectedFileCategory] = useState('all'); // Seçili file kategorisi (folder)
  const {
    tabs,
    setTabs,
    activeTabId,
    setActiveTabId,
    tabCounter,
    setTabCounter,
    tabCodeCacheRef,
    workflowDataRef,
    selectedShapePerTabRef,
    originalScriptsRef,
    savedWorkflowHashRef,
    handleFileClick,
    handleTabSwitch,
    handleTabClose,
  } = useTabManager({
    workflowData,
    navigate,
    fetchWrapper,
    selectedShape,
    setSelectedShape,
    setCode,
    editorRef,
    isUpdatingEditorRef,
    shapeCodeCacheRef,
    previousSelectedShapeRef,
    previousActiveTabIdRef,
    graphRef,
    onBeforeTabLeave: onBeforeTabLeaveCallback,
  });

  useEffect(() => {
    debugContextRef.current = {
      activeTabId: activeTabId || null,
      selectedShape: selectedShape || null,
      codeEditorShapeId: codeEditorShapeId || null,
    };
  }, [activeTabId, selectedShape, codeEditorShapeId]);

  // tabsRef: graph effect'in tabs'a doğrudan bağımlılığını kırar; her render'da sync edilir
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // ========== BULK AI: UPDATE SCRIPTS FROM COMMENT INSTRUCTIONS ==========
  const [showBulkAiModal, setShowBulkAiModal] = useState(false);
  const [bulkAiItems, setBulkAiItems] = useState([]);
  const [isBulkAiGenerating, setIsBulkAiGenerating] = useState(false);
  const [isBulkAiApplying, setIsBulkAiApplying] = useState(false);
  const [bulkAiError, setBulkAiError] = useState(null);
  const [bulkAiExpanded, setBulkAiExpanded] = useState({});
  const [bulkAiSelected, setBulkAiSelected] = useState(() => ({}));
  const [showScenarioFlowModal, setShowScenarioFlowModal] = useState(false);
  const [scenarioFlowInput, setScenarioFlowInput] = useState('');
  const [isScenarioFlowGenerating, setIsScenarioFlowGenerating] = useState(false);
  const [scenarioFlowError, setScenarioFlowError] = useState(null);

  const stripCodeFences = useCallback((text) => {
    if (!text) return '';
    const t = String(text).trim();
    const noStart = t.replace(/^```[a-zA-Z0-9_-]*\s*/m, '');
    const noEnd = noStart.replace(/```$/m, '');
    return noEnd.trim();
  }, []);

  const detectLanguageFromScript = useCallback((script) => {
    const s = (script || '').trim();
    const head = s.slice(0, 800).toLowerCase();
    const body = s.toLowerCase();

    if (head.includes('<?php') || body.includes('$this->') || /\$\w+/.test(s)) return 'php';
    if (head.includes('#!/bin/bash') || head.includes('#!/usr/bin/env bash')) return 'bash';
    if (/\bselect\b[\s\S]{0,200}\bfrom\b/i.test(s) || /\binsert\b[\s\S]{0,200}\binto\b/i.test(s)) return 'sql';
    if (/\bdef\s+\w+\s*\(/.test(s) || /\bimport\s+\w+/.test(s) || /\bprint\s*\(/.test(s)) return 'python';
    if (/\bconsole\./.test(s) || /\bexport\s+/.test(s) || /\bimport\s+/.test(s)) return 'javascript';
    if (/\binterface\s+\w+/.test(s) || /\btype\s+\w+\s*=/.test(s)) return 'typescript';
    return 'unknown';
  }, []);

  const extractInstructionComments = useCallback((script) => {
    const s = String(script || '');
    const lines = s.split('\n');
    const maxScan = Math.min(lines.length, 250);
    const explicitRe =
      /(TODO|FIXME|AI|İSTEK|ISTEK|REQUEST|GÜNCELLE|GUNCELLE|DEĞİŞTİR|DEGISTIR|EKLE|KALDIR|REFACTOR|OPTIMIZE|İYİLEŞTİR|IYILESTIR|BUG)/i;

    const out = [];
    const pushIf = (raw) => {
      const t = String(raw || '').trim();
      if (!t) return;
      if (t.length > 500) return;
      out.push(t);
    };

    // 1) Line comments (explicit)
    for (let i = 0; i < maxScan; i++) {
      const t = (lines[i] || '').trim();
      if (!t) continue;
      const isLineComment = t.startsWith('//') || t.startsWith('#') || t.startsWith('--') || t.startsWith(';--');
      if (!isLineComment) continue;
      const cleaned = t.replace(/^\/\//, '').replace(/^#/, '').replace(/^;?--/, '').trim();
      if (explicitRe.test(cleaned)) pushIf(cleaned);
    }
    if (out.length > 0) return out.slice(0, 40);

    // 2) Top-of-file block comment (explicit)
    const top = lines.slice(0, Math.min(lines.length, 80)).join('\n');
    const blockMatch = top.match(/\/\*[\s\S]*?\*\//);
    if (blockMatch && blockMatch[0]) {
      const block = blockMatch[0]
        .replace(/^\/\*/, '')
        .replace(/\*\/$/, '')
        .split('\n')
        .map((l) => l.replace(/^\s*\*\s?/, '').trim())
        .filter(Boolean)
        .filter((l) => explicitRe.test(l));
      if (block.length > 0) return block.slice(0, 40);
    }

    return [];
  }, []);

  const getShapeDisplayName = useCallback((shapeId, shapeData) => {
    try {
      const graph = graphRef.current;
      const model = graph?.getModel?.();
      const cell = model?.getCell?.(shapeId);
      const v = cell?.getValue?.();
      const label = v !== undefined && v !== null ? String(v).trim() : '';
      if (label) return label;
    } catch (_e) {
      // ignore
    }
    const def = shapeData?.definition ? String(shapeData.definition).trim() : '';
    return def || String(shapeId);
  }, []);

  const getCurrentWorkflowDataForBulk = useCallback(() => {
    // workflowDataRef.current her zaman en güncel kaynak olmalı (prompt edit / include / vs.)
    const refData = workflowDataRef.current;
    if (refData) return refData;
    const activeTab = tabs?.find?.((t) => t.id === activeTabId);
    return activeTab?.workflowData || workflowData;
  }, [activeTabId, tabs, workflowData]); // workflowDataRef çıkarıldı - ref'ler dependency'ye girmemeli

  const getScriptValueForShape = useCallback(
    (shapeId, shapeData) => {
      const tabCache =
        activeTabId && tabCodeCacheRef.current && tabCodeCacheRef.current[activeTabId]
          ? tabCodeCacheRef.current[activeTabId][shapeId]
          : undefined;
      if (tabCache !== undefined) return tabCache !== null ? String(tabCache) : '';

      const fromData = shapeData?.script;
      return typeof fromData === 'string' ? fromData : '';
    },
    [activeTabId],
  );

  // Merkezi cache yazım fonksiyonu — her yazımı detaylı loglar ve cross-file guard uygular.
  // tabCodeCacheRef'e doğrudan yazım YAPILMAZ; tüm yazımlar buradan geçer ki sızıntı kaynağı
  // tam olarak tespit edilebilsin.
  const writeTabCache = useCallback((tabId, shapeId, value, source) => {
    if (!tabId || !shapeId) return false;
    const tabsNow = tabsRef.current || [];
    const tab = tabsNow.find((t) => String(t.id) === String(tabId));
    const wd = tab?.workflowData;
    const shapeInTab = !!(wd && Array.isArray(wd.contents) && wd.contents.some((it) => it && String(it.id) === String(shapeId)));
    const valueStr = value == null ? '' : String(value);
    const previous = (tabCodeCacheRef.current[tabId] && tabCodeCacheRef.current[tabId][shapeId] !== undefined)
      ? String(tabCodeCacheRef.current[tabId][shapeId] || '')
      : null;

    if (!shapeInTab) {
      pushDebugLog('cache_write_blocked_foreign', {
        source: source || 'unknown',
        tabId: String(tabId),
        shapeId: String(shapeId),
        newLen: valueStr.length,
        newPreview: valueStr.length > 160 ? valueStr.slice(0, 160) + `... (+${valueStr.length - 160} chars)` : valueStr,
        stack: (new Error().stack || '').split('\n').slice(2, 8).join(' | '),
      });
      return false;
    }

    if (!tabCodeCacheRef.current[tabId]) tabCodeCacheRef.current[tabId] = {};
    tabCodeCacheRef.current[tabId][shapeId] = valueStr;
    // PERFORMANS: keystroke-level cache yazımlarını log'a basmıyoruz (çok yüksek frekans).
    // handleCodeChange dışındaki tüm yazımlar loglanır (script_load, save snapshot, vb.).
    if (source !== 'handleCodeChange') {
      pushDebugLog('cache_write', {
        source: source || 'unknown',
        tabId: String(tabId),
        shapeId: String(shapeId),
        oldLen: previous == null ? -1 : previous.length,
        newLen: valueStr.length,
      });
    }
    return true;
  }, [pushDebugLog]);

  // Shape gerçekten bu tab'ın workflow'una ait mi? (cross-file script pollution'ı önler)
  // ÖNEMLİ: Fallback YOK. Her tab yalnızca kendi workflowData.contents'ine göre doğrulanır.
  // Aksi halde aktif olmayan bir tab sorgulandığında workflowDataRef (= aktif tab) kullanılıp
  // yanlış TRUE döndürebiliyor ve cross-file guard'ı bypass ediyor.
  const isShapeInTabWorkflow = useCallback((shapeId, tabId) => {
    if (!shapeId || !tabId) return false;
    const tabsNow = tabsRef.current || [];
    const tab = tabsNow.find((t) => String(t.id) === String(tabId));
    const wd = tab?.workflowData;
    if (!wd || !Array.isArray(wd.contents)) {
      pushDebugLog('anomaly_shape_tab_check_no_workflow', {
        shapeId: String(shapeId),
        tabId: String(tabId),
        tabExists: !!tab,
        hasWorkflowData: !!wd,
      });
      return false;
    }
    return wd.contents.some((item) => item && String(item.id) === String(shapeId));
  }, [pushDebugLog]);

  const toScriptPreview = useCallback((txt, maxLen = 220) => {
    const s = txt == null ? '' : String(txt);
    if (s.length <= maxLen) return s;
    return `${s.slice(0, maxLen)}... (+${s.length - maxLen} chars)`;
  }, []);

  const buildSavePreview = useCallback(() => {
    const queuedScripts = [];
    const seen = new Set();
    const tabsById = new Map((tabs || []).map((t) => [String(t.id), t]));
    const shapeBelongsToTab = (sid, tid) => {
      const t = tabsById.get(String(tid));
      const wd = t?.workflowData;
      if (wd && Array.isArray(wd.contents)) {
        return wd.contents.some((it) => it && String(it.id) === String(sid));
      }
      const wdRef = workflowDataRef.current;
      if (wdRef && Array.isArray(wdRef.contents)) {
        return wdRef.contents.some((it) => it && String(it.id) === String(sid));
      }
      return false;
    };
    for (const [tabId, cache] of Object.entries(tabCodeCacheRef.current || {})) {
      if (!cache || typeof cache !== 'object') continue;
      const originals = originalScriptsRef.current[tabId] || {};
      for (const [shapeId, scriptVal] of Object.entries(cache)) {
        // CROSS-FILE GUARD: yabancı shape’leri preview'den de çıkar
        if (!shapeBelongsToTab(shapeId, tabId)) continue;
        if (seen.has(shapeId)) continue;
        const currentScript = scriptVal != null ? String(scriptVal) : '';
        const originalScript = originals[shapeId] !== undefined ? String(originals[shapeId]) : '';
        if (currentScript === originalScript) continue;
        seen.add(shapeId);
        const tabMeta = tabsById.get(String(tabId));
        queuedScripts.push({
          tabId: String(tabId),
          tabLabel: tabMeta?.name || tabMeta?.filename || null,
          shapeId: String(shapeId),
          length: currentScript.length,
          preview: toScriptPreview(currentScript),
        });
      }
    }
    const tail = (debugLogRef.current || []).slice(-120);
    const activeTabMeta = tabsById.get(String(activeTabId));
    return {
      activeTabId: activeTabId || null,
      activeFileLabel: activeTabMeta?.name || activeTabMeta?.filename || null,
      selectedShape: selectedShape || null,
      codeEditorShapeId: codeEditorShapeId || null,
      codeLength: typeof code === 'string' ? code.length : 0,
      currentEditorPreview: toScriptPreview(code || ''),
      queuedScripts,
      debugTail: tail,
    };
  }, [activeTabId, selectedShape, codeEditorShapeId, code, toScriptPreview, tabCodeCacheRef, originalScriptsRef, tabs]);

  // setTabs debounce ref: prompt yazarken her tuşta tabs cascade'ini önler
  const promptTabsDebounceRef = useRef(null);

  const updateShapeAIPrompt = useCallback(
    (shapeId, newPrompt) => {
      const promptStr = newPrompt !== null && newPrompt !== undefined ? String(newPrompt) : '';

      // 1) Cache — senkron, kayıpsız
      promptCacheRef.current[String(shapeId)] = promptStr;

      // 2) workflowDataRef — senkron, UI render olmadan
      try {
        const current = workflowDataRef.current || workflowData;
        if (current && Array.isArray(current.contents)) {
          const updatedContents = current.contents.map((it) => {
            if (!it || String(it.id) !== String(shapeId)) return it;
            return { ...it, ai_prompt: promptStr };
          });
          workflowDataRef.current = { ...current, contents: updatedContents };
        }
      } catch (_e) { /* ignore */ }

      // 3) setTabs — debounce: 200ms içinde gelen çağrılar birleştirilir
      if (activeTabId) {
        if (promptTabsDebounceRef.current) clearTimeout(promptTabsDebounceRef.current);
        promptTabsDebounceRef.current = setTimeout(() => {
          setTabs((prev) =>
            prev.map((tab) => {
              if (!tab || tab.id !== activeTabId) return tab;
              const currentData = tab.workflowData || workflowDataRef.current || workflowData;
              if (!currentData || !Array.isArray(currentData.contents)) return tab;
              const updatedContents = currentData.contents.map((it) => {
                if (!it || String(it.id) !== String(shapeId)) return it;
                return { ...it, ai_prompt: promptCacheRef.current[String(shapeId)] ?? promptStr };
              });
              return { ...tab, workflowData: { ...currentData, contents: updatedContents } };
            }),
          );
        }, 200);
      }
    },
    [activeTabId, setTabs, workflowData],
  );

  const openPromptEditorWindow = useCallback(
    (shapeId, options = {}) => {
      const { x = 120, y = 120 } = options;
      const current = workflowDataRef.current || workflowData;
      const shapeData =
        current && Array.isArray(current.contents)
          ? current.contents.find((it) => it && String(it.id) === String(shapeId))
          : null;
      const label = getShapeDisplayName(shapeId, shapeData);
      // Önce cache'ten oku (kullanıcı düzenlemeleri), sonra workflowData
      const cachedPrompt = promptCacheRef.current[String(shapeId)];
      const currentPrompt = cachedPrompt !== undefined ? cachedPrompt : (shapeData?.ai_prompt !== undefined && shapeData?.ai_prompt !== null ? String(shapeData.ai_prompt) : '');

      const winId = `prompt-${shapeId}`;
      setPromptWindows((prev) => {
        const existing = prev.find((w) => w.id === winId);
        const base = existing
          ? {
              ...existing,
              position: existing.position || { x, y },
              stepLabel: label, // Label'ı da güncelle
              prompt: currentPrompt, // ✅ FIX: HER ZAMAN en güncel prompt'u kullan
              originalPrompt: currentPrompt,
              saveStatus: null, // Status'u resetle
            }
          : {
              id: winId,
              shapeId: String(shapeId),
              stepLabel: label,
              prompt: currentPrompt,
              originalPrompt: currentPrompt,
              position: { x, y },
              width: 620,
              height: 360,
              isMinimized: false,
              saveStatus: null,
            };
        // bring to front
        const filtered = prev.filter((w) => w.id !== winId);
        return [...filtered, base];
      });
    },
    [getShapeDisplayName, setPromptWindows, workflowData],
  );

  const closePromptWindow = useCallback((windowId) => {
    setPromptWindows((prev) => prev.filter((w) => w.id !== windowId));
  }, [setPromptWindows]);

  const toggleMinimizePromptWindow = useCallback((windowId) => {
    setPromptWindows((prev) =>
      prev.map((w) => (w.id === windowId ? { ...w, isMinimized: !w.isMinimized } : w)),
    );
  }, [setPromptWindows]);

  const onChangePromptWindow = useCallback(
    (windowId, nextValue) => {
      setPromptWindows((prev) =>
        prev.map((w) => (w.id === windowId ? { ...w, prompt: nextValue, saveStatus: null } : w)),
      );
      // Anında workflow içine yaz ki üstteki Kaydet butonu prompt’u da göndersin
      // windowId formatı: "prompt-<shapeId>"
      const parsedShapeId = windowId && windowId.startsWith('prompt-') ? windowId.slice('prompt-'.length) : null;
      const win = promptWindows.find((w) => w.id === windowId);
      const shapeId = win?.shapeId || parsedShapeId;
      if (shapeId) updateShapeAIPrompt(shapeId, nextValue);
    },
    [promptWindows, setPromptWindows, updateShapeAIPrompt],
  );

  const savePromptWindow = useCallback(
    (windowId) => {
      const parsedShapeId = windowId && windowId.startsWith('prompt-') ? windowId.slice('prompt-'.length) : null;
      const win = promptWindows.find((w) => w.id === windowId);
      const shapeId = win?.shapeId || parsedShapeId;
      if (!shapeId) return;
      updateShapeAIPrompt(shapeId, win?.prompt || '');
      setPromptWindows((prev) =>
        prev.map((w) =>
          w.id === windowId ? { ...w, saveStatus: { state: 'ok', message: 'Prompt güncellendi' } } : w,
        ),
      );
    },
    [promptWindows, setPromptWindows, updateShapeAIPrompt],
  );

  // Inline prompt editing handler (code editor panel)
  const handlePromptChange = useCallback((value) => {
    setCurrentPrompt(value);
    // Anında local state'e kaydet (workflowDataRef.current)
    // Toolbar'daki Kaydet butonu ile birlikte sunucuya gönderilecek
    if (codeEditorShapeId) {
      updateShapeAIPrompt(codeEditorShapeId, value);
    }
  }, [codeEditorShapeId, updateShapeAIPrompt]);

  /* ── Şekil seçildiğinde per-tab cache'e kaydet ── */
  useEffect(() => {
    if (selectedShape && activeTabId) {
      selectedShapePerTabRef.current[activeTabId] = selectedShape;
    }
  }, [selectedShape, activeTabId, selectedShapePerTabRef]);

  /* ── Mevcut code editor tab'larının label'larını güncelle ── */
  /* Yeni tab EKLEME sağ-tık handler'ında doğrudan yapılır; burası sadece label sync'i */
  useEffect(() => {
    if (!codeEditorShapeId || !showCodeEditor) return;
    const label = codeEditorShapeLabel || String(codeEditorShapeId);
    setEditorTabs((prev) => {
      const existing = prev.find((t) => t.shapeId === codeEditorShapeId);
      if (!existing) return prev; // Yeni tab bu effect'ten eklenmiyor
      if (existing.shapeLabel !== label && label !== String(codeEditorShapeId)) {
        return prev.map((t) =>
          t.shapeId === codeEditorShapeId ? { ...t, shapeLabel: label } : t
        );
      }
      return prev;
    });
  }, [codeEditorShapeId, showCodeEditor, codeEditorShapeLabel]);

  /* ── Flow değişince editor tab'larını per-tab cache'den yönet ── */
  const editorTabsCacheRef = useRef({});
  const codeEditorShapePerTabRef = useRef({});
  const prevActiveTabIdForEditorRef = useRef(null);
  useEffect(() => {
    const prevTabId = prevActiveTabIdForEditorRef.current;
    if (prevTabId && prevTabId !== activeTabId) {
      const tabsNow = tabsRef.current || [];
      const fromTab = tabsNow.find((t) => String(t.id) === String(prevTabId));
      const toTab = tabsNow.find((t) => String(t.id) === String(activeTabId));
      pushDebugLog('file_switch_before_cache', {
        fromTabId: prevTabId,
        toTabId: activeTabId || null,
        fromTabLabel: fromTab?.name || fromTab?.filename || null,
        toTabLabel: toTab?.name || toTab?.filename || null,
        cachedEditorTabsCount: editorTabs.length,
        cachedCodeEditorShapeId: codeEditorShapeId || null,
      });
      editorTabsCacheRef.current[prevTabId] = editorTabs;
      codeEditorShapePerTabRef.current[prevTabId] = codeEditorShapeId || null;
    }
    prevActiveTabIdForEditorRef.current = activeTabId;
    if (activeTabId) {
      const cached = editorTabsCacheRef.current[activeTabId];
      const tabsNow = tabsRef.current || [];
      const toTab = tabsNow.find((t) => String(t.id) === String(activeTabId));
      setEditorTabs(cached && cached.length > 0 ? cached : []);
      const nextShapeId = codeEditorShapePerTabRef.current[activeTabId] || null;
      pushDebugLog('file_switch_restore_editor_context', {
        toTabId: activeTabId,
        toTabLabel: toTab?.name || toTab?.filename || null,
        restoredCodeEditorShapeId: nextShapeId,
        restoredEditorTabsCount: (cached && cached.length > 0 ? cached : []).length,
      });
      setCodeEditorShapeId(nextShapeId);
      if (nextShapeId) {
        const nextTab = (cached && cached.length > 0 ? cached : []).find((t) => t.shapeId === nextShapeId);
        setCodeEditorShapeLabel(nextTab?.shapeLabel || String(nextShapeId));
      } else {
        setCodeEditorShapeLabel('');
      }
    } else {
      pushDebugLog('file_switch_clear_editor_context', {});
      setCodeEditorShapeId(null);
      setCodeEditorShapeLabel('');
    }
  }, [activeTabId, pushDebugLog]);

  /* ── Flow değiştiğinde prompt cache'ini yeniden doldur ── */
  useEffect(() => {
    promptCacheRef.current = {};
    const wd = workflowDataRef.current || workflowData;
    if (wd && Array.isArray(wd.contents)) {
      wd.contents.forEach((item) => {
        if (!item || !item.id) return;
        const p = item.ai_prompt;
        if (p !== undefined && p !== null && p !== 'null') {
          promptCacheRef.current[String(item.id)] = String(p);
        }
      });
    }
  }, [activeTabId, workflowData]);

  /* ── Code editor tab yönetimi ── */
  const handleCodeTabClose = useCallback((shapeId) => {
    setEditorTabs((prev) => {
      const next = prev.filter((t) => t.shapeId !== shapeId);
      if (next.length === 0) setShowCodeEditor(false);
      return next;
    });
  }, []);

  const handleTabActivate = useCallback((shapeId) => {
    pushDebugLog('code_tab_activate', { shapeId: String(shapeId) });
    setCodeEditorShapeId(shapeId);
    // Tab'da kayıtlı label'ı kullan — graphRef'e gerek yok
    setEditorTabs((prev) => {
      const tab = prev.find((t) => t.shapeId === shapeId);
      setCodeEditorShapeLabel(tab?.shapeLabel || String(shapeId));
      return prev;
    });
    // ÖNEMLİ: setSelectedShape ÇAĞIRMA — tab tıklaması flowchart seçimini değiştirmemeli.
    // Code editor ve flowchart seçimi tamamen birbirinden bağımsız olmalı.
  }, [pushDebugLog]);

  const handleTabDetach = useCallback((shapeId, shapeLabel) => {
    const code = activeTabId && tabCodeCacheRef.current[activeTabId] && tabCodeCacheRef.current[activeTabId][shapeId] !== undefined
      ? String(tabCodeCacheRef.current[activeTabId][shapeId] ?? '')
      : '';
    /* Şeklin mevcut AI prompt'unu al — önce cache, sonra workflowData */
    const cached = promptCacheRef.current[String(shapeId)];
    const shapeData = (workflowDataRef.current || workflowData)?.contents?.find(
      (it) => it && String(it.id) === String(shapeId),
    );
    const prompt = cached !== undefined ? cached : (shapeData?.ai_prompt != null ? String(shapeData.ai_prompt) : '');
    setFloatingEditors((prev) => {
      const alreadyExists = prev.some((f) => f.shapeId === shapeId);
      if (alreadyExists) return prev;
      const offset = prev.length * 30;
      return [...prev, {
        id: `float-${shapeId}-${Date.now()}`,
        shapeId, shapeLabel: shapeLabel || String(shapeId),
        code, prompt, x: 80 + offset, y: 60 + offset, w: 640, h: 480,
      }];
    });
    /* Tab listesinden çıkar ve bir önceki taba geç */
    setEditorTabs((prev) => {
      const idx = prev.findIndex((t) => t.shapeId === shapeId);
      const next = prev.filter((t) => t.shapeId !== shapeId);
      if (next.length > 0) {
        const switchTo = idx > 0 ? next[idx - 1] : next[0];
        setCodeEditorShapeId(switchTo.shapeId);
        setCodeEditorShapeLabel(switchTo.shapeLabel || String(switchTo.shapeId));
      } else {
        setCodeEditorShapeId(null);
        setCodeEditorShapeLabel('');
      }
      return next;
    });
  }, [workflowData, activeTabId]);

  const handleFloatingClose = useCallback((id) => {
    setFloatingEditors((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleFloatingCodeChange = useCallback((shapeId, newCode) => {
    shapeCodeCacheRef.current[shapeId] = newCode;
    if (activeTabId) {
      writeTabCache(activeTabId, shapeId, newCode, 'handleFloatingCodeChange');
    }
  }, [activeTabId, writeTabCache]);

  const handleFloatingSave = useCallback((shapeId, code) => {
    return saveScript(shapeId, code);
  }, [saveScript]);

  const handleFloatingPromptChange = useCallback((shapeId, newPrompt) => {
    updateShapeAIPrompt(shapeId, newPrompt);
  }, [updateShapeAIPrompt]);

  // codeEditorShapeId değiştiğinde prompt'u yükle (önce cache, sonra workflowData)
  useEffect(() => {
    if (!codeEditorShapeId) {
      setCurrentPrompt('');
      return;
    }
    const cached = promptCacheRef.current[String(codeEditorShapeId)];
    if (cached !== undefined) {
      setCurrentPrompt(cached);
      return;
    }
    const current = workflowDataRef.current || workflowData;
    const shapeData =
      current && Array.isArray(current.contents)
        ? current.contents.find((it) => it && String(it.id) === String(codeEditorShapeId))
        : null;
    const prompt = shapeData?.ai_prompt !== undefined && shapeData?.ai_prompt !== null ? String(shapeData.ai_prompt) : '';
    setCurrentPrompt(prompt);
  }, [codeEditorShapeId, workflowData]);

  // ✅ FIX: workflowData değiştiğinde açık prompt window'larını güncelle
  useEffect(() => {
    if (!workflowData || !Array.isArray(workflowData.contents)) return;
    if (promptWindows.length === 0) return;

    setPromptWindows((prev) =>
      prev.map((win) => {
        const shapeData = workflowData.contents.find((it) => it && String(it.id) === String(win.shapeId));
        if (!shapeData) return win; // Shape silinmişse window'u koru (kullanıcı kapatır)
        
        const currentPrompt = shapeData.ai_prompt !== undefined && shapeData.ai_prompt !== null ? String(shapeData.ai_prompt) : '';
        const label = getShapeDisplayName(win.shapeId, shapeData);
        
        // Sadece değişiklik varsa güncelle (gereksiz render'ları önle)
        if (win.prompt === currentPrompt && win.stepLabel === label) return win;
        
        return {
          ...win,
          prompt: currentPrompt,
          originalPrompt: currentPrompt,
          stepLabel: label,
        };
      })
    );
  }, [workflowData, promptWindows, setPromptWindows, getShapeDisplayName]);

  const getOpenAIProxyUrl = useCallback(() => {
    const envUrl = process.env.REACT_APP_OPENAI_PROXY_URL;
    if (envUrl) return envUrl;
    if (process.env.NODE_ENV === 'production') return '/openai-proxy.php';
    return null;
  }, []);

  const callAI = useCallback(
    async ({ model, messages, max_tokens, temperature }) => {
      const proxyProcessId = process.env.REACT_APP_OPENAI_PROXY_PROCESS;
      const proxyUrl = getOpenAIProxyUrl();
      const apiKey = getOpenAIKey();

      // Öncelik 1: call.php üzerinden process ile proxy (prod için en stabil yol)
      if (proxyProcessId) {
        const token = localStorage.getItem('token');
        const bodyParams = {
          token,
          model,
          messages,
          max_tokens,
          temperature,
          // Bazı backend'lerde payload $data['data'] altında bekleniyor
          data: {
            token,
            model,
            messages,
            max_tokens,
            temperature,
          },
        };

        const res = await fetchWrapper(proxyProcessId, bodyParams, navigate);

        console.log('%c[callAI] Proxy response:', 'color:#fbbf24;font-weight:bold', JSON.stringify(res, null, 2)?.substring(0, 1000));

        // Proxy hata kontrolü — ok:0 veya http hata kodu geldiğinde fırlat
        const proxyOk = res?.ok ?? res?.result?.ok ?? res?.CSResponse?.ok;
        const proxyError = res?.error ?? res?.result?.error ?? res?.CSResponse?.error;
        const proxyHttp = res?.http ?? res?.result?.http ?? res?.CSResponse?.http;

        if (proxyOk === 0 || proxyOk === '0' || (proxyHttp && proxyHttp >= 400)) {
          let friendlyMsg = proxyError || `Proxy hata döndürdü (http=${proxyHttp ?? 'bilinmiyor'})`;
          if (proxyHttp === 429 || (proxyError && proxyError.toLowerCase().includes('quota'))) {
            friendlyMsg = `💳 OpenAI krediniz/kotanız dolmuş (429 Quota Exceeded).\n\nOpenAI hesabınıza giriş yapıp bakiye ekleyin:\nhttps://platform.openai.com/account/billing\n\nDetay: ${proxyError}`;
          } else if (proxyHttp === 401 || (proxyError && proxyError.toLowerCase().includes('invalid'))) {
            friendlyMsg = `🔑 API key geçersiz (401). Proxy sunucusundaki OpenAI API key'ini kontrol edin.\n\nDetay: ${proxyError}`;
          } else if (proxyHttp === 500) {
            friendlyMsg = `🔥 OpenAI sunucu hatası (500). Lütfen biraz bekleyin.\n\nDetay: ${proxyError}`;
          }
          console.error('%c[callAI] Proxy hata döndürdü!', 'color:#f87171;font-weight:bold', { proxyOk, proxyHttp, proxyError });
          const err = new Error(friendlyMsg);
          err.status = proxyHttp;
          throw err;
        }

        const cs = res?.CSResponse ?? res?.result?.CSResponse ?? res?.result ?? res;
        if (typeof cs === 'string') {
          // Bazı süreçler string JSON döndürüyor
          try {
            const parsed = JSON.parse(cs);
            if (parsed && typeof parsed === 'object') {
              if (parsed.ok === 0 || parsed.ok === '0') {
                const parsedErr = parsed.error || 'Proxy hata döndürdü';
                console.error('%c[callAI] Parsed string proxy hata:', 'color:#f87171;font-weight:bold', parsedErr);
                const err = new Error(parsedErr);
                err.status = parsed.http;
                throw err;
              }
              if (typeof parsed.content === 'string') return parsed.content;
              if (parsed?.CSResponse?.content) return String(parsed.CSResponse.content);
              return JSON.stringify(parsed);
            }
          } catch (_e) {
            if (_e.status) throw _e;
          }
          return cs;
        }
        if (cs && typeof cs === 'object') {
          if (typeof cs.content === 'string') return cs.content;
          if (cs?.CSResponse && typeof cs.CSResponse?.content === 'string') return cs.CSResponse.content;
        }
        return '';
      }

      if (proxyUrl) {
        const resp = await fetch(proxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ model, messages, max_tokens, temperature }),
        });
        const text = await resp.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (_e) {
          throw new Error(`Proxy JSON parse hatası. Response: ${text.substring(0, 200)}`);
        }
        if (!resp.ok || json?.CSResponse?.ok === 0) {
          throw new Error(json?.error || `Proxy HTTP ${resp.status}`);
        }
        return String(json?.CSResponse?.content || '');
      }

      if (!apiKey) {
        throw new Error(
          "OpenAI API key bulunamadı ve proxy tanımlı değil. Dev ortamında `localStorage.openai_api_key` ile ekleyebilir veya `.env` içinde `REACT_APP_OPENAI_API_KEY` kullanabilirsiniz.",
        );
      }

      // Browser direct (CORS engeline takılabilir)
      const OpenAI = (await import('openai')).default;
      const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
      const completion = await openai.chat.completions.create({
        model,
        messages,
        max_tokens,
        temperature,
      });
      return String(completion?.choices?.[0]?.message?.content || '');
    },
    [getOpenAIKey, getOpenAIProxyUrl, navigate],
  );

  const handleOpenScenarioFlowModal = useCallback(() => {
    setScenarioFlowError(null);
    setShowScenarioFlowModal(true);
  }, []);

  const handleGenerateFlowFromScenario = useCallback(async () => {
    if (isScenarioFlowGenerating) return;

    const scenario = String(scenarioFlowInput || '').trim();
    if (!scenario) {
      setScenarioFlowError('Lütfen bir senaryo girin.');
      return;
    }

    setIsScenarioFlowGenerating(true);
    setScenarioFlowError(null);

    try {
      const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o';
      const activeTab = tabs.find((tab) => tab.id === activeTabId);
      const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current || workflowData || {};
      const currentWorkflowId = currentWorkflowData?.workflow?.id
        ? String(currentWorkflowData.workflow.id)
        : '100022941';
      const currentWorkflowName = currentWorkflowData?.workflow?.name
        ? String(currentWorkflowData.workflow.name)
        : 'AI Flowchart';

      const { semanticGraph, repaired, warnings } = await generateSemanticGraphWithAutoRepair({
        callAI,
        scenario,
        model,
        temperature: 0,
      });

      const compiled = compileSemanticGraphToMxData(semanticGraph, {
        workflowId: currentWorkflowId,
        workflowName: semanticGraph.workflow_name || currentWorkflowName,
      });

      const nextWorkflowData = {
        ...currentWorkflowData,
        workflow: {
          ...(currentWorkflowData?.workflow || {}),
          ...(compiled.workflow || {}),
        },
        AI_GLOBAL: Array.isArray(currentWorkflowData?.AI_GLOBAL) ? currentWorkflowData.AI_GLOBAL : [],
        contents: compiled.contents,
      };

      workflowDataRef.current = nextWorkflowData;

      if (activeTabId) {
        setTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeTabId
              ? { ...tab, workflowData: nextWorkflowData }
              : tab,
          ),
        );
      }

      setSelectedShape(null);
      setSelectedShapeLabel('');
      setCurrentPrompt('');
      setCode('');
      shapeCodeCacheRef.current = {};
      if (activeTabId) tabCodeCacheRef.current[activeTabId] = {};

      if (editorRef.current) {
        isUpdatingEditorRef.current = true;
        try {
          editorRef.current.setValue('');
        } catch (_) { /* ignore */ }
        setTimeout(() => {
          isUpdatingEditorRef.current = false;
        }, 100);
      }

      // Aynı tabda workflow id aynı olsa bile graph'in yeniden kurulmasını zorla.
      workflowIdRef.current = null;

      setShowScenarioFlowModal(false);
      setScenarioFlowInput('');

      const successMsg = repaired
        ? 'Flowchart üretildi (çıktı auto-repair ile düzeltildi).'
        : 'Flowchart başarıyla üretildi.';
      showToast('success', successMsg, warnings?.length ? { title: 'AI Flowchart', warnings } : null);
    } catch (error) {
      const msg = error?.message || 'Senaryodan flowchart üretilirken hata oluştu.';
      setScenarioFlowError(msg);
      showToast('error', msg, { title: 'AI Flowchart Generate' });
    } finally {
      setIsScenarioFlowGenerating(false);
    }
  }, [
    activeTabId,
    callAI,
    isScenarioFlowGenerating,
    isUpdatingEditorRef,
    scenarioFlowInput,
    setCode,
    setTabs,
    showToast,
    tabCodeCacheRef,
    tabs,
    workflowData,
    workflowDataRef,
  ]);

  const handleBulkUpdateScriptsFromComments = useCallback(async () => {
    if (isBulkAiGenerating || isBulkAiApplying) return;

    setBulkAiError(null);
    const bulkProxyProcessId = process.env.REACT_APP_OPENAI_PROXY_PROCESS;
    const proxyUrl = getOpenAIProxyUrl();
    const apiKey = getOpenAIKey();
    if (!apiKey && !proxyUrl && !bulkProxyProcessId) {
      setBulkAiError(
        "OpenAI API key bulunamadı ve proxy tanımlı değil. Önerilen: server-side proxy + OPENAI_API_KEY.",
      );
      setShowBulkAiModal(true);
      return;
    }

    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = currentWorkflowData?.contents || [];
    const vertices = contents.filter(
      (it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id != null,
    );

    const candidates = vertices
      .map((shapeData) => {
        const shapeId = String(shapeData.id);
        const oldCode = getScriptValueForShape(shapeId, shapeData);
        const lang = detectLanguageFromScript(oldCode);
        const instructions = extractInstructionComments(oldCode);
        const label = getShapeDisplayName(shapeId, shapeData);
        return {
          shapeId,
          label,
          lang,
          instructions,
          oldCode,
          newCode: '',
          status: instructions.length ? 'pending' : 'skipped',
          error: null,
        };
      })
      .filter((x) => x.status !== 'skipped');

    if (candidates.length === 0) {
      setBulkAiError(
        'Hiçbir şekilde “AI/TODO/FIXME/İSTEK/REQUEST …” gibi açık talimat içeren yorum satırı bulunamadı. (İpucu: script içine `// AI: ...` veya `// TODO: ...` yazın.)',
      );
      setBulkAiItems([]);
      setBulkAiSelected({});
      setBulkAiExpanded({});
      setShowBulkAiModal(true);
      return;
    }

    setBulkAiItems(candidates);
    setBulkAiExpanded({});
    setBulkAiSelected(Object.fromEntries(candidates.map((c) => [c.shapeId, true])));
    setShowBulkAiModal(true);
    setIsBulkAiGenerating(true);

    const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o';

    // Preflight: daha anlaşılır hata mesajı için tek bir küçük istek
    try {
      await callAI({
        model,
        messages: [{ role: 'user', content: 'ping' }],
        max_tokens: 1,
        temperature: 0,
      });
    } catch (err) {
      const status = err?.status || err?.code || '';
      const msg = err?.message || String(err);
      const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
      setBulkAiError(`OpenAI bağlantı testi başarısız. model=${model}${status ? ` | status=${status}` : ''}\n${msg}${cause}`);
      setIsBulkAiGenerating(false);
      return;
    }

    for (const item of candidates) {
      setBulkAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'running', error: null } : p)),
      );

      try {
        const oldCode = item.oldCode || '';
        if (oldCode.length > 20000) {
          throw new Error('Script çok uzun (20000+ karakter). Önce kısaltın veya parçalayın.');
        }

        const instructionText = item.instructions.map((x) => `- ${x}`).join('\n');
        const userPrompt = `${instructionText}

Mevcut Kod:
${oldCode}`;

        const raw = await callAI({
          model,
          messages: [
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.8,
          max_tokens: 2000,
        });
        const newCode = stripCodeFences(raw);

        setBulkAiItems((prev) =>
          prev.map((p) => {
            if (p.shapeId !== item.shapeId) return p;
            const changed = newCode && newCode.trim() && newCode.trim() !== (p.oldCode || '').trim();
            return { ...p, newCode: newCode || '', status: changed ? 'ready' : 'nochange', error: null };
          }),
        );
      } catch (err) {
        const status = err?.status || err?.code || '';
        const msg = err?.message || String(err);
        const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
        setBulkAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId
              ? {
                  ...p,
                  status: 'error',
                  error: `${msg}${status ? ` (status=${status})` : ''}${cause}`,
                }
              : p,
          ),
        );
      }
    }

    setIsBulkAiGenerating(false);
  }, [
    activeTabId,
    detectLanguageFromScript,
    extractInstructionComments,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getScriptValueForShape,
    getShapeDisplayName,
    isBulkAiApplying,
    isBulkAiGenerating,
    stripCodeFences,
    tabCodeCacheRef,
    tabs,
    workflowData,
    // workflowDataRef çıkarıldı - ref'ler dependency'ye girmemeli
  ]);

  const handleApplyBulkAiUpdates = useCallback(async () => {
    if (isBulkAiGenerating || isBulkAiApplying) return;
    const toApply = bulkAiItems.filter(
      (it) => bulkAiSelected[it.shapeId] && (it.status === 'ready' || it.status === 'nochange') && it.newCode,
    );

    if (toApply.length === 0) {
      setBulkAiError('Uygulanacak güncelleme yok.');
      return;
    }

    setIsBulkAiApplying(true);
    setBulkAiError(null);


    for (const item of toApply) {
      setBulkAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saving', error: null } : p)),
      );
      try {
        const updatedCode = String(item.newCode || '');

        shapeCodeCacheRef.current[item.shapeId] = updatedCode;
        if (activeTabId) {
          writeTabCache(activeTabId, item.shapeId, updatedCode, 'bulk_ai_apply');
        }

        if (codeEditorShapeId && String(codeEditorShapeId) === String(item.shapeId)) {
          isUpdatingEditorRef.current = true;
          setCode(updatedCode);
          if (editorRef.current) editorRef.current.setValue(updatedCode);
          setTimeout(() => {
            isUpdatingEditorRef.current = false;
          }, 100);
        }


        const saveResult = await saveScript(item.shapeId, updatedCode, selectedTicketId);
        if (!saveResult?.success) {
          throw new Error(saveResult?.error || 'Script kaydetme başarısız');
        }

        setBulkAiItems((prev) =>
          prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saved', error: null } : p)),
        );
      } catch (err) {
        setBulkAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId ? { ...p, status: 'error', error: err?.message || String(err) } : p,
          ),
        );
      }
    }

    setIsBulkAiApplying(false);
  }, [
    activeTabId,
    bulkAiItems,
    bulkAiSelected,
    editorRef,
    isBulkAiApplying,
    isBulkAiGenerating,
    isUpdatingEditorRef,
    saveScript,
    selectedShape,
    selectedTicketId,
    setCode,
    shapeCodeCacheRef,
    tabCodeCacheRef,
  ]);

  // ========== AI: COMPILE SCRIPTS FROM JSON ai_prompt + AI_GLOBAL ==========
  const [showCompileAiModal, setShowCompileAiModal] = useState(false);
  const [compileAiItems, setCompileAiItems] = useState([]);
  const [compileAiActiveItemId, setCompileAiActiveItemId] = useState(null);
  const [compileAiViewTab, setCompileAiViewTab] = useState('diff');   // 'diff'|'old'|'new'|'prompt'
  const [compileAiCopiedId, setCompileAiCopiedId] = useState(null);  // copy feedback
  const [isCompileAiGenerating, setIsCompileAiGenerating] = useState(false);
  const [isCompileAiApplying, setIsCompileAiApplying] = useState(false);
  const [compileAiError, setCompileAiError] = useState(null);
  const [compileAiExpanded, setCompileAiExpanded] = useState({});
  const [compileAiSelected, setCompileAiSelected] = useState(() => ({}));

  const truncate = useCallback((text, max = 220) => {
    const t = String(text || '').trim();
    if (!t) return '';
    if (t.length <= max) return t;
    return `${t.slice(0, max)}…`;
  }, []);

  const normalizePhpTags = useCallback((code) => {
    const t = String(code || '').trim();
    // "kodu sil" gibi durumlarda gerçekten boş kalabilsin
    if (!t) return '';
    let out = t;
    if (!out.includes('<?php')) {
      out = `<?php\n${out}`;
    }
    if (!out.trim().endsWith('?>')) {
      out = `${out}\n?>`;
    }
    return out.trim();
  }, []);

  const validateGeneratedCodeAgainstPrompt = useCallback((prompt, code) => {
    const p = String(prompt || '').toLowerCase();
    const c = String(code || '');
    const issues = [];

    // 1) Genel değişken sayısı kontrolü (tamsayı, değişken vb.)
    const varCountMatch = p.match(/(\d+)\s*(adet\s*)?(değişken|degisken|tamsayı|tamsayi|integer|variable)/i);
    if (varCountMatch && varCountMatch[1]) {
      const expected = Math.max(0, parseInt(varCountMatch[1], 10) || 0);
      // PHP değişkenlerini say ($var şeklinde)
      const varMatches = c.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
      // Unique değişkenleri al (goitem_id hariç)
      const uniqueVars = [...new Set(varMatches)].filter(v => v !== '$goitem_id');
      const actual = uniqueVars.length;
      if (expected > 0 && actual < expected) {
        issues.push(`Değişken sayısı: beklenen ${expected}, bulunan ${actual} (${uniqueVars.slice(0, 5).join(', ')}${uniqueVars.length > 5 ? '...' : ''})`);
      }
    }

    // 2) "N random değişken" kontrolü (rand/mt_rand sayısı)
    const randCountMatch = p.match(/(\d+)\s*(adet\s*)?(random|rastgele)\s*(değişken|degisken)/i);
    if (randCountMatch && randCountMatch[1]) {
      const expected = Math.max(0, parseInt(randCountMatch[1], 10) || 0);
      const actual =
        (c.match(/\brand\s*\(/g) || []).length +
        (c.match(/\bmt_rand\s*\(/g) || []).length;
      if (expected > 0 && actual < expected) {
        issues.push(`Random değişken sayısı: beklenen ${expected}, bulunan ${actual}`);
      }
    }

    // 3) karekök isteniyorsa sqrt olmalı
    if (p.includes('karekök') || p.includes('karekok') || p.includes('sqrt')) {
      if (!/\bsqrt\s*\(/i.test(c)) {
        issues.push('Karekök (sqrt) kullanılmamış');
      }
    }

    // 4) goitem_id kuralı isteniyorsa 10/20 setleri olmalı
    if (p.includes('goitem_id')) {
      if (!/\$goitem_id\s*=\s*10\s*;/i.test(c)) issues.push('$goitem_id=10 set edilmiyor');
      if (!/\$goitem_id\s*=\s*20\s*;/i.test(c)) issues.push('$goitem_id=20 set edilmiyor');
    }

    // 5) "X'ten büyükse/küçükse" basit threshold kontrolü (varsa)
    const gtMatch = p.match(/sonuç\s*(\d+)\s*(ten|dan|den|tan)?\s*büyükse/i) || p.match(/(\d+)\s*(ten|dan|den|tan)?\s*büyükse/i);
    if (gtMatch && gtMatch[1]) {
      const x = gtMatch[1];
      if (!new RegExp(`>\\s*${x}\\b`).test(c)) {
        issues.push(`Karşılaştırma eşiği (> ${x}) kodda bulunamadı`);
      }
    }
    const ltMatch = p.match(/sonuç\s*(\d+)\s*(ten|dan|den|tan)?\s*küçükse/i) || p.match(/(\d+)\s*(ten|dan|den|tan)?\s*küçükse/i);
    if (ltMatch && ltMatch[1]) {
      const x = ltMatch[1];
      if (!new RegExp(`<\\s*${x}\\b`).test(c)) {
        issues.push(`Karşılaştırma eşiği (< ${x}) kodda bulunamadı`);
      }
    }

    return issues;
  }, []);

  const handleCompileScriptsFromJsonPrompts = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;

    setCompileAiError(null);
    const proxyProcessId = process.env.REACT_APP_OPENAI_PROXY_PROCESS;
    const proxyUrl = getOpenAIProxyUrl();
    const apiKey = getOpenAIKey();
    if (!apiKey && !proxyUrl && !proxyProcessId) {
      setCompileAiError(
        "OpenAI API key bulunamadı ve proxy tanımlı değil. Önerilen: server-side proxy + OPENAI_API_KEY.",
      );
      setShowCompileAiModal(true);
      return;
    }

    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = Array.isArray(currentWorkflowData?.contents) ? currentWorkflowData.contents : [];

    const steps = contents.filter((it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id);
    const edges = contents.filter((it) => it && (it.edge === '1' || it.edge === 1 || it.edge === true));

    const stepsById = new Map(steps.map((s) => [String(s.id), s]));
    const outgoingBySource = new Map();
    const incomingByTarget = new Map();
    for (const e of edges) {
      const sourceId = e?.source != null ? String(e.source) : '';
      const targetId = e?.target != null ? String(e.target) : '';
      if (!sourceId || !targetId) continue;
      if (!outgoingBySource.has(sourceId)) outgoingBySource.set(sourceId, []);
      outgoingBySource.get(sourceId).push(e);
      if (!incomingByTarget.has(targetId)) incomingByTarget.set(targetId, []);
      incomingByTarget.get(targetId).push(e);
    }

    const workflowInfo = currentWorkflowData?.workflow || {};
    const globalRulesRaw = currentWorkflowData?.AI_GLOBAL || workflowData?.AI_GLOBAL || [];
    const globalRules = Array.isArray(globalRulesRaw)
      ? globalRulesRaw.map((x) => String(x || '').trim()).filter(Boolean)
      : [String(globalRulesRaw || '').trim()].filter(Boolean);

    const candidates = steps
      .map((shapeData) => {
        const shapeId = String(shapeData.id);
        const aiPrompt = String(shapeData.ai_prompt || '').trim();
        const oldCode = getScriptValueForShape(shapeId, shapeData);
        const langGuess = detectLanguageFromScript(oldCode);
        const lang = langGuess && langGuess !== 'unknown' ? langGuess : 'php';
        const label = getShapeDisplayName(shapeId, shapeData);
        
        // ✅ Token Optimizasyonu: Önceki compile'dan eski prompt var mı?
        const existingItem = compileAiItems.find(it => it.shapeId === shapeId);
        const hasExistingGeneration = existingItem && existingItem.originalPrompt && existingItem.newCode;
        const promptUnchanged = hasExistingGeneration && existingItem.originalPrompt === aiPrompt;
        const shouldSkipAI = promptUnchanged && existingItem.newCode;
        
        return {
          shapeId,
          label,
          lang,
          aiPrompt,
          originalPrompt: aiPrompt, // Şimdiki promptu kaydet
          globalRules,
          oldCode,
          newCode: shouldSkipAI ? existingItem.newCode : '', // ✅ Cached kodu kullan
          status: !aiPrompt ? 'skipped' : shouldSkipAI ? 'cached' : 'pending',
          error: null,
        };
      })
      .filter((x) => x.status !== 'skipped');

    if (candidates.length === 0) {
      setCompileAiError('Hiçbir step için `ai_prompt` bulunamadı.');
      setCompileAiItems([]);
      setCompileAiSelected({});
      setCompileAiExpanded({});
      setShowCompileAiModal(true);
      return;
    }

    // Modal'ı aç ve listeyi göster
    setCompileAiItems(candidates);
    setCompileAiExpanded({});
    setCompileAiSelected(Object.fromEntries(candidates.map((c) => [c.shapeId, true])));
    setShowCompileAiModal(true);
    
    // NOT: Modal sadece açıldı, henüz generation başlamadı
    // Kullanıcı modal'da promptları görebilir ve Generate'e bastığında
    // EN GÜNCEL promptlarla generation başlar
  }, [
    callAI,
    compileAiItems, // ✅ Token optimization için gerekli
    detectLanguageFromScript,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getOpenAIProxyUrl,
    getScriptValueForShape,
    getShapeDisplayName,
    workflowData,
  ]);

  // Generate butonu için ayrı handler
  const handleStartCompileAiGeneration = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;

    setIsCompileAiGenerating(true);
    setCompileAiError(null);

    // EN GÜNCEL workflowData'yı oku
    const currentWorkflowData = getCurrentWorkflowDataForBulk();
    const contents = Array.isArray(currentWorkflowData?.contents) ? currentWorkflowData.contents : [];
    const steps = contents.filter((it) => it && (it.vertex === '1' || it.vertex === 1 || it.vertex === true) && it.id);
    const edges = contents.filter((it) => it && (it.edge === '1' || it.edge === 1 || it.edge === true));

    const stepsById = new Map(steps.map((s) => [String(s.id), s]));
    const outgoingBySource = new Map();
    const incomingByTarget = new Map();
    for (const e of edges) {
      const sourceId = e?.source != null ? String(e.source) : '';
      const targetId = e?.target != null ? String(e.target) : '';
      if (!sourceId || !targetId) continue;
      if (!outgoingBySource.has(sourceId)) outgoingBySource.set(sourceId, []);
      outgoingBySource.get(sourceId).push(e);
      if (!incomingByTarget.has(targetId)) incomingByTarget.set(targetId, []);
      incomingByTarget.get(targetId).push(e);
    }

    const workflowInfo = currentWorkflowData?.workflow || {};
    const globalRulesRaw = currentWorkflowData?.AI_GLOBAL || workflowData?.AI_GLOBAL || [];
    const globalRules = Array.isArray(globalRulesRaw)
      ? globalRulesRaw.map((x) => String(x || '').trim()).filter(Boolean)
      : [String(globalRulesRaw || '').trim()].filter(Boolean);

    // ✅ Mevcut listeyi GÜNCELLE (yeniden oluşturma!)
    // Modal açıldıktan sonra kullanıcı prompt değiştirmiş olabilir, EN GÜNCEL prompt'ları al
    const candidates = compileAiItems.map((existingItem) => {
      const shapeData = stepsById.get(existingItem.shapeId);
      if (!shapeData) return existingItem; // Shape silinmişse mevcut item'ı koru

      const aiPrompt = String(shapeData.ai_prompt || '').trim();
      const oldCode = getScriptValueForShape(existingItem.shapeId, shapeData);

      // Token Optimizasyonu: Prompt değişti mi kontrol et
      const promptChanged = existingItem.originalPrompt !== aiPrompt;
      
      // Eğer prompt değiştiyse status'u pending'e çevir
      if (promptChanged && existingItem.status === 'cached') {
        return {
          ...existingItem,
          aiPrompt,
          originalPrompt: aiPrompt, // Yeni promptu kaydet
          oldCode,
          status: aiPrompt ? 'pending' : 'skipped',
          error: null,
        };
      }

      // Prompt değişmediyse mevcut item'ı koru
      return {
        ...existingItem,
        aiPrompt, // En güncel promptu yükle
        oldCode, // En güncel kodu yükle
      };
    });

    // Listeyi güncelle
    setCompileAiItems(candidates);

    const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o';

    const systemPrompt = `Sen bir ERP flowchart geliştirme ortamında PHP script üreten bir yazılım mühendisisin.

KURALLAR:
- Sadece PHP kodu üret. Açıklama, yorum veya markdown yazma.
- Kod \`<?php\` ile başlamalı ve \`?>\` ile bitmeli.
- Kullanıcının ai_prompt talimatını birebir uygula.
- Mevcut kod varsa, onu referans al ve talimatına göre güncelle.
- Değişken isimleri ve tablo adları büyük harf olmalı (ERP konvansiyonu).
- $goitem_id değişkeni flowchart routing için kullanılır (10=devam, 20=hata vb.).
- AI_GLOBAL kuralları tüm step'ler için geçerli genel kurallardır.
- Flowchart bağlamı (gelen/giden edge'ler) sana verilecek, ona göre routing yap.`;

    console.log('%c[AI Compile] Preflight ping başlatılıyor...', 'color:#fbbf24;font-weight:bold', { model });
    try {
      const pingRes = await callAI({
        model,
        messages: [{ role: 'user', content: 'ping' }],
        max_tokens: 5,
        temperature: 0,
      });
      console.log('%c[AI Compile] Preflight OK', 'color:#4ade80;font-weight:bold', String(pingRes).substring(0, 100));
    } catch (err) {
      console.error('%c[AI Compile] Preflight HATA', 'color:#f87171;font-weight:bold', err);
      const status = err?.status || err?.code || '';
      const msg = err?.message || String(err);
      const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
      setCompileAiError(
        `OpenAI bağlantı testi başarısız. model=${model}${status ? ` | status=${status}` : ''}\n${msg}${cause}`,
      );
      setIsCompileAiGenerating(false);
      return;
    }

    for (const item of candidates) {
      if (item.status === 'cached') continue;

      setCompileAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'running', error: null } : p)),
      );

      try {
        const promptLower = (item.aiPrompt || '').toLocaleLowerCase('tr-TR');
        const hasDontDeleteIntent =
          /\bsilmeden\b/.test(promptLower) ||
          /\bsilme\b/.test(promptLower) ||
          /\bsilinmesin\b/.test(promptLower) ||
          /\bdelete\s+etme\b/.test(promptLower) ||
          /\bdo\s+not\s+delete\b/.test(promptLower) ||
          /\bwithout\s+delet/i.test(promptLower);
        const hasDeleteIntent =
          /kodu\s+tamamen\s+sil/.test(promptLower) ||
          /tamamen\s+sil/.test(promptLower) ||
          /delete\s+code/.test(promptLower) ||
          /remove\s+all\s+code/.test(promptLower) ||
          /clear\s+script/.test(promptLower) ||
          /\bkodu\s+sil\b/.test(promptLower) ||
          /\bscripti\s+sil\b/.test(promptLower) ||
          /\bdelete\s+script\b/.test(promptLower);
        const shouldDeleteCode = hasDeleteIntent && !hasDontDeleteIntent;
        
        if (shouldDeleteCode) {
          const newCode = '';
          setCompileAiItems((prev) =>
            prev.map((p) => {
              if (p.shapeId === item.shapeId) {
                const hasOldCode = (p.oldCode || '').trim().length > 0;
                const newStatus = hasOldCode ? 'ready' : 'nochange';
                return { ...p, newCode, status: newStatus, error: null };
              }
              return p;
            }),
          );
          continue;
        }

        if ((item.oldCode || '').length > 20000) {
          throw new Error('Script çok uzun (20000+ karakter). Önce kısaltın veya parçalayın.');
        }

        const stepData = stepsById.get(item.shapeId);
        const outEdges = outgoingBySource.get(item.shapeId) || [];
        const inEdges = incomingByTarget.get(item.shapeId) || [];

        const outgoingText =
          outEdges.length === 0
            ? '— (terminal step / çıkış yok)'
            : outEdges
                .map((e) => {
                  const kosul = e?.kosul != null ? String(e.kosul) : '';
                  const targetId = e?.target != null ? String(e.target) : '';
                  const targetDef = stepsById.get(targetId)?.definition || targetId;
                  return `- kosul(goitem_id)=${kosul} => target="${targetDef}" (${targetId})`;
                })
                .join('\n');

        const incomingText =
          inEdges.length === 0
            ? '—'
            : inEdges
                .map((e) => {
                  const kosul = e?.kosul != null ? String(e.kosul) : '';
                  const sourceId = e?.source != null ? String(e.source) : '';
                  const sourceDef = stepsById.get(sourceId)?.definition || sourceId;
                  const srcPrompt = truncate(stepsById.get(sourceId)?.ai_prompt || '', 200);
                  return `- source="${sourceDef}" (${sourceId}) [kosul=${kosul}] prompt: "${srcPrompt}"`;
                })
                .join('\n');

        const globalRulesText = globalRules.length ? globalRules.map((x) => `- ${x}`).join('\n') : '(yok)';

        const userPrompt = `## Step Bilgisi
- ID: ${item.shapeId}
- Label: ${stepData?.definition || item.label || ''}
- Dil: ${item.lang}

## Gelen Edge'ler (Bu step'e nereden geliniyor)
${incomingText}

## Giden Edge'ler (Bu step'ten nereye gidiliyor)
${outgoingText}

## AI_GLOBAL Kuralları
${globalRulesText}

## Talimat (ai_prompt)
${item.aiPrompt}

## Mevcut Kod
\`\`\`${item.lang}
${item.oldCode || '// (boş)'}
\`\`\`

Yukarıdaki talimatı uygulayarak kodu üret veya güncelle. Sadece kod döndür.`;

        console.log(`%c[AI Compile] ${item.shapeId} — İstek gönderiliyor (model=${model})`, 'color:#a78bfa;font-weight:bold');
        console.log('[AI Compile] Prompt:', item.aiPrompt);

        const raw = await callAI({
          model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          temperature: 0.2,
          max_tokens: 4096,
        });

        console.log(`%c[AI Compile] ${item.shapeId} — Response alındı`, 'color:#4ade80;font-weight:bold');
        console.log('[AI Compile] Raw response (ilk 500 char):', String(raw || '').substring(0, 500));
        console.log('[AI Compile] Raw response uzunluk:', String(raw || '').length);

        let newCode = stripCodeFences(raw);
        if (item.lang === 'php') newCode = normalizePhpTags(newCode);

        console.log('[AI Compile] stripCodeFences sonrası uzunluk:', newCode.length);
        console.log('[AI Compile] Eski kod uzunluk:', (item.oldCode || '').trim().length);
        console.log('[AI Compile] Yeni === Eski?', newCode.trim() === (item.oldCode || '').trim());

        const issues = validateGeneratedCodeAgainstPrompt(item.aiPrompt, newCode);
        if (issues.length) {
          const retryPrompt = userPrompt + `\n\n## RETRY — Önceki üretimde tespit edilen hatalar:\n${issues.map(e => `- ${e}`).join('\n')}\n\nBu hataları düzelterek kodu tekrar üret.`;
          
          const retryRaw = await callAI({
            model,
            messages: [
              { role: 'system', content: systemPrompt },
              {
                role: 'user',
                content: retryPrompt,
              },
            ],
            temperature: 0.1,
            max_tokens: 4096,
          });
          let retryCode = stripCodeFences(retryRaw);
          if (item.lang === 'php') retryCode = normalizePhpTags(retryCode);
          
          // AI Response Log (Retry)
          
          const retryIssues = validateGeneratedCodeAgainstPrompt(item.aiPrompt, retryCode);
          if (retryIssues.length === 0) {
            newCode = retryCode;
          } else {
            // yine başarısızsa item hata verelim ama yine de newCode'u gösterelim
            setCompileAiItems((prev) =>
              prev.map((p) =>
                p.shapeId === item.shapeId
                  ? { ...p, status: 'error', error: `Prompt doğrulama başarısız: ${retryIssues.join(' | ')}` }
                  : p,
              ),
            );
          }
        }

        setCompileAiItems((prev) =>
          prev.map((p) => {
            if (p.shapeId !== item.shapeId) return p;
            const newCodeTrim = (newCode || '').trim();
            const oldCodeTrim = (p.oldCode || '').trim();
            const changed = newCodeTrim && newCodeTrim !== oldCodeTrim;
            const status = changed ? 'ready' : 'nochange';
            
            // DEBUG: Status belirleme mantığını logla
            
            return { ...p, newCode: newCode || '', status, error: null };
          }),
        );
      } catch (err) {
        const status = err?.status || err?.code || '';
        const msg = err?.message || String(err);
        const cause = err?.cause?.message ? ` | cause: ${err.cause.message}` : '';
        setCompileAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId
              ? { ...p, status: 'error', error: `${msg}${status ? ` (status=${status})` : ''}${cause}` }
              : p,
          ),
        );
      }
    }

    setIsCompileAiGenerating(false);
  }, [
    callAI,
    compileAiItems,
    detectLanguageFromScript,
    getCurrentWorkflowDataForBulk,
    getOpenAIKey,
    getOpenAIProxyUrl,
    getScriptValueForShape,
    getShapeDisplayName,
    isCompileAiApplying,
    isCompileAiGenerating,
    normalizePhpTags,
    stripCodeFences,
    truncate,
    validateGeneratedCodeAgainstPrompt,
    workflowData,
  ]);

  const handleApplyCompileAiUpdates = useCallback(async () => {
    if (isCompileAiGenerating || isCompileAiApplying) return;
    const toApply = compileAiItems.filter(
      (it) => compileAiSelected[it.shapeId] && (it.status === 'ready' || it.status === 'cached') && it.newCode,
    );
    if (toApply.length === 0) {
      setCompileAiError('Uygulanacak güncelleme yok.');
      return;
    }

    setIsCompileAiApplying(true);
    setCompileAiError(null);


    for (const item of toApply) {
      setCompileAiItems((prev) =>
        prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saving', error: null } : p)),
      );
      try {
        const updatedCode = String(item.newCode || '');

        shapeCodeCacheRef.current[item.shapeId] = updatedCode;
        if (activeTabId) {
          writeTabCache(activeTabId, item.shapeId, updatedCode, 'compile_ai_apply');
        }

        if (codeEditorShapeId && String(codeEditorShapeId) === String(item.shapeId)) {
          isUpdatingEditorRef.current = true;
          setCode(updatedCode);
          if (editorRef.current) editorRef.current.setValue(updatedCode);
          setTimeout(() => {
            isUpdatingEditorRef.current = false;
          }, 100);
        }


        const saveResult = await saveScript(item.shapeId, updatedCode, selectedTicketId);
        if (!saveResult?.success) {
          throw new Error(saveResult?.error || 'Script kaydetme başarısız');
        }

        setCompileAiItems((prev) =>
          prev.map((p) => (p.shapeId === item.shapeId ? { ...p, status: 'saved', error: null } : p)),
        );
      } catch (err) {
        setCompileAiItems((prev) =>
          prev.map((p) =>
            p.shapeId === item.shapeId ? { ...p, status: 'error', error: err?.message || String(err) } : p,
          ),
        );
      }
    }

    setIsCompileAiApplying(false);
  }, [
    activeTabId,
    compileAiItems,
    compileAiSelected,
    editorRef,
    isCompileAiApplying,
    isCompileAiGenerating,
    isUpdatingEditorRef,
    saveScript,
    selectedShape,
    selectedTicketId,
    setCode,
    shapeCodeCacheRef,
    tabCodeCacheRef,
  ]);
  // Save design mode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('flowchart-design-mode', String(isDesignMode));
    } catch (error) {
      console.warn('Failed to save design mode:', error);
    }
  }, [isDesignMode]);
  
  
  // Shape definitions - mxGraph'in built-in shape'lerini kullan
  // Her şekil için flowchart algısına uygun renkler
  const shapes = useMemo(() => [
    { name: 'startCircle', label: 'Başlangıç', mxShape: 'ellipse', color: '#22c55e' }, // Yeşil
    { name: 'stopCircle', label: 'Bitiş', mxShape: 'ellipse', color: '#ef4444' }, // Kırmızı
    { name: 'rectangle', label: 'İşlem', mxShape: 'rectangle', color: '#3b82f6' }, // Mavi
    { name: 'preProcess', label: 'Alt İşlem', mxShape: 'predefinedProcess', color: '#60a5fa' }, // Açık mavi - çift kenarlı
    { name: 'rhombus', label: 'Karar', mxShape: 'rhombus', color: '#f59e0b' }, // Turuncu
    { name: 'triangle', label: 'Üçgen', mxShape: 'triangle', color: '#a855f7' }, // Mor - custom triangle
    { name: 'hexagon', label: 'Altıgen', mxShape: 'hexagon', color: '#8b5cf6' }, // Mor-altıgen
    { name: 'cylinder', label: 'Veri', mxShape: 'cylinder', color: '#06b6d4' }, // Turkuaz
  ], []);

  // ========== CODE EDITOR FUNCTIONS ==========
  const handleEditorDidMount = useCallback((editor, monaco) => {
    if (!editor || !monaco) return;
    
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Editor container'ına data attribute ekle - keyboard event kontrolü için
    try {
      const editorDomNode = editor.getDomNode();
      if (editorDomNode) {
        editorDomNode.setAttribute('data-monaco-editor', 'true');
      }
    } catch (error) {
    }

    // AI Completion Provider'ı kaydet
    const apiKey = getOpenAIKey();
    if (apiKey) {
      setAiStatus({ enabled: true, loading: false, error: null });
      
      const completionProvider = {
        provideCompletionItems: async (model, position) => {
          try {
            const textUntilPosition = model.getValueInRange({
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            });

            // AI önerilerini al
            const aiSuggestions = await getAICodeSuggestions(textUntilPosition, position);
            
            return {
              suggestions: aiSuggestions,
            };
          } catch (error) {
            return { suggestions: [] };
          }
        },
        triggerCharacters: ['.', '(', '$', '>', ' ', '\n'],
      };

      monaco.languages.registerCompletionItemProvider('php', completionProvider);
    } else {
      setAiStatus({ enabled: false, loading: false, error: 'OpenAI API key bulunamadı. Lütfen REACT_APP_OPENAI_API_KEY environment variable\'ını ayarlayın veya localStorage\'a "openai_api_key" olarak kaydedin.' });
    }
    
    setTimeout(() => {
      if (editor && editor.focus) {
        editor.focus();
      }
    }, 100);
  }, [getOpenAIKey, getAICodeSuggestions]);

  // RAF handle: her tuşta sadece ref + cache güncellenir; setCode sadece idle frame'de çalışır
  const codeChangeRAFRef = useRef(null);
  const codeChangeLogTsRef = useRef(0);

  const handleCodeChange = useCallback((value) => {
    if (isUpdatingEditorRef.current) return;

    const newValue = value || '';

    // PERFORMANS: Keystroke başına sadece cache yaz. Debug log yok (hot path).
    // Anomaly kontrolü de hızlı: isShapeInTabWorkflow check'i yalnızca guard için.
    if (codeEditorShapeId && activeTabId) {
      if (isShapeInTabWorkflow(codeEditorShapeId, activeTabId)) {
        shapeCodeCacheRef.current[codeEditorShapeId] = newValue;
        writeTabCache(activeTabId, codeEditorShapeId, newValue, 'handleCodeChange');
      } else {
        // Cross-file guard — sadece anomaly'yi logla (nadiren fire eder)
        pushDebugLog('anomaly_code_change_foreign_shape_blocked', {
          shapeId: String(codeEditorShapeId),
          tabId: String(activeTabId),
          codeLength: newValue.length,
        });
      }
    }

    if (codeChangeRAFRef.current) {
      cancelAnimationFrame(codeChangeRAFRef.current);
      codeChangeRAFRef.current = null;
    }
    setCode(newValue);
  }, [codeEditorShapeId, activeTabId, pushDebugLog, isShapeInTabWorkflow, writeTabCache]);

  // Tab veya code-editor shape değişince pending RAF'i iptal et: stale setCode riski kalmaz.
  useEffect(() => {
    if (codeChangeRAFRef.current) {
      cancelAnimationFrame(codeChangeRAFRef.current);
      codeChangeRAFRef.current = null;
    }
  }, [activeTabId, codeEditorShapeId]);

  const handleSaveScript = useCallback(async () => {
    if (!codeEditorShapeId || !code) return;
    
    setIsSavingScript(true);
    setSaveScriptStatus(null);
    
    try {
      const result = await saveScript(codeEditorShapeId, code);
      if (result.success) {
        setSaveScriptStatus({ type: 'success', message: 'Script başarıyla kaydedildi!' });
        setTimeout(() => setSaveScriptStatus(null), 3000);
      } else {
        setSaveScriptStatus({ type: 'error', message: result.error || 'Script kaydetme başarısız!' });
      }
    } catch (error) {
      setSaveScriptStatus({ type: 'error', message: error.message || 'Script kaydetme başarısız!' });
    } finally {
      setIsSavingScript(false);
    }
  }, [codeEditorShapeId, code, saveScript]);

  const appendTerminalLine = useCallback((line) => {
    const safeLine = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      type: 'system',
      text: '',
      ...line,
    };
    setTerminalLines((prev) => [...prev, safeLine]);
  }, []);

  const handleTerminalClear = useCallback(() => {
    setTerminalLines([
      { id: 'ready', type: 'system', text: '$ ready' },
      { id: 'hint', type: 'muted', text: '# AI terminal temizlendi' },
    ]);
  }, []);

  useEffect(() => {
    if (!showTerminal) return;
    if (!codeEditorShapeId) {
      appendTerminalLine({ type: 'muted', text: '# Şekil seçilmedi' });
      return;
    }
    appendTerminalLine({
      type: 'system',
      text: `# Seçili şekil: ${codeEditorShapeLabel || codeEditorShapeId}`,
    });
  }, [showTerminal, codeEditorShapeId, codeEditorShapeLabel, appendTerminalLine]);

  useEffect(() => {
    if (!pendingChange) return;
    if (codeEditorShapeId && pendingChange.shapeId !== codeEditorShapeId) {
      setPendingChange(null);
      appendTerminalLine({ type: 'muted', text: '# Şekil değişti, önizleme iptal edildi.' });
    }
  }, [pendingChange, codeEditorShapeId, appendTerminalLine]);

  const handleTerminalSubmit = useCallback(async () => {
    const prompt = terminalInput.trim();
    if (!prompt) return;

    appendTerminalLine({ type: 'user', text: `> ${prompt}` });
    setTerminalInput('');

    if (!codeEditorShapeId) {
      appendTerminalLine({ type: 'error', text: '! Önce bir şekil seçmelisin.' });
      return;
    }

    const apiKey = getOpenAIKey();
    if (!apiKey) {
      appendTerminalLine({
        type: 'error',
        text: '! OpenAI API key bulunamadı. Ayarlarını kontrol et.',
      });
      return;
    }

    setIsTerminalBusy(true);
    appendTerminalLine({ type: 'system', text: '# Kod güncelleniyor…' });

    try {
      const currentCode = code || '';
      const updatedCode = await generateCodeFromPrompt(prompt, currentCode, 'modify', currentCode);

      if (!updatedCode) {
        appendTerminalLine({ type: 'error', text: '! Kod üretilemedi. Lütfen tekrar dene.' });
        return;
      }

      setPendingChange({
        shapeId: codeEditorShapeId,
        shapeLabel: codeEditorShapeLabel || codeEditorShapeId,
        oldCode: currentCode,
        newCode: updatedCode,
        prompt,
      });
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) {
        editorRef.current.setValue(updatedCode);
      }
      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);

      appendTerminalLine({ type: 'ai', text: '✓ Önizleme hazır. Keep/Undo ile onayla.' });
    } catch (error) {
      appendTerminalLine({
        type: 'error',
        text: `! İşlem hatası: ${error?.message || 'Bilinmeyen hata'}`,
      });
    } finally {
      setIsTerminalBusy(false);
    }
  }, [
    terminalInput,
    appendTerminalLine,
    codeEditorShapeId,
    codeEditorShapeLabel,
    getOpenAIKey,
    generateCodeFromPrompt,
    code,
  ]);

  /* ── Agent panel'den tek şekil için AI kodu üret (callAI üzerinden) ── */
  const handleRunShapeAI = useCallback(async (prompt) => {
    if (!prompt?.trim() || !codeEditorShapeId) return;

    const proxyProcessId = process.env.REACT_APP_OPENAI_PROXY_PROCESS;
    const apiKey = getOpenAIKey();
    const proxyUrl = getOpenAIProxyUrl();
    if (!apiKey && !proxyProcessId && !proxyUrl) {
      appendTerminalLine({ type: 'error', text: '! API key veya proxy bulunamadı.' });
      return;
    }

    setIsTerminalBusy(true);
    const model = process.env.REACT_APP_FLOWCHART_AI_MODEL || process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o';
    appendTerminalLine({ type: 'system', text: `# "${codeEditorShapeLabel || codeEditorShapeId}" — ${model} ile üretiliyor…` });

    try {
      const currentCode = code || '';

      const systemMsg = `Sen bir ERP flowchart ortamında PHP script üreten yazılım mühendisisin. Sadece PHP kodu üret, açıklama yazma. Kod <?php ile başlamalı ve ?> ile bitmeli.`;

      const userMsg = `## Talimat
${prompt.trim()}

## Mevcut Kod
\`\`\`php
${currentCode || '// (boş)'}
\`\`\`

Talimatı uygulayarak kodu üret veya güncelle. Sadece kod döndür.`;

      const raw = await callAI({
        model,
        messages: [
          { role: 'system', content: systemMsg },
          { role: 'user', content: userMsg },
        ],
        temperature: 0.2,
        max_tokens: 4096,
      });

      let updatedCode = (raw || '').replace(/^```(?:php)?\s*/i, '').replace(/```\s*$/, '').trim();
      if (!updatedCode) {
        appendTerminalLine({ type: 'error', text: '! Kod üretilemedi. Lütfen tekrar dene.' });
        return;
      }

      setPendingChange({
        shapeId: codeEditorShapeId,
        shapeLabel: codeEditorShapeLabel || codeEditorShapeId,
        oldCode: currentCode,
        newCode: updatedCode,
        prompt: prompt.trim(),
      });
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) editorRef.current.setValue(updatedCode);
      setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
      appendTerminalLine({ type: 'ai', text: `✓ Önizleme hazır (${model}). Keep ile onayla.` });
    } catch (err) {
      appendTerminalLine({ type: 'error', text: `! Hata: ${err?.message || 'Bilinmeyen hata'}` });
    } finally {
      setIsTerminalBusy(false);
    }
  }, [
    codeEditorShapeId, codeEditorShapeLabel, getOpenAIKey, getOpenAIProxyUrl, callAI,
    code, appendTerminalLine, setPendingChange, setCode,
  ]);

  const handleApplyPendingChange = useCallback(async () => {
    if (!pendingChange || !pendingChange.newCode || !pendingChange.shapeId) return;
    if (pendingChange.shapeId !== codeEditorShapeId) {
      appendTerminalLine({ type: 'error', text: '! Değişiklik başka bir şekle ait.' });
      return;
    }

    setIsApplyingChange(true);
    try {
      const updatedCode = pendingChange.newCode;
      isUpdatingEditorRef.current = true;
      setCode(updatedCode);
      if (editorRef.current) {
        editorRef.current.setValue(updatedCode);
      }

      shapeCodeCacheRef.current[codeEditorShapeId] = updatedCode;
      if (codeEditorShapeId && activeTabId) {
        writeTabCache(activeTabId, codeEditorShapeId, updatedCode, 'handleApplyPendingChange');
      }

      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);

      const saveResult = await saveScript(codeEditorShapeId, updatedCode);
      if (saveResult.success) {
        appendTerminalLine({ type: 'ai', text: '✓ Script güncellendi ve kaydedildi.' });
        setPendingChange(null);
      } else {
        appendTerminalLine({
          type: 'error',
          text: `! Kaydetme başarısız: ${saveResult.error || 'Bilinmeyen hata'}`,
        });
      }
    } catch (error) {
      appendTerminalLine({
        type: 'error',
        text: `! İşlem hatası: ${error?.message || 'Bilinmeyen hata'}`,
      });
    } finally {
      setIsApplyingChange(false);
    }
  }, [
    pendingChange,
    codeEditorShapeId,
    activeTabId,
    appendTerminalLine,
    saveScript,
  ]);

  const handleDiscardPendingChange = useCallback(() => {
    if (!pendingChange) return;
    const previousCode = pendingChange.oldCode || '';
    isUpdatingEditorRef.current = true;
    setCode(previousCode);
    if (editorRef.current) {
      editorRef.current.setValue(previousCode);
    }
    setTimeout(() => {
      isUpdatingEditorRef.current = false;
    }, 100);
    setPendingChange(null);
    appendTerminalLine({ type: 'muted', text: '# Değişiklik iptal edildi.' });
  }, [pendingChange, appendTerminalLine]);

  const handleNewScript = useCallback(() => {
    const newScript = `<?php\n// Yeni script oluşturuldu\n// Tarih: ${new Date().toLocaleDateString('tr-TR')}\n\nfunction processData() {\n    // Kodunuzu buraya yazın\n    echo "Merhaba Dünya!";\n}\n\n// Ana işlem\nprocessData();\n?>`;
    isUpdatingEditorRef.current = true;
    setCode(newScript);
    if (editorRef.current) {
      editorRef.current.setValue(newScript);
    }
    if (codeEditorShapeId && activeTabId) {
      writeTabCache(activeTabId, codeEditorShapeId, newScript, 'handleNewScript');
    }
    if (codeEditorShapeId) {
      shapeCodeCacheRef.current[codeEditorShapeId] = newScript;
    }
    setTimeout(() => {
      isUpdatingEditorRef.current = false;
    }, 100);
  }, [codeEditorShapeId, activeTabId]);

  // Load script when code editor shape changes or flow tab changes
  // codeEditorShapeId yalnızca sağ-tık "Code Editor" ve tab tıklamasıyla değişir — sol tıktan bağımsız
  useEffect(() => {
    const previousShape = previousSelectedShapeRef.current;
    const previousTabId = previousActiveTabIdRef.current;
    
    const shapeChanged = previousShape !== codeEditorShapeId;
    const tabChanged = previousTabId !== activeTabId;
    if (shapeChanged || tabChanged) {
      pushDebugLog('editor_context_change', {
        previousShape: previousShape || null,
        nextShape: codeEditorShapeId || null,
        previousTabId: previousTabId || null,
        nextTabId: activeTabId || null,
        shapeChanged,
        tabChanged,
      });
    }
    
    // PREV CACHE BLOCK TAMAMEN KALDIRILDI.
    // Gerekçe: Sızıntı timing yarışları bu blokta ortaya çıkıyordu. editor.getValue()'nin
    // gerçekten (previousTabId, previousShape) çiftine ait olduğunu ispatlayamayız
    // (Editor remount, Monaco async onChange, setValue side effects, vb.).
    // handleCodeChange her tuşta cache'i zaten senkronize tuttuğu için bu blok gereksiz.
    
    // Tab değiştiyse ve şekil değişmediyse, sadece tab bazlı cache'den yükle
    if (tabChanged && !shapeChanged && codeEditorShapeId && activeTabId) {
      // CROSS-FILE GUARD: Eğer bu shape yeni tab'ın workflow'unda yoksa hiçbir şey yapma.
      // Aksi halde tab-N'in cache'ini yabancı shape ile kirletiyoruz ve save'de ezme oluyor.
      if (!isShapeInTabWorkflow(codeEditorShapeId, activeTabId)) {
        pushDebugLog('editor_load_skipped_foreign_shape', {
          shapeId: String(codeEditorShapeId),
          tabId: String(activeTabId),
          reason: 'tab_changed_same_shape',
        });
        previousSelectedShapeRef.current = codeEditorShapeId;
        previousActiveTabIdRef.current = activeTabId;
        return;
      }
      if (!tabCodeCacheRef.current[activeTabId]) {
        tabCodeCacheRef.current[activeTabId] = {};
      }
      
      const cachedValue = tabCodeCacheRef.current[activeTabId][codeEditorShapeId];
      let scriptValue = '';
      
      if (cachedValue !== undefined) {
        scriptValue = cachedValue !== null ? String(cachedValue) : '';
        pushDebugLog('editor_script_load_from_tab_cache', {
          shapeId: String(codeEditorShapeId),
          tabId: String(activeTabId),
          scriptLength: scriptValue.length,
          reason: 'tab_changed_same_shape',
        });
      } else {
        const activeTab = tabs.find(tab => tab.id === activeTabId);
        const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
        const shapeData = currentWorkflowData?.contents?.find(item => item.id === codeEditorShapeId);
        const script = shapeData?.script || '';
        scriptValue = typeof script === 'string' ? script : '';
        
        writeTabCache(activeTabId, codeEditorShapeId, scriptValue, 'script_load_workflow_tab_changed');
        shapeCodeCacheRef.current[codeEditorShapeId] = scriptValue;
        pushDebugLog('editor_script_load_from_workflow', {
          shapeId: String(codeEditorShapeId),
          tabId: String(activeTabId),
          scriptLength: scriptValue.length,
          reason: 'tab_changed_same_shape_cache_miss',
        });
      }
      
      if (editorRef.current) {
        isUpdatingEditorRef.current = true;
        editorRef.current.setValue(scriptValue);
        setTimeout(() => { isUpdatingEditorRef.current = false; }, 300);
      }
      
      setCode(scriptValue);
      previousSelectedShapeRef.current = codeEditorShapeId;
      previousActiveTabIdRef.current = activeTabId;
      return;
    }
    
    if (!shapeChanged && !tabChanged) {
      return;
    }

    previousSelectedShapeRef.current = codeEditorShapeId;
    previousActiveTabIdRef.current = activeTabId;
    
    const currentWorkflowData = workflowDataRef.current || workflowData;
    if (!codeEditorShapeId || !currentWorkflowData?.contents) {
      // codeEditorShapeId null ise editor'ı temizle
      isUpdatingEditorRef.current = true;
      setCode('');
      if (editorRef.current) {
        editorRef.current.setValue('');
      }
      setTimeout(() => {
        isUpdatingEditorRef.current = false;
      }, 100);
      return;
    }

    // CROSS-FILE GUARD: shape bu tab'ın workflow'unda yoksa yükleme.
    if (codeEditorShapeId && activeTabId && !isShapeInTabWorkflow(codeEditorShapeId, activeTabId)) {
      pushDebugLog('editor_load_skipped_foreign_shape', {
        shapeId: String(codeEditorShapeId),
        tabId: String(activeTabId),
        reason: 'general_shape_tab_change',
      });
      return;
    }
    
    // Önce tab bazlı cache'den kontrol et, yoksa workflowData'dan oku
    let scriptValue = '';
    
    // Tab bazlı cache'i başlat
    if (!tabCodeCacheRef.current[activeTabId]) {
      tabCodeCacheRef.current[activeTabId] = {};
    }
    
    const cachedValue = activeTabId && tabCodeCacheRef.current[activeTabId] 
      ? tabCodeCacheRef.current[activeTabId][codeEditorShapeId] 
      : undefined;
    
    if (cachedValue !== undefined) {
      scriptValue = cachedValue !== null ? String(cachedValue) : '';
      pushDebugLog('editor_script_load_from_tab_cache', {
        shapeId: String(codeEditorShapeId),
        tabId: String(activeTabId),
        scriptLength: scriptValue.length,
        reason: 'shape_or_tab_change',
      });
    } else {
      const shapeData = currentWorkflowData.contents.find(item => item.id === codeEditorShapeId);
      const script = shapeData?.script || '';
      scriptValue = typeof script === 'string' ? script : '';
    
      if (activeTabId) {
        writeTabCache(activeTabId, codeEditorShapeId, scriptValue, 'script_load_workflow_general');
      }
      shapeCodeCacheRef.current[codeEditorShapeId] = scriptValue;
      pushDebugLog('editor_script_load_from_workflow', {
        shapeId: String(codeEditorShapeId),
        tabId: String(activeTabId),
        scriptLength: scriptValue.length,
        reason: 'shape_or_tab_change_cache_miss',
      });
    }

    // NOT: Eskiden burada cachedValueBeforeUpdate / setTimeout cache-preserve vardı.
    // Kaldırıldı çünkü cross-file pollution kaynağıydı: shapeCodeCacheRef (global) ile
    // tabCodeCacheRef'e yazım yapıp yabancı tab'daki shape'in yerine geçebiliyordu.
    if (editorRef.current) {
      isUpdatingEditorRef.current = true;
      editorRef.current.setValue(scriptValue);
      setTimeout(() => { isUpdatingEditorRef.current = false; }, 300);

      const position = editorRef.current.getPosition();
      if (position) {
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.setPosition(position);
          }
        }, 10);
      }
    }

    setCode(scriptValue);
  }, [codeEditorShapeId, activeTabId, tabs, pushDebugLog, isShapeInTabWorkflow, writeTabCache]);

  // Seçili şeklin rengini ve label'ını güncelle (design toolbar için)
  useEffect(() => {
    if (!selectedShape || !graphRef.current) {
      setSelectedShapeLabel('');
      return;
    }
    
    const graph = graphRef.current;
    const model = graph.getModel();
    const cell = model.getCell(selectedShape);
    
    if (cell && model.isVertex(cell)) {
      const style = cell.getStyle() || '';
      const fillColorMatch = style.match(/fillColor=([^;]+)/);
      if (fillColorMatch) {
        setSelectedShapeColor(fillColorMatch[1]);
      } else {
        setSelectedShapeColor('#FFFF00');
      }
      
      const cellValue = cell.getValue();
      const label = cellValue ? String(cellValue).trim() : '';
      setSelectedShapeLabel(label || selectedShape);
    } else {
      setSelectedShapeLabel('');
    }
  }, [selectedShape]);

  // Code editor'ın kendi shape label'ını güncelle (design seçiminden bağımsız)
  useEffect(() => {
    if (!codeEditorShapeId || !graphRef.current) {
      setCodeEditorShapeLabel('');
      return;
    }
    const graph = graphRef.current;
    const model = graph.getModel();
    const cell = model.getCell(codeEditorShapeId);
    if (cell && model.isVertex(cell)) {
      const cellValue = cell.getValue();
      const label = cellValue ? String(cellValue).trim() : '';
      setCodeEditorShapeLabel(label || String(codeEditorShapeId));
    } else {
      setCodeEditorShapeLabel(String(codeEditorShapeId));
    }
  }, [codeEditorShapeId]);

  const handleChangeShapeColor = useCallback((color) => {
    if (!selectedShape || !graphRef.current) return;
    const graph = graphRef.current;
    const model = graph.getModel();
    const cell = model.getCell(selectedShape);
    if (!cell || !model.isVertex(cell)) return;

    model.beginUpdate();
    try {
      let style = cell.getStyle() || '';
      // Mevcut fillColor'ı değiştir ya da yoksa ekle
      if (style.includes('fillColor=')) {
        style = style.replace(/fillColor=[^;]*/g, `fillColor=${color}`);
      } else {
        style = style.endsWith(';') ? style + `fillColor=${color};` : style + `;fillColor=${color};`;
      }
      model.setStyle(cell, style);
      graph.refresh(cell);
    } finally {
      model.endUpdate();
    }
    setSelectedShapeColor(color);
  }, [selectedShape]);

  const normalizeIncludeId = useCallback((value) => {
    if (value === null || value === undefined) {
      return '';
    }
    const str = String(value).trim();
    if (!str || str === 'null') {
      return '';
    }
    return str;
  }, []);

  const openIncludeDetails = useCallback((shapeId, options = {}) => {
    const { showPopup = false } = options;
    const currentWorkflowData = workflowDataRef.current || workflowData;
    const nodeData = currentWorkflowData?.contents?.find(item =>
      item && String(item.id) === String(shapeId)
    );
    const includeValue = normalizeIncludeId(
      shapeIncludePathsRef.current?.[shapeId] ??
      nodeData?.include ??
      nodeData?.diagrun_id ??
      nodeData?.fileId
    );
    const foldername =
      shapeIncludeFoldersRef.current?.[shapeId] ||
      nodeData?.foldername ||
      nodeData?.folderName ||
      '';
    const filename =
      nodeData?.filename ||
      nodeData?.fileName ||
      nodeData?.name ||
      '';
    if (includeValue) {
      handleFileClick({
        diagrun_id: includeValue,
        name: filename || `Include ${includeValue}`,
      });
    }
    if (showPopup) {
      setIncludeInfoData({
        shapeId,
        include: includeValue,
        foldername,
        filename,
      });
      setShowIncludeInfoModal(true);
    }
  }, [handleFileClick, normalizeIncludeId, workflowData]);
  

  // Move Request

  const handleMoveRequest = useCallback(async () => {
    const ticid = selectedTicketId || workflowData?.TICID;
    const flowid = workflowData?.workflow?.id || workflowData?.FLOWID || '';
    const sirano = workflowData?.sirano ?? 0;

    if (!ticid || !flowid) {
      showToast('error', 'TICID veya FLOWID bulunamadı!');
      return;
    }

    const eventData = {
      inputName: 'SELECT',
      message: '-requestmove yes',
      data: JSON.stringify({ ticid: String(ticid), flowid: String(flowid), sirano }),
    };

    const startTime = Date.now();
    setMoveRequestStatus({ state: 'pending', startTime });

    try {
      const result = await SendEvent(eventData, [], 'FlowChartDevelopmentScreen-MoveRequest');
      const endTime = Date.now();
      setMoveRequestStatus({
        state: 'success',
        startTime,
        endTime,
        durationMs: endTime - startTime,
        message: result?.sy_message || result?.result?.sy_message || 'Taşıma başarılı',
      });
      showToast('success', 'Move Request gönderildi.', {
        title: 'Move Request',
        request: eventData,
        response: result,
      });
    } catch (err) {
      const endTime = Date.now();
      setMoveRequestStatus({
        state: 'error',
        startTime,
        endTime,
        durationMs: endTime - startTime,
        message: err?.message || 'Bilinmeyen hata',
      });
      showToast('error', 'Move Request gönderilemedi.', {
        title: 'Move Request',
        request: eventData,
        response: { error: err?.message || 'Bilinmeyen hata' },
      });
    }
  }, [workflowData, selectedTicketId, SendEvent, showToast]);

  const handleCreateCmd = useCallback(async (cmdName, cmdType = 'cmd') => {
    const reqId = workflowData?.REQID || '';
    const message = `-${cmdName} yes ${cmdType}`;

    const eventData = {
      inputName: 'SELECT',
      message,
      data: reqId ? JSON.stringify({ REQID: String(reqId) }) : '',
    };
    console.log('%c[handleCreateCmd] eventData:', 'color:#fbbf24;font-weight:bold', JSON.stringify(eventData, null, 2)?.substring(0, 1000));
    const result = await SendEvent(eventData, [], 'FlowChartDevelopmentScreen-CreateCmd');
    console.log('%c[handleCreateCmd] result:', 'color:#fbbf24;font-weight:bold', JSON.stringify(result, null, 2)?.substring(0, 1000));
    return result;
  }, [workflowData, SendEvent]);

  // Fx Groups API çağrısı
  const fetchFxGroups = useCallback(async () => {
    setIsLoadingFxGroups(true);
    setFxGroupsError(null);
    setFxGroups([]);
    setSelectedFxGroup(null);
    setFunctions([]);
    setFunctionsError(null);
    setSelectedFunction(null);
    setShowFunctionsModal(false);
    try {
      const processId = process.env.REACT_APP_GET_FX_GROUP_LIST || '100023235';
      const bodyParams = {
        process: '100023235',
      };
      
      const response = await fetchWrapper(processId, bodyParams, navigate);
      
      // Response'u kontrol et
      if (response && response.result && response.result.contents) {
        setFxGroups(response.result.contents);
        setIsLoadingFxGroups(false);
        setShowFxGroupsModal(true);
      } else {
        throw new Error(response?.error || response?.message || 'Fx groups alınamadı');
      }
    } catch (error) {
      setFxGroupsError(error.message || 'Fx groups yüklenirken bir hata oluştu');
      setIsLoadingFxGroups(false);
    }
  }, [navigate, workflowData?.REQID]);

  // Functions API çağrısı (Fx Group seçildiğinde)
  const fetchFunctions = useCallback(async (fxGroupName) => {
    setIsLoadingFunctions(true);
    setFunctionsError(null);
    setFunctions([]);
    
    try {      
      // fxgroup: Son "_" karakterinden sonraki kısmı al
      // Örnek: "Function_Create_DR" -> "DR"
      const fxGroupShort = fxGroupName.includes('_') 
        ? fxGroupName.split('_').pop() 
        : fxGroupName;
      
      const processId = process.env.REACT_APP_GET_FX_LIST || '100023236';
      const bodyParams = {
        process: '100023236',
        fxgroup: fxGroupShort,
      };
      
      const response = await fetchWrapper(processId, bodyParams, navigate);
      
      // Response'u kontrol et
      if (response && response.result && response.result.contents) {
        setFunctions(response.result.contents);
        setSelectedFxGroup(fxGroupName);
        setIsLoadingFunctions(false);
        // Fx Groups modal'ını kapat ve Functions modal'ını aç
        setShowFxGroupsModal(false);
        setShowFunctionsModal(true);
      } else {
        throw new Error(response?.error || response?.message || 'Functions alınamadı - response.result.contents yok');
      }
    } catch (error) {
      setFunctionsError(error.message || 'Functions yüklenirken bir hata oluştu');
      setIsLoadingFunctions(false);
      // Hata durumunda Functions modal'ını aç ki kullanıcı hatayı görsün
      setShowFxGroupsModal(false);
      setShowFunctionsModal(true);
    }
  }, [navigate]);

  // Function Script gösterimi (Function seçildiğinde) - Draggable Window olarak aç
  const fetchFunctionScript = useCallback((func) => {
    if (!func || !func.fname) {
      return;
    }
    
    // Function objesinden script'i al
    const script = func.script || '';
    const fname = func.fname;
    
    // Aynı function zaten açık mı kontrol et
    const existingWindow = functionWindows.find(w => w.functionName === fname);
    if (existingWindow) {
      // Zaten açıksa, o window'u öne getir
      setFunctionWindows(prev => {
        const filtered = prev.filter(w => w.id !== existingWindow.id);
        return [...filtered, { ...existingWindow, isMinimized: false }];
      });
      setShowFunctionsModal(false);
      return;
    }
    
    // Maksimum 5 window kontrolü
    if (functionWindows.length >= 5) {
      alert('Maksimum 5 function window açabilirsiniz. Önce bir tanesini kapatın.');
      return;
    }
    
    // Yeni window oluştur
    const windowId = `func-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newWindow = {
      id: windowId,
      functionName: fname,
      script: script,
      position: { 
        x: 100 + (functionWindows.length * 30), 
        y: 100 + (functionWindows.length * 30) 
      },
      width: 800,
      height: 600,
      isMinimized: false,
      originalScript: script, // Save için original script'i sakla
      saveStatus: null, // { state: 'saving' | 'success' | 'error', message: string }
    };
    
    setFunctionWindows(prev => [...prev, newWindow]);
    setShowFunctionsModal(false);
  }, [functionWindows]);

  // Window kapatma
  const closeFunctionWindow = useCallback((windowId) => {
    setFunctionWindows(prev => prev.filter(w => w.id !== windowId));
    // Editor ref'i temizle
    if (functionWindowEditorRefs.current[windowId]) {
      delete functionWindowEditorRefs.current[windowId];
    }
  }, []);

  // Window minimize/maximize
  const toggleMinimizeWindow = useCallback((windowId) => {
    setFunctionWindows(prev => 
      prev.map(w => {
        if (w.id !== windowId) return w;
        
        if (w.isMinimized) {
          // Maximize: Eski boyutlara dön
          return { ...w, isMinimized: false };
        } else {
          // Minimize: Mevcut boyutları sakla ve çubuk haline getir
          return { 
            ...w, 
            isMinimized: true,
            // Boyutlar zaten w.width ve w.height'ta saklı
          };
        }
      })
    );
  }, []);

  // Window save
  const saveFunctionWindow = useCallback(async (windowId) => {
    const window = functionWindows.find(w => w.id === windowId);
    if (!window) return;
    
    const editorRef = functionWindowEditorRefs.current[windowId];
    if (!editorRef || !editorRef.current) {
      alert('Editor bulunamadı');
      return;
    }
    
    const currentScript = editorRef.current.getValue();

    setFunctionWindows(prev =>
      prev.map(w =>
        w.id === windowId
          ? { ...w, saveStatus: { state: 'saving', message: 'Kaydediliyor...' } }
          : w
      )
    );

    const processId = process.env.REACT_APP_DIAGRUN_FX_UPDATE;
    if (!processId) {
      alert('REACT_APP_DIAGRUN_FX_UPDATE tanımlı değil');
      return;
    }

    const bodyParams = {
      TICID: selectedTicketId || workflowData?.TICID || null,
      fxname: window.functionName,
      script: currentScript,
      space: 'jubi',
      REQID: workflowData?.REQID || null,
      process: processId,
    };

    try {
      const response = await fetchWrapper(processId, bodyParams, navigate);

      const isSuccess =
        response?.result === true ||
        response?.success === true ||
        response?.result?.sy_subrc === 0;

      if (isSuccess) {
        // Original script'i güncelle
        setFunctionWindows(prev =>
          prev.map(w =>
            w.id === windowId
              ? {
                  ...w,
                  originalScript: currentScript,
                  saveStatus: { state: 'success', message: response?.result?.sy_message || 'Kaydedildi' }
                }
              : w
          )
        );
        setTimeout(() => {
          setFunctionWindows(prev =>
            prev.map(w =>
              w.id === windowId ? { ...w, saveStatus: null } : w
            )
          );
        }, 3000);
        return;
      }

      throw new Error(
        response?.result?.sy_message ||
        response?.error ||
        response?.message ||
        'Kaydetme başarısız'
      );
    } catch (error) {
      setFunctionWindows(prev =>
        prev.map(w =>
          w.id === windowId
            ? { ...w, saveStatus: { state: 'error', message: error.message || 'Kaydetme hatası' } }
            : w
        )
      );
    }
  }, [functionWindows, navigate, selectedTicketId, workflowData]);

  const handleSave = useCallback(async (options = {}) => {
    const { showToastFeedback = true, force = false, source = 'manual' } = options;
    if (!graphRef.current) return;

    if (!force) {
      const preview = buildSavePreview();
      setPendingSavePreview(preview);
      setShowSaveConfirmModal(true);
      pushDebugLog('save_preview_opened', {
        source,
        queuedScriptsCount: preview.queuedScripts.length,
        debugTailCount: preview.debugTail.length,
      });
      return;
    }
    
    setIsSaving(true);
    setSaveStatus(null);
    pushDebugLog('save_start', {
      source,
      showToastFeedback,
      codeLength: typeof code === 'string' ? code.length : 0,
      editorHasValue: !!editorRef.current,
    });
    if (selectedShape && codeEditorShapeId && String(selectedShape) !== String(codeEditorShapeId)) {
      pushDebugLog('anomaly_save_with_design_code_shape_mismatch', {
        selectedShapeNow: String(selectedShape),
        codeEditorShapeNow: String(codeEditorShapeId),
      });
    }

    // Kaydetmeden önce editördeki en güncel kodu cache'e yaz
    // ÖNEMLİ: code editor'da gösterilen shape (codeEditorShapeId) için; selectedShape (design) DEĞİL
    if (codeEditorShapeId && activeTabId) {
      if (!tabCodeCacheRef.current[activeTabId]) {
        tabCodeCacheRef.current[activeTabId] = {};
      }
      if (editorRef.current) {
        try {
          const currentVal = editorRef.current.getValue();
          if (currentVal !== undefined && currentVal !== null && currentVal !== '') {
            writeTabCache(activeTabId, codeEditorShapeId, String(currentVal), 'handleSave_first_snapshot');
            shapeCodeCacheRef.current[codeEditorShapeId] = String(currentVal);
          }
        } catch (_) { /* editor erişim hatası */ }
      }
    }

    // Kaydetmeden önce aktif tab'ın graph snapshot'ını diğer tab'lara da yansıt
    if (activeTabId && graphRef.current) {
      const _graph = graphRef.current;
      const _model = _graph.getModel();
      const _cells = _graph.getChildCells(_graph.getDefaultParent());
      if (_cells && _cells.length > 0) {
        const snap = _cells.map((c) => {
          const iv = _model.isVertex(c);
          const g = c.getGeometry();
          const r = {
            id: c.getId() ? String(c.getId()) : '',
            vertex: iv ? '1' : '0',
            edge: !iv ? '1' : '0',
            definition: _model.getValue(c) ? String(_model.getValue(c)) : '',
            GeometryX: g.x, GeometryY: g.y, GeometryWidth: g.width, GeometryHeight: g.height,
            source: !iv && c.getTerminal(true) ? String(c.getTerminal(true).getId()) : null,
            target: !iv && c.getTerminal(false) ? String(c.getTerminal(false).getId()) : null,
            style: c.getStyle() ? String(c.getStyle()) : '',
          };
          if (!iv) {
            const lbl = r.definition;
            if (lbl && lbl.includes(':Next')) { const km = lbl.match(/(\d+):Next/); if (km) r.kosul = km[1]; }
            if (c.getGeometry().points) {
              try { r.GeometryPoints = JSON.stringify(c.getGeometry().points.map((p) => ({ x: p.x, y: p.y }))); } catch (_) { /* */ }
            }
          }
          return r;
        });
        setTabs((prev) =>
          prev.map((t) => {
            if (t.id !== activeTabId) return t;
            const pWd = t.workflowData || workflowDataRef.current || workflowData;
            if (!pWd) return t;
            const oc = pWd.contents || [];
            const merged = snap.map((s) => {
              const o = oc.find((x) => String(x.id) === String(s.id));
              return o ? { ...o, ...s, script: o.script, ai_prompt: o.ai_prompt, include: o.include } : s;
            });
            return { ...t, workflowData: { ...pWd, contents: merged } };
          }),
        );
      }
    }
    
    try {
      const graph = graphRef.current;
      const model = graph.getModel();
      const cells = graph.getChildCells(graph.getDefaultParent());
      
      const contents = cells.map(cell => {
        const isVertex = model.isVertex(cell);
        const geometry = cell.getGeometry();
        const cellId = cell.getId() ? String(cell.getId()) : '';
        // Model'den güncel value'yu al - label değişikliklerini yakalamak için
        const cellValue = model.getValue(cell);
        const cellDefinition = cellValue ? String(cellValue) : '';
        const cellStyle = cell.getStyle() ? String(cell.getStyle()) : '';
        
        const result = {
          id: cellId,
          vertex: isVertex ? "1" : "0",
          edge: !isVertex ? "1" : "0",
          definition: cellDefinition,
          GeometryX: geometry.x,
          GeometryY: geometry.y,
          GeometryWidth: geometry.width,
          GeometryHeight: geometry.height,
          source: !isVertex && cell.getTerminal(true) ? String(cell.getTerminal(true).getId()) : null,
          target: !isVertex && cell.getTerminal(false) ? String(cell.getTerminal(false).getId()) : null,
          style: cellStyle
        };
        
        // Edge için kosul değerini label'dan çıkar
        if (!isVertex) {
          const label = cellDefinition;
          if (label && label.includes(':Next')) {
            const kosulMatch = label.match(/(\d+):Next/);
            if (kosulMatch) {
              result.kosul = kosulMatch[1];
              // Server'a definition "Next" olarak gönder
              result.definition = 'Next';
            }
          }
        }
        
        if (!isVertex && cell.getGeometry().points) {
          try {
            const points = cell.getGeometry().points.map(p => ({ x: p.x, y: p.y }));
            result.GeometryPoints = JSON.stringify(points);
          } catch (err) {
            result.GeometryPoints = null;
          }
        }
        
        return result;
      });

      // ✅ EN GÜNCEL workflowData'yı kullan (workflowDataRef her zaman en güncel)
      const currentWorkflowData = workflowDataRef.current || workflowData;
      const originalObjects = currentWorkflowData?.contents || [];
      
      contents.forEach((newObj) => {
        const originalObj = originalObjects.find(obj => String(obj.id) === String(newObj.id));
        if (originalObj) {
          const requiredFields = ['kosul', 'filename', 'parent', 'include', 'script', 'ai_prompt', 'GeometryRelative', 'GeometryAs', 'LOEKZ', 'LODAT', 'sourceHandle', 'targetHandle'];
          requiredFields.forEach(field => {
            if (!(field in newObj)) {
              // ✅ ai_prompt için ekstra kontrol: workflowDataRef'ten EN GÜNCEL değeri al
              let originalValue = originalObj[field];
              
              // ✅ ai_prompt için double-check: updateShapeAIPrompt ile güncellenmiş olabilir
              if (field === 'ai_prompt') {
                const freshData = workflowDataRef.current;
                if (freshData && Array.isArray(freshData.contents)) {
                  const freshObj = freshData.contents.find(obj => String(obj.id) === String(newObj.id));
                  if (freshObj && freshObj.ai_prompt !== undefined) {
                    originalValue = freshObj.ai_prompt; // ✅ EN GÜNCEL prompt
                  }
                }
              }
              
              // ✅ script için: TÜM shape'ler için cache'den en güncel kodu al
              if (field === 'script') {
                const shapeId = String(newObj.id);
                // Önce aktif tab cache, en son editor (code editor'daki shape ise)
                const fromTabCache = tabCodeCacheRef.current?.[activeTabId]?.[shapeId];
                // ÖNEMLİ: code sadece codeEditorShapeId'ye ait — selectedShape (design) değil
                const fromEditor = shapeId === String(codeEditorShapeId) ? code : undefined;
                const cachedScript = fromEditor !== undefined && fromEditor !== '' ? fromEditor
                  : fromTabCache !== undefined ? fromTabCache
                  : undefined;
                if (cachedScript !== undefined && cachedScript !== null) {
                  originalValue = String(cachedScript);
                }
              }
              
              // "null" string'ini de null olarak kabul et
              if (originalValue !== undefined && originalValue !== null && originalValue !== 'null') {
                // null, undefined veya "null" string değilse, değeri set et
                if (typeof originalValue === 'object') {
                  newObj[field] = JSON.stringify(originalValue);
                } else {
                  newObj[field] = String(originalValue);
                }
              } else {
                // undefined, null veya "null" string ise, default değerleri set et
                if (field === 'parent') {
                  newObj[field] = currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : '100022941';
                } else if (field === 'script') {
                  newObj[field] = ''; // script boş string olmalı
                } else if (field === 'LODAT') {
                  newObj[field] = null; // LODAT null olabilir
                } else if (field === 'GeometryRelative') {
                  newObj[field] = '0';
                } else if (field === 'GeometryAs') {
                  newObj[field] = 'geometry';
                } else {
                  newObj[field] = '';
                }
              }
            }
          });
        } else {
          // Yeni oluşturulmuş shape (originalObj yok)
          newObj.kosul = newObj.kosul || '';
          newObj.filename = '';
          newObj.parent = currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : '100022941';
          newObj.include = '';
          
          // ✅ Yeni shape için de EN GÜNCEL değerleri kontrol et
          // (Kullanıcı shape'i oluşturduktan hemen sonra prompt girebilir)
          const freshData = workflowDataRef.current;
          if (freshData && Array.isArray(freshData.contents)) {
            const freshObj = freshData.contents.find(obj => String(obj.id) === String(newObj.id));
            if (freshObj) {
              newObj.script = freshObj.script || '';
              newObj.ai_prompt = freshObj.ai_prompt || '';
            } else {
              newObj.script = '';
              newObj.ai_prompt = '';
            }
          } else {
            newObj.script = '';
            newObj.ai_prompt = '';
          }
          newObj.GeometryRelative = '0';
          newObj.GeometryAs = 'geometry';
          newObj.LOEKZ = '';
          newObj.LODAT = null;
          newObj.sourceHandle = '';
          newObj.targetHandle = '';
        }
        
        // "null" string değerlerini temizle (eğer varsa)
        if (newObj.script === 'null') {
          newObj.script = '';
        }
        if (newObj.ai_prompt === 'null') {
          newObj.ai_prompt = '';
        }
        if (newObj.LODAT === 'null') {
          newObj.LODAT = null;
        }
      });
      
      // Son kontrol: promptCacheRef yetkin kaynaktır — tüm panellerin promptları buradadır.
      // Önce workflowDataRef'ten fallback oku, sonra cache ile üzerine yaz.
      const freshRef = workflowDataRef.current;
      contents.forEach((obj) => {
        const id = String(obj.id);
        // 1) workflowDataRef'ten fallback
        if (freshRef && Array.isArray(freshRef.contents)) {
          const src = freshRef.contents.find((c) => c && String(c.id) === id);
          if (src && src.ai_prompt !== undefined && src.ai_prompt !== null && src.ai_prompt !== 'null') {
            obj.ai_prompt = String(src.ai_prompt);
          }
        }
        // 2) promptCacheRef her zaman son sözü söyler
        const cached = promptCacheRef.current[id];
        if (cached !== undefined) {
          obj.ai_prompt = String(cached);
        }
      });

      const finalData = {
        process: currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : "100022941",
        ...(selectedTicketId && { TICID: selectedTicketId }),
        filename: currentWorkflowData?.workflow?.filename ? String(currentWorkflowData.workflow.filename) : "sstforyusuf.php",
        desc: currentWorkflowData?.workflow?.desc ? String(currentWorkflowData.workflow.desc) : "saestforyusuf.php",
        longdesc: currentWorkflowData?.workflow?.longdesc ? String(currentWorkflowData.workflow.longdesc) : "testforyusuf.php",
        cat_id: currentWorkflowData?.workflow?.cat_id ? String(currentWorkflowData.workflow.cat_id) : "90008417",
        diagrun: currentWorkflowData?.workflow?.id ? String(currentWorkflowData.workflow.id) : "100022941",
        objects: contents
      };
      
      /* ═══════════════════════════════════════════════════════
         1) Aktif tab'ın workflow'u (graph'tan okunan contents)
         ═══════════════════════════════════════════════════════ */
      const promises = [];
      promises.push(
        saveWorkflow(finalData, selectedTicketId)
          .then((r) => ({ tab: 'active', type: 'workflow', result: r })),
      );

      // Aktif tab'ın hash'ini kaydet (save başarılı olursa)
      const activeContentsHash = hashContents(contents);

      /* ═══════════════════════════════════════════════════════
         2) Diğer tab'lar: tab geçişinde otomatik kaydedilir,
            burada tekrar kaydedilmez.
         ═══════════════════════════════════════════════════════ */

      /* ═══════════════════════════════════════════════════════
         3) Sadece DEĞİŞEN script'ler (dirty detection)
         ═══════════════════════════════════════════════════════ */
      const savedScriptIds = new Set();
      const shapeFirstSeenTabRef = {};

      // SOURCE OF TRUTH: sadece editorRef.getValue() kullanılır.
      // Eskiden ikinci kez `code` state'inden snapshot yazıyorduk; RAF ile queue edilmiş
      // handleCodeChange -> setCode("yusufcan...") fire olunca `code` state stale hale geliyordu
      // ve cache[tab-B][shapeB] = tab-A'nın içeriği şeklinde cross-file pollution üretiyordu.
      // Bu nedenle bu blok tamamen kaldırıldı.

      // Tüm tab cache'lerinden script'leri topla — sadece değişenleri kaydet
      for (const [tabId, cache] of Object.entries(tabCodeCacheRef.current)) {
        if (!cache || typeof cache !== 'object') continue;
        const originals = originalScriptsRef.current[tabId] || {};
        for (const [shapeId, scriptVal] of Object.entries(cache)) {
          // CROSS-FILE GUARD: shape bu tab'ın workflow'una ait değilse cache'i temizle ve atla.
          if (!isShapeInTabWorkflow(shapeId, tabId)) {
            pushDebugLog('anomaly_foreign_shape_in_tab_cache_rejected', {
              tabId: String(tabId),
              shapeId: String(shapeId),
              scriptLength: (scriptVal == null ? 0 : String(scriptVal).length),
            });
            try { delete cache[shapeId]; } catch (_e) { /* ignore */ }
            continue;
          }
          if (savedScriptIds.has(shapeId)) {
            pushDebugLog('anomaly_duplicate_shape_id_across_tabs', {
              shapeId: String(shapeId),
              firstSeenTabId: shapeFirstSeenTabRef[shapeId] || null,
              duplicateTabId: String(tabId),
            });
            continue;
          }
          const currentScript = scriptVal != null ? String(scriptVal) : '';
          const originalScript = originals[shapeId] !== undefined ? String(originals[shapeId]) : '';
          if (currentScript === originalScript) continue;
          shapeFirstSeenTabRef[shapeId] = String(tabId);
          pushDebugLog('save_queue_script', {
            tabId: String(tabId),
            shapeId: String(shapeId),
            scriptLength: currentScript.length,
            originalLength: originalScript.length,
            scriptPreview: toScriptPreview(currentScript),
            changed: true,
          });
          savedScriptIds.add(shapeId);
          // Server save + başarılıysa originals'i bu tab için güncelle.
          if (!originalScriptsRef.current[tabId]) originalScriptsRef.current[tabId] = {};
          const originalsForTab = originalScriptsRef.current[tabId];
          promises.push(
            saveScript(shapeId, currentScript, selectedTicketId)
              .then((r) => {
                if (r && r.success) {
                  originalsForTab[shapeId] = currentScript;
                }
                return { tab: shapeId, type: 'script', result: r };
              }),
          );
        }
      }
      // ÖNEMLİ: shapeCodeCacheRef global olduğu için (shapeId çakışabilir),
      // cross-file overwrite riskini engellemek amacıyla save kaynağı olarak kullanılmıyor.

      /* ═══════════════════════════════════════════════════════
         4) Sonuçları değerlendir ve log kaydet
         ═══════════════════════════════════════════════════════ */
      const results = await Promise.allSettled(promises);

      let successCount = 0;
      let failCount = 0;
      const logEntries = [];

      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') {
          const { tab, type, result: saveResult } = res.value;
          const entry = {
            type,
            name: tab,
            success: !!saveResult.success,
            error: saveResult.success ? null : (saveResult.error || 'Hata'),
          };
          if (saveResult.success) successCount++;
          else failCount++;
          logEntries.push(entry);
        } else {
          failCount++;
          logEntries.push({
            type: 'unknown',
            name: `promise-${idx}`,
            success: false,
            error: res.reason?.message || 'Bilinmeyen hata',
          });
        }
      });

      const wfCount = promises.filter(p => p).length - savedScriptIds.size;
      const scrCount = savedScriptIds.size;
      const totalOps = promises.length;

      setSaveLogEntries(logEntries);

      const saveDetails = { title: 'Save', logEntries };

      if (totalOps === 0) {
        setSaveStatus({ type: 'success', message: 'Değişiklik yok — her şey güncel.' });
        if (showToastFeedback) showToast('success', 'Değişiklik yok — her şey güncel.');
      } else if (failCount === 0) {
        if (activeTabId) savedWorkflowHashRef.current[activeTabId] = activeContentsHash;
        const msg = `Kaydedildi! (${Math.max(wfCount, 0)} flow, ${scrCount} script)`;
        setSaveStatus({ type: 'success', message: msg });
        if (showToastFeedback) showToast('success', msg, saveDetails);
      } else if (successCount > 0) {
        const msg = `Kısmen kaydedildi — ${failCount} hata`;
        setSaveStatus({ type: 'error', message: msg });
        if (showToastFeedback) showToast('error', msg, saveDetails);
      } else {
        setSaveStatus({ type: 'error', message: 'Kaydetme başarısız!' });
        if (showToastFeedback) showToast('error', 'Kaydetme başarısız!', saveDetails);
      }
      pushDebugLog('save_end', {
        successCount,
        failCount,
        totalOps,
        workflowOps: Math.max(wfCount, 0),
        scriptOps: scrCount,
      });
    } catch (error) {
      const entries = [{ type: 'fatal', name: 'handleSave', success: false, error: error.message }];
      setSaveLogEntries(entries);
      setSaveStatus({ type: 'error', message: error.message || 'Kaydetme başarısız!' });
      if (showToastFeedback) {
        showToast('error', error.message || 'Kaydetme başarısız!', { title: 'Save', logEntries: entries });
      }
      pushDebugLog('anomaly_save_exception', {
        message: error?.message || 'unknown',
      });
    } finally {
      setIsSaving(false);
    }
  }, [saveWorkflow, saveScript, workflowData, codeEditorShapeId, code, selectedTicketId, tabs, activeTabId, setTabs, pushDebugLog, selectedShape, buildSavePreview, toScriptPreview, isShapeInTabWorkflow, writeTabCache]);

  useEffect(() => { handleSaveRef.current = handleSave; }, [handleSave]);

  const handleZoomIn = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = Math.min(currentScale * 1.1, 4);
    const steps = 10;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      }
    };
    animateZoom();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = Math.max(currentScale * 0.9, 0.1);
    const steps = 10;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      }
    };
    animateZoom();
  }, []);

  const handleZoomReset = useCallback(() => {
    if (!graphRef.current) return;
    const graph = graphRef.current;
    const currentScale = graph.view.scale || 1;
    const targetScale = 1;
    if (Math.abs(currentScale - targetScale) < 0.01) return;
    const steps = 12;
    const stepSize = (targetScale - currentScale) / steps;
    let currentStep = 0;
    const animateZoom = () => {
      if (currentStep < steps) {
        const newScale = currentScale + stepSize * (currentStep + 1);
        graph.view.setScale(newScale);
        graph.view.validate();
        currentStep++;
        requestAnimationFrame(animateZoom);
      } else {
        // Sadece scale sıfırla — translate'e dokunma, aksi hâlde şekiller kaybolur
        graph.view.setScale(targetScale);
        graph.view.validate();
      }
    };
    animateZoom();
  }, []);

  // ========== GENERATE UUID FUNCTION ==========
  const generateUUID = useCallback(() => {
    // 0009-095a-443b-985d-2c85 formatında UUID oluştur (5 grup, her biri 4 karakter hex)
    const generateHex = (length) => {
      return Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    };
    return `${generateHex(4)}-${generateHex(4)}-${generateHex(4)}-${generateHex(4)}-${generateHex(4)}`;
  }, []);

  // ========== ADD SHAPE FUNCTION ==========
  const addShape = useCallback((shapeName, label) => {
    if (!graphRef.current || !isDesignMode) {
      return;
    }
    
    const graph = graphRef.current;
    const parent = graph.getDefaultParent();
    const gridSize = graph.gridSize || 20;
    
    // Son eklenen şeklin yakınında (sağ-altında) yerleştir
    // Eğer ilk şekilse viewport ortasında çıksın
    let x, y;
    if (lastAddedShapePositionRef.current) {
      // Son eklenen şeklin grid-snap'li koordinatına yakın, biraz sağ-alta kaydır
      x = Math.round((lastAddedShapePositionRef.current.x + 120) / gridSize) * gridSize;
      y = Math.round((lastAddedShapePositionRef.current.y + 20) / gridSize) * gridSize;
    } else {
      const bounds = graph.getGraphBounds();
      const view = graph.view;
      const translate = view.translate || { x: 0, y: 0 };
      const scale = view.scale || 1;
      // Görünür alanın merkezini hesapla
      const containerWidth = graph.container ? graph.container.clientWidth : 800;
      const containerHeight = graph.container ? graph.container.clientHeight : 600;
      const cx = (containerWidth / 2 - translate.x * scale) / scale;
      const cy = (containerHeight / 2 - translate.y * scale) / scale;
      x = Math.round(cx / gridSize) * gridSize;
      y = Math.round(cy / gridSize) * gridSize;
    }
    
    const shape = shapes.find(s => s.name === shapeName);
    if (!shape) return;
    
    // mxGraph'in built-in shape'lerini kullan
    const mxShape = shape.mxShape || 'rectangle';
    
    // Şekil boyutları — daire (40×40) referans, diğerleri oransal
    let w, h;
    switch (shapeName) {
      case 'startCircle':
      case 'stopCircle':
        w = 40; h = 40; break;
      case 'rectangle':
        w = 90; h = 40; break;
      case 'preProcess':
        w = 90; h = 40; break;
      case 'rhombus':
        w = 60; h = 50; break;
      case 'triangle':
        w = 50; h = 45; break;
      case 'hexagon':
        w = 70; h = 40; break;
      case 'cylinder':
        w = 45; h = 50; break;
      default:
        w = 80; h = 40; break;
    }
    
    // Şekil için renk - shape'den al, yoksa shapeColor kullan
    const fillColor = shape.color || shapeColor;
    
    // Style string — overflow=hidden ile label şeklin içine sıkıştırılır
    const style = `shape=${mxShape};fillColor=${fillColor};strokeColor=#000000;fontSize=10;fontColor=#000000;whiteSpace=wrap;html=1;overflow=hidden;connectable=1;selectable=1;movable=1;resizable=1;`;
    
    const id = generateUUID();
    
    try {
      graph.getModel().beginUpdate();
      const vertex = graph.insertVertex(parent, id, defaultText || label, x, y, w, h, style);
      
      graph.refresh(vertex);
      graph.view.validate();
      graph.setSelectionCell(vertex);
      // Son eklenen şeklin pozisyonunu kaydet
      lastAddedShapePositionRef.current = { x, y };
    } catch (error) {
      console.error('Error adding shape:', error);
    } finally {
      graph.getModel().endUpdate();
    }
  }, [isDesignMode, shapeColor, defaultText, shapes, generateUUID]);

  /* ── Graph'ın mevcut durumunu alıp önceki tab'ın workflowData.contents'ine yazan yardımcı ── */
  const snapshotGraphToTab = useCallback((tabId) => {
    if (!graphRef.current || !tabId) return;
    const graph = graphRef.current;
    const model = graph.getModel();
    const cells = graph.getChildCells(graph.getDefaultParent());
    if (!cells || cells.length === 0) return;

    const snappedContents = cells.map((cell) => {
      const isVertex = model.isVertex(cell);
      const geometry = cell.getGeometry();
      const cellId = cell.getId() ? String(cell.getId()) : '';
      const cellValue = model.getValue(cell);
      const cellDefinition = cellValue ? String(cellValue) : '';
      const cellStyle = cell.getStyle() ? String(cell.getStyle()) : '';

      const result = {
        id: cellId,
        vertex: isVertex ? '1' : '0',
        edge: !isVertex ? '1' : '0',
        definition: cellDefinition,
        GeometryX: geometry.x,
        GeometryY: geometry.y,
        GeometryWidth: geometry.width,
        GeometryHeight: geometry.height,
        source: !isVertex && cell.getTerminal(true) ? String(cell.getTerminal(true).getId()) : null,
        target: !isVertex && cell.getTerminal(false) ? String(cell.getTerminal(false).getId()) : null,
        style: cellStyle,
      };

      if (!isVertex) {
        const label = cellDefinition;
        if (label && label.includes(':Next')) {
          const kosulMatch = label.match(/(\d+):Next/);
          if (kosulMatch) result.kosul = kosulMatch[1];
        }
        if (cell.getGeometry().points) {
          try {
            result.GeometryPoints = JSON.stringify(cell.getGeometry().points.map((p) => ({ x: p.x, y: p.y })));
          } catch (_) { /* ignore */ }
        }
      }
      return result;
    });

    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId) return tab;
        const prevWd = tab.workflowData || workflowDataRef.current || workflowData;
        if (!prevWd) return tab;
        const originalContents = prevWd.contents || [];
        const merged = snappedContents.map((snap) => {
          const orig = originalContents.find((o) => String(o.id) === String(snap.id));
          if (orig) {
            return { ...orig, ...snap, script: orig.script, ai_prompt: orig.ai_prompt, include: orig.include };
          }
          return snap;
        });
        return { ...tab, workflowData: { ...prevWd, contents: merged } };
      }),
    );
  }, [workflowData, setTabs]);

  const prevActiveTabIdForSnapshot = useRef(activeTabId);

  useEffect(() => {
    const prevTab = prevActiveTabIdForSnapshot.current;
    if (prevTab && prevTab !== activeTabId) {
      snapshotGraphToTab(prevTab);
    }
    prevActiveTabIdForSnapshot.current = activeTabId;
  }, [activeTabId, snapshotGraphToTab]);

  // ========== GRAPH INITIALIZATION ==========
  useEffect(() => {
    // Aktif tab'ın workflowData'sını kullan — tabs ref'ten okunur (dependency yok)
    const activeTab = tabsRef.current.find(tab => tab.id === activeTabId);
    const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
    const currentWorkflowId = currentWorkflowData?.workflow?.id || null;
    const workflowIdChanged = workflowIdRef.current !== currentWorkflowId;
    const isInitialLoad = workflowIdRef.current === null;
    
    // workflowIdRef'i güncelle
    if (currentWorkflowId) {
      workflowIdRef.current = currentWorkflowId;
    }
    
    const container = graphContainerRef.current;
    if (!container) {
      return;
    }
    if (!mxClient.isBrowserSupported()) {
      alert('mxGraph bu tarayıcıda desteklenmiyor!');
      return;
    }
    
    // Eğer workflow ID değişmediyse ve graph zaten varsa, graph'ı yeniden oluşturma
    // SADECE activeTabId gerçekten değiştiyse (tab geçişi) yeniden render et
    // tabs içeriği değiştiğinde (save, include güncelleme vb.) yeniden render ETME
    if (!isInitialLoad && !workflowIdChanged && graphRef.current) {
      const activeTabActuallyChanged = graphInitActiveTabRef.current !== activeTabId;
      if (activeTabActuallyChanged && activeTabId) {
        // Tab geçişi: graph'i temizle ve yeniden render et
        const graph = graphRef.current;
        graph.getModel().clear();
        workflowIdRef.current = null; // Force re-render
        // graphInitActiveTabRef aşağıda güncelleniyor
      } else {
        // tabs içeriği değişti (save, include vb.) ya da sadece isDesignMode değişti
        // Graph'i yeniden init'leme, viewport korunuyor
        if (graphRef.current._applyDesignModeSettings) {
          graphRef.current._applyDesignModeSettings(isDesignMode);
        }
        // Cleanup önceki effect'te rubberbandRef'i destroy etti — burada yeniden kur
        if (!rubberbandRef.current && graphRef.current) {
          const rb3 = new mxRubberband(graphRef.current);
          rb3.setEnabled(true);
          rubberbandRef.current = rb3;
        }
        return;
      }
    }
    // Aktif tab'ı güncelle (bir sonraki effect çalışmasında karşılaştırma için)
    graphInitActiveTabRef.current = activeTabId;
    
    // Container CSS — 20×20 SVG grid (mxGraph gridSize=20 ile tam eşleşir)
    const GRID_SIZE = 20;
    const gridSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${GRID_SIZE}' height='${GRID_SIZE}'%3E%3Crect width='${GRID_SIZE}' height='${GRID_SIZE}' fill='%23fafafa'/%3E%3Cpath d='M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}' fill='none' stroke='%23d0d0d0' stroke-width='0.5'/%3E%3C/svg%3E")`;
    container.style.background = `${gridSvg} repeat`;
    container.style.backgroundColor = '#fafafa';
    container.style.position = 'relative';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.overflow = 'hidden';
    container.style.outline = 'none';
    container.style.cursor = isDesignMode ? 'default' : 'grab';
    container.style.pointerEvents = 'auto';
    container.style.zIndex = '1';
    
    let graph = null;
    
    // mxGraph'in built-in shape'lerini kullandığımız için image preload'a gerek yok
    (async () => {
      if (graphRef.current) {
        try {
          graphRef.current.destroy();
        } catch (error) {
          // ignore
        }
      }
      
      graph = new mxGraph(container);
      graphRef.current = graph;
      
      // Register custom shapes
      if (mxCellRenderer) {
        mxCellRenderer.registerShape('triangle', mxTriangleShape);
        mxCellRenderer.registerShape('predefinedProcess', mxPredefinedProcessShape);
      }
      
      // Basic graph settings
      graph.setEnabled(true);
      graph.setGridEnabled(true);
      graph.setGridSize(20);
      graph.view.gridColor = '#e0e0e0';
      graph.view.gridImage = null;
      graph.setTooltips(false);
      graph.setAllowDanglingEdges(false);
      graph.setDisconnectOnMove(false);
      graph.setConnectable(true);
      graph.setCellsEditable(true);
      graph.setCellsResizable(true);
      graph.setDropEnabled(true);
      graph.setMultigraph(false);
      
      // Container style
      graph.container.style.width = '100%';
      graph.container.style.height = '100%';
      graph.container.style.position = 'relative';
      graph.container.style.overflow = 'hidden';
      graph.container.style.pointerEvents = 'auto';
      graph.container.style.zIndex = '1';
      
      // Pointer events
      setTimeout(() => {
        if (graph.view) {
          if (graph.view.canvas) {
            graph.view.canvas.style.pointerEvents = 'auto';
          }
          if (graph.view.backgroundPane) {
            graph.view.backgroundPane.style.pointerEvents = 'none';
          }
          if (graph.view.drawPane) {
            graph.view.drawPane.style.pointerEvents = 'auto';
          }
          if (graph.view.overlayPane) {
            graph.view.overlayPane.style.pointerEvents = 'auto';
          }
          if (graph.container) {
            graph.container.style.pointerEvents = 'auto';
            graph.container.style.position = 'relative';
            graph.container.style.width = '100%';
            graph.container.style.height = '100%';
          }
        }
      }, 50);
      
      // Apply design mode settings
      const applyDesignModeSettings = (designMode) => {
        graph.setEnabled(true);
        
        graph.setCellsMovable(designMode);
        graph.setConnectable(designMode);
        graph.setCellsEditable(designMode);
        graph.setCellsResizable(designMode);
        graph.setCellsBendable(designMode);
        graph.setCellsDisconnectable(designMode);
        graph.setAllowDanglingEdges(designMode);
        
        graph.setPanning(true);
        // Sol tık rubber-band seçimi için KAPALI (Space basılıyken açılır)
        graph.panningHandler.useLeftButtonForPanning = false;
        graph.panningHandler.useMiddleButtonForPanning = true; // Orta tuş her zaman pan
        graph.panningHandler.usePopupTrigger = false;
        graph.panningHandler.ignoreCell = false;
        
        // Pan cursor'ı için event listener'lar
        const originalMouseDown = graph.panningHandler.mouseDown;
        const originalMouseUp = graph.panningHandler.mouseUp;
        
        // Space tuşu durumunu kontrol etmek için container'a data attribute ekle
        const updatePanCursor = () => {
          if (!graph.container) return;
          const isSpaceActive = graph.container.dataset.spacePan === 'true';
          if (isSpaceActive) {
            graph.container.style.cursor = 'grab';
            if (graph.view && graph.view.canvas) {
              graph.view.canvas.style.cursor = 'grab';
            }
          }
        };
        
        graph.panningHandler.mouseDown = function(sender, me) {
          const isSpaceActive = graph.container && graph.container.dataset.spacePan === 'true';
          const isMiddleButton = me.getEvent().button === 1;

          // Orta tuş: her zaman pan
          if (isMiddleButton) {
            const result = originalMouseDown.call(this, sender, me);
            if (graph.container) {
              graph.container.style.cursor = 'grabbing';
              if (graph.view?.canvas) graph.view.canvas.style.cursor = 'grabbing';
            }
            return result;
          }

          // Space basılı + sol tık: pan
          if (isSpaceActive) {
            const result = originalMouseDown.call(this, sender, me);
            if (graph.container) {
              graph.container.style.cursor = 'grabbing';
              if (graph.view?.canvas) graph.view.canvas.style.cursor = 'grabbing';
            }
            return result;
          }

          // Sol tık, Space yok: panning handler devreye girmesin
          // → rubber-band veya shape drag otomatik çalışır
          return;
        };
        
        graph.panningHandler.mouseUp = function(sender, me) {
          const result = originalMouseUp.call(this, sender, me);
          // Pan bittiğinde cursor'ı güncelle
          updatePanCursor();
          return result;
        };
        
        // Orta mouse tuşu için cursor güncellemesi
        if (graph.container) {
          const handleMiddleMouseDown = (e) => {
            if (e.button === 1) { // Orta mouse tuşu
              graph.container.style.cursor = 'grabbing';
              if (graph.view && graph.view.canvas) {
                graph.view.canvas.style.cursor = 'grabbing';
              }
            }
          };
          
          const handleMiddleMouseUp = (e) => {
            if (e.button === 1) { // Orta mouse tuşu
              updatePanCursor();
            }
          };
          
          graph.container.addEventListener('mousedown', handleMiddleMouseDown);
          graph.container.addEventListener('mouseup', handleMiddleMouseUp);

          // Graph canvas'a tıklanınca Monaco editörünün focus'unu kaldır
          // Böylece ok tuşları editörde değil graph'ta çalışır
          const handleGraphBlurEditor = () => {
            try {
              if (editorRef.current) {
                editorRef.current.blur();
              }
            } catch (_) {}
          };
          graph.container.addEventListener('mousedown', handleGraphBlurEditor);
          
          // Cleanup için referansları sakla
          graph._middleMouseHandlers = {
            down: handleMiddleMouseDown,
            up: handleMiddleMouseUp
          };
          graph._blurEditorHandler = handleGraphBlurEditor;
        }
        
        // Zoom ayarları
        graph.centerZoom = true;
        
        // Smooth scroll zoom için container'a wheel event listener ekle
        if (graph.container) {
          let zoomAnimationId = null;
          
          const smoothZoomHandler = (evt) => {
            // Ctrl/Cmd tuşu basılıyken zoom yap
            if (evt.ctrlKey || evt.metaKey) {
              evt.preventDefault();
              evt.stopPropagation();
              
              if (zoomAnimationId) cancelAnimationFrame(zoomAnimationId);
              
              const delta = evt.deltaY || evt.wheelDelta || 0;
              const currentScale = graph.view.scale || 1;
              const zoomFactor = delta < 0 ? 1.05 : 0.95;
              const targetScale = Math.max(0.1, Math.min(4, currentScale * zoomFactor));
              
              const startScale = currentScale;
              const scaleDiff = targetScale - startScale;
              const duration = 200;
              const startTime = Date.now();
              
              const animateZoom = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeOut = 1 - Math.pow(1 - progress, 3);
                graph.view.setScale(startScale + scaleDiff * easeOut);
                graph.view.validate();
                if (progress < 1) {
                  zoomAnimationId = requestAnimationFrame(animateZoom);
                } else {
                  zoomAnimationId = null;
                }
              };
              animateZoom();
              return;
            }

            // Ctrl/Cmd YOK → iki parmak trackpad scroll = layout pan
            evt.preventDefault();
            evt.stopPropagation();
            if (!graph.view) return;
            const translate = graph.view.translate;
            const scale = graph.view.scale || 1;
            // deltaX/deltaY piksel cinsinden gelir, scale'e bölerek graph koordinatına çevir
            const dx = -(evt.deltaX || 0) / scale;
            const dy = -(evt.deltaY || 0) / scale;
            graph.view.setTranslate(translate.x + dx, translate.y + dy);
            graph.view.validate();
          };
          
          graph.container.addEventListener('wheel', smoothZoomHandler, { passive: false });
          
          // Cleanup için referansı sakla
          graph._smoothZoomHandler = smoothZoomHandler;
        }
        
        if (graph.container) {
          graph.container.style.pointerEvents = 'auto';
        }
        if (graph.view && graph.view.canvas) {
          graph.view.canvas.style.pointerEvents = 'auto';
        }
        
        // Design Mode specific handlers
        if (designMode) {
          graph.isCellSelectable = function(cell) {
            if (!cell) return false;
            return this.getModel().isVertex(cell) || this.getModel().isEdge(cell);
          };
          
          graph.isCellMovable = function(cell) {
            if (!cell) return false;
            return this.getModel().isVertex(cell);
          };
          
          graph.dblClick = function(evt, cell) {
            if (cell != null) {
              graph.startEditingAtCell(cell);
            }
          };
          
          graph.connectionHandler.createEdgeState = function(me) {
            // Edge oluşturulurken label set etme - CONNECT event'inde set edilecek
            // Edge oluşturulurken UUID formatında ID belirle
            const edgeId = generateUUID();
            const edge = graph.createEdge(edgeId, null, null, null, null, 'endArrow=block;edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;bendable=1;perimeter=1;editable=1;labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;fontColor=#000000;');
            return new window.mxCellState(graph.view, edge, graph.getCellStyle(edge));
          };
          
          // 8 handle noktası için connection constraint'ler (4 köşe + 4 kenar orta)
          // perimeter=true ile edge'ler shape'in border'ına tam temas eder
          graph.getAllConnectionConstraints = function(terminal) {
            if (terminal != null && terminal.shape != null) {
              return [
                // 4 köşe - perimeter=true ile border'a tam temas
                new mxConnectionConstraint(new mxPoint(0, 0), true),      // Sol üst
                new mxConnectionConstraint(new mxPoint(1, 0), true),      // Sağ üst
                new mxConnectionConstraint(new mxPoint(1, 1), true),       // Sağ alt
                new mxConnectionConstraint(new mxPoint(0, 1), true),       // Sol alt
                // 4 kenar orta - perimeter=true ile border'a tam temas
                new mxConnectionConstraint(new mxPoint(0.5, 0), true),     // Üst orta
                new mxConnectionConstraint(new mxPoint(1, 0.5), true),     // Sağ orta
                new mxConnectionConstraint(new mxPoint(0.5, 1), true),     // Alt orta
                new mxConnectionConstraint(new mxPoint(0, 0.5), true),     // Sol orta
              ];
            }
            return null;
          };
          
          graph.connectionHandler.isConnectableCell = function(cell) {
            return graph.getModel().isVertex(cell);
          };
          // Bağlantı başlatmak için constraint noktasına tam tıklanmalı
          // Küçük tolerance → şekil gövdesinde drag = hareket, sadece bağlantı noktasında drag = ok çizimi
          graph.connectionHandler.tolerance = 2;
        } else {
          // Design Mode kapalıyken - fonksiyonları temizle veya boş fonksiyon yap
          delete graph.isCellSelectable;
          delete graph.isCellMovable;
          // dblClick her zaman tanımlı olmalı (hata önlemek için)
          graph.dblClick = function(evt, cell) {
            // Design mode kapalıyken double-click'te hiçbir şey yapma
            // Sadece hata önlemek için boş fonksiyon
          };
          graph.connectionHandler.createEdgeState = null;
          graph.getAllConnectionConstraints = null;
          graph.connectionHandler.isConnectableCell = null;
        }
      };
      
      applyDesignModeSettings(isDesignMode);
      graph._applyDesignModeSettings = applyDesignModeSettings;

      // Graph container klavye odağı alabilsin — outline gösterme
      if (graph.container) {
        graph.container.setAttribute('tabindex', '-1');
        graph.container.style.outline = 'none';
      }

      // NOT: selection change listener'da focus() ÇAĞIRMIYORUZ.
      // focus() çağrısı drag seansı sırasında mouse event'lerini bozuyor
      // (mousedown → state change → focus() → mxGraph drag state kayboluyor).
      // Klavye shortcut'ları (ok, Ctrl+C/V) klavye handler'ında isGraphFocused
      // yerine "graph'ta seçili hücre var mı" kontrolüyle çalışacak.

      // Rubberband selection — yeni graph için her zaman yeniden oluştur
      if (rubberbandRef.current) {
        try { rubberbandRef.current.destroy(); } catch (_) {}
      }
      const rb = new mxRubberband(graph);
      rb.setEnabled(true);
      // defaultStyle'a z-index ekle — CSS !important ile de güvence altında
      rb.defaultStyle = 'position:absolute;overflow:hidden;z-index:99999;pointer-events:none;';
      rubberbandRef.current = rb;
      
      // Initial validation
      setTimeout(() => {
        if (graph && graph.view) {
          try {
            graph.view.validate();
            graph.refresh();
          } catch (error) {
            // ignore
          }
        }
      }, 100);
      
      // Ruler update function — RAF throttled, innerHTML sadece değer değişince
      let rulerRAF = null;
      let lastRulerKey = '';

      const updateRulers = () => {
        if (!graph || !graph.view || !container) return;
        if (rulerRAF) return; // already scheduled
        rulerRAF = requestAnimationFrame(() => {
          rulerRAF = null;
          try {
            const view = graph.view;
            const scale = view.scale || 1;
            const translate = view.translate || new mxPoint(0, 0);
            const containerRect = container.getBoundingClientRect();
            const gridSize = graph.gridSize || 20;
            const scaledGrid = gridSize * scale;
            const startX = -translate.x * scale;
            const startY = -translate.y * scale;
            const width = containerRect.width;
            const height = containerRect.height;

            // Skip if nothing changed
            const rulerKey = `${scale.toFixed(3)}_${translate.x.toFixed(1)}_${translate.y.toFixed(1)}_${width}_${height}`;
            if (rulerKey === lastRulerKey) return;
            lastRulerKey = rulerKey;

            const leftRuler = document.getElementById('left-ruler');
            const topRuler = document.getElementById('top-ruler');

            if (leftRuler) {
              const tickStep = scaledGrid * 3;
              let html = '';
              let lastValue = null;
              let lastPixelY = -100;
              for (let y = Math.floor(startY / tickStep) * tickStep; y < startY + height + tickStep; y += tickStep) {
                const pixelY = y - startY;
                const value = Math.round(y / scale);
                if (value === lastValue) continue;
                if (Math.abs(pixelY - lastPixelY) < 25 && lastPixelY !== -100) continue;
                lastValue = value;
                lastPixelY = pixelY;
                if (pixelY >= -1 && pixelY <= height + 1) {
                  html += `<div style="position:absolute;top:${pixelY}px;left:0;width:4px;height:1px;background:#555;"></div><div style="position:absolute;top:${pixelY - 5}px;left:5px;font-size:7px;color:#777;line-height:1;white-space:nowrap;">${value}</div>`;
                }
              }
              leftRuler.innerHTML = html;
              leftRuler.style.height = `${height}px`;
            }

            if (topRuler) {
              const graphWrapper = container.parentElement?.parentElement;
              const leftRulerWidth = 20;
              const wrapperWidth = graphWrapper ? graphWrapper.getBoundingClientRect().width - leftRulerWidth : width;
              const tickStep = scaledGrid * 3;
              let html = '';
              let lastValue = null;
              let lastPixelX = -100;
              for (let x = Math.floor(startX / tickStep) * tickStep; x < startX + wrapperWidth + tickStep; x += tickStep) {
                const pixelX = x - startX;
                const value = Math.round(x / scale);
                if (value === lastValue) continue;
                if (Math.abs(pixelX - lastPixelX) < 40 && lastPixelX !== -100) continue;
                lastValue = value;
                lastPixelX = pixelX;
                if (pixelX >= -1 && pixelX <= wrapperWidth + 1) {
                  html += `<div style="position:absolute;left:${pixelX}px;top:0;width:1px;height:4px;background:#555;"></div><div style="position:absolute;left:${pixelX + 3}px;top:5px;font-size:7px;color:#777;line-height:1;white-space:nowrap;">${value}</div>`;
                }
              }
              topRuler.innerHTML = html;
              topRuler.style.width = `${wrapperWidth}px`;
            }
          } catch (_) { /* ignore */ }
        });
      };

      const updateRulersHandler = () => { updateRulers(); };
      
      if (graph.view) {
        graph.view.addListener(mxEvent.SCALE, updateRulersHandler);
        graph.view.addListener(mxEvent.SCALE_AND_TRANSLATE, updateRulersHandler);
        graph.view.addListener(mxEvent.TRANSLATE, updateRulersHandler);
      }
      
      // Resize observer
      const handleResize = () => {
        if (graph && container) {
          try {
            graph.view.validate();
            updateRulers();
          } catch (error) {
            // ignore
          }
        }
      };
      
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
      
      // Scroll listener for ruler updates
      const scrollHandler = () => {
        updateRulers();
      };
      if (container) {
        container.addEventListener('scroll', scrollHandler);
      }
      
      setTimeout(() => {
        handleResize();
        updateRulers();
      }, 100);
      
      graph.view.validate();
      
      // Context menu
      if (graph.popupMenuHandler) {
        graph.popupMenuHandler.hideMenu = function() {};
        graph.popupMenuHandler.isPopupTrigger = function() { return false; };
      } else {
        graph.popupMenuHandler = {
          hideMenu: function() {},
          isPopupTrigger: function() { return false; }
        };
      }
      
      const handleContextMenu = (sender, evt) => {
        evt.consume();
        evt.preventDefault();
        
        const me = evt.getProperty('event');
        const cell = evt.getProperty('cell');
        const selectedCell = graph.getSelectionCell();
        const targetCell = cell || selectedCell;
        
        if (!targetCell) {
          return;
        }
        
        graph.setSelectionCell(targetCell);
        
        const existingMenu = document.querySelector('.mx-context-menu');
        if (existingMenu) {
          document.body.removeChild(existingMenu);
        }
        
        const menu = document.createElement('div');
        menu.className = 'mx-context-menu';
        menu.style.cssText = `
          position: fixed;
          left: ${me.clientX}px;
          top: ${me.clientY}px;
          background: #2d2d2d;
          border: 1px solid #3e3e42;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 100000;
          min-width: 160px;
          padding: 4px 0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        `;
        
        const isVertex = graph.getModel().isVertex(targetCell);
        
        if (isVertex) {
          const codeBtn = document.createElement('div');
          codeBtn.textContent = '💻 Code Editor';
          codeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
          codeBtn.onmouseenter = () => codeBtn.style.background = '#3e3e42';
          codeBtn.onmouseleave = () => codeBtn.style.background = 'transparent';
          codeBtn.onclick = (e) => {
            e.stopPropagation();
            if (targetCell && setSelectedShape) {
              const cellId = targetCell.getId();
              setSelectedShape(cellId);
              
              // Seçili şeklin rengini al
              const style = targetCell.getStyle() || '';
              const fillColorMatch = style.match(/fillColor=([^;]+)/);
              if (fillColorMatch) {
                setSelectedShapeColor(fillColorMatch[1]);
              } else {
                setSelectedShapeColor('#FFFF00');
              }

              // Code editor kendi shape'ini ayarla — selectedShape'ten bağımsız
              const cellValue = targetCell.getValue();
              const cellLabel = (cellValue ? String(cellValue).trim() : '') || cellId;
              setCodeEditorShapeId(cellId);
              setCodeEditorShapeLabel(cellLabel);
              // Tab'ı burada doğrudan ekle — useEffect timing sorunlarından kaçınmak için
              setEditorTabs((prev) => {
                if (prev.find((t) => t.shapeId === cellId)) return prev;
                return [...prev, { shapeId: cellId, shapeLabel: cellLabel }];
              });
              const currentWorkflowDataForLog = workflowDataRef.current || workflowData;
              const shapeDataForLog = currentWorkflowDataForLog?.contents?.find((it) => it && String(it.id) === String(cellId));
              const scriptForLog = activeTabId && tabCodeCacheRef.current?.[activeTabId]?.[cellId] !== undefined
                ? String(tabCodeCacheRef.current[activeTabId][cellId] ?? '')
                : (shapeDataForLog?.script != null ? String(shapeDataForLog.script) : '');
              pushDebugLog('context_open_code_editor', {
                shapeId: String(cellId),
                shapeLabel: cellLabel,
                scriptLength: scriptForLog.length,
                scriptPreview: toScriptPreview(scriptForLog),
                editorTabsAfter: editorTabs.map((t) => String(t.shapeId)),
              });
              setShowCodeEditor(true);
            }
            if (document.body.contains(menu)) {
              document.body.removeChild(menu);
            }
          };
          menu.appendChild(codeBtn);
          
          // Include butonları - şekle include eklenmişse "Remove Include", yoksa "Add Include"
          const shapeId = targetCell.getId();
          
          // Include kontrolü: hem state'den hem de workflowData'dan kontrol et
          // (State asenkron güncellenebilir, bu yüzden her iki kaynağı da kontrol ediyoruz)
          const hasIncludeInState = shapesWithInclude.has(shapeId);
          
          // workflowData'dan da kontrol et
          const currentWorkflowData = workflowDataRef.current || workflowData;
          let hasIncludeInData = false;
          if (currentWorkflowData?.contents) {
            const nodeData = currentWorkflowData.contents.find(item => 
              item && String(item.id) === String(shapeId)
            );
            hasIncludeInData = nodeData && 
                               nodeData.include && 
                               nodeData.include !== '' && 
                               nodeData.include !== 'null' && 
                               nodeData.include !== null;
          }
          
          // Overlay kontrolü - en güvenilir yöntem
          const cellOverlays = graph.getCellOverlays(targetCell);
          const hasIncludeOverlay = cellOverlays && cellOverlays.length > 0;
          
          // Herhangi birinde include varsa Remove Include göster
          const hasInclude = hasIncludeInState || hasIncludeInData || hasIncludeOverlay;
          
          
          if (hasInclude) {
            const includeInfoBtn = document.createElement('div');
            includeInfoBtn.textContent = 'ℹ️ Include Info';
            includeInfoBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
            includeInfoBtn.onmouseenter = () => includeInfoBtn.style.background = '#3e3e42';
            includeInfoBtn.onmouseleave = () => includeInfoBtn.style.background = 'transparent';
            includeInfoBtn.onclick = (e) => {
              e.stopPropagation();
              openIncludeDetails(shapeId, { showPopup: false });
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(includeInfoBtn);

            // Include edilmişse "Remove Include" butonu ekle
            const removeIncludeBtn = document.createElement('div');
            removeIncludeBtn.textContent = '🔗 Remove Include';
            removeIncludeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #ff6b6b; font-size: 13px; transition: background 0.2s;';
            removeIncludeBtn.onmouseenter = () => removeIncludeBtn.style.background = '#3e3e42';
            removeIncludeBtn.onmouseleave = () => removeIncludeBtn.style.background = 'transparent';
            removeIncludeBtn.onclick = async (e) => {
              e.stopPropagation();
              if (targetCell && graphRef.current) {
                try {
                  const processId = process.env.REACT_APP_ADD_INCLUDABLE_LAST_SELECT;
                  if (!processId) {
                    throw new Error('REACT_APP_ADD_INCLUDABLE_LAST_SELECT environment variable tanımlı değil!');
                  }
                  
                  // Include edilmiş şeklin foldername'ini al
                  const removeFoldername = shapeIncludeFolders[shapeId] || '';
                  
                  // Process ID'yi workflowData'dan al
                  const removeWorkflowData = workflowDataRef.current || workflowData;
                  const removeWorkflowProcessId = removeWorkflowData?.workflow?.id 
                    ? String(removeWorkflowData.workflow.id) 
                    : processId; // Fallback olarak REACT_APP_ADD_INCLUDABLE_LAST_SELECT kullan
                  
                  const bodyParams = {
                    stepid: shapeId, // Context menu'den gelen shapeId kullan (selectedIncludeShape değil!)
                    include: '', // Boş string - remove için
                    foldername: removeFoldername, // Include edilirken kaydedilen foldername
                    process: processId, 
                  };
                  
                  const response = await fetchWrapper(processId, bodyParams, navigate);
                  
                  // Response kontrolü
                  const resultData = response?.result;
                  const isSuccess = resultData && 
                                   (resultData.result === 1 || resultData.result === "1") && 
                                   (resultData.message === 'success' || resultData.message === 'Success');
                  
                  if (response && isSuccess) {
                    // Başarılı - Overlay'i kaldır ve state'leri güncelle
                    const graph = graphRef.current;
                    const model = graph.getModel();
                    const cell = model.getCell(shapeId);
                    
                    if (cell && model.isVertex(cell)) {
                      // Overlay'i kaldır - tüm overlay'leri kontrol et ve kaldır
                      const overlays = graph.getCellOverlays(cell);
                      if (overlays && overlays.length > 0) {
                        graph.removeCellOverlay(cell);
                      }
                      
                      // Label'ı eski haline döndür (eğer saklanmışsa)
                      const originalLabel = shapeOriginalLabels[shapeId];
                      if (originalLabel !== undefined) {
                        model.beginUpdate();
                        try {
                          cell.setValue(originalLabel);
                          graph.refresh(cell);
                        } finally {
                          model.endUpdate();
                        }
                      }
                      
                      // Graph'i refresh et - overlay'in kaldırıldığından emin olmak için
                      graph.getView().invalidate(cell);
                      graph.getView().validate();
                      graph.refresh();
                      
                      // Overlay'in gerçekten kaldırıldığını kontrol et
                      const remainingOverlays = graph.getCellOverlays(cell);
                      if (remainingOverlays && remainingOverlays.length > 0) {
                        // Hala overlay varsa tekrar kaldır
                        graph.removeCellOverlay(cell);
                        graph.refresh();
                      }
                    }
                    
                    // State'leri güncelle
                    setShapesWithInclude(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(shapeId);
                      return newSet;
                    });
                    
                    setShapeIncludePaths(prev => {
                      const newPaths = { ...prev };
                      delete newPaths[shapeId];
                      return newPaths;
                    });
                    
                    setShapeIncludeFolders(prev => {
                      const newFolders = { ...prev };
                      delete newFolders[shapeId];
                      return newFolders;
                    });
                    
                    setShapeOriginalLabels(prev => {
                      const newLabels = { ...prev };
                      delete newLabels[shapeId];
                      return newLabels;
                    });

                    // Aktif tab'ın workflowData'sından include bilgisini temizle
                    if (activeTabId) {
                      setTabs(prev => prev.map(tab => {
                        if (tab.id !== activeTabId) return tab;
                        const currentData = tab.workflowData || workflowDataRef.current;
                        if (!currentData?.contents) return tab;
                        const updatedContents = currentData.contents.map(item => {
                          if (!item || String(item.id) !== String(shapeId)) {
                            return item;
                          }
                          return {
                            ...item,
                            include: '',
                          };
                        });
                        return {
                          ...tab,
                          workflowData: {
                            ...currentData,
                            contents: updatedContents,
                          },
                        };
                      }));
                    }
                    
                    // Başarı mesajı göster
                    
                  } else {
                    alert('Include kaldırma başarısız!');
                  }
                } catch (error) {
                  alert('Include kaldırma sırasında bir hata oluştu: ' + error.message);
                }
              }
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(removeIncludeBtn);
          } else {
            // Include edilmemişse "Add Include" butonu ekle
            const includeBtn = document.createElement('div');
            includeBtn.textContent = '🔗 Add Include';
            includeBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
            includeBtn.onmouseenter = () => includeBtn.style.background = '#3e3e42';
            includeBtn.onmouseleave = () => includeBtn.style.background = 'transparent';
            includeBtn.onclick = (e) => {
              e.stopPropagation();
              if (targetCell) {
                setSelectedIncludeShape(targetCell.getId());
                setShowIncludeModal(true);
              }
              if (document.body.contains(menu)) {
                document.body.removeChild(menu);
              }
            };
            menu.appendChild(includeBtn);
          }
        }
        
        const deleteBtn = document.createElement('div');
        deleteBtn.textContent = '🗑️ Sil';
        deleteBtn.style.cssText = 'padding: 10px 16px; cursor: pointer; color: #cccccc; font-size: 13px; transition: background 0.2s;';
        deleteBtn.onmouseenter = () => deleteBtn.style.background = '#3e3e42';
        deleteBtn.onmouseleave = () => deleteBtn.style.background = 'transparent';
        deleteBtn.onclick = (e) => {
          e.stopPropagation();
          
          // Close context menu first
          if (document.body.contains(menu)) {
            document.body.removeChild(menu);
          }
          
          // Show confirmation dialog
          const isVertex = graph.getModel().isVertex(targetCell);
          const cellLabel = targetCell.getValue() ? String(targetCell.getValue()) : 'item';
          const itemType = isVertex ? 'shape' : 'connection';
          
          if (window.confirm(`Are you sure you want to delete this ${itemType}?\n\n"${cellLabel}"\n\nThis action cannot be undone.`)) {
            graph.removeCells([targetCell]);
          }
        };
        menu.appendChild(deleteBtn);
        document.body.appendChild(menu);
        
        const closeMenu = (e) => {
          if (!menu.contains(e.target)) {
            if (document.body.contains(menu)) {
              document.body.removeChild(menu);
            }
            document.removeEventListener('click', closeMenu);
            document.removeEventListener('contextmenu', closeMenu);
          }
        };
        
        setTimeout(() => {
          document.addEventListener('click', closeMenu, true);
          document.addEventListener('contextmenu', closeMenu, true);
        }, 10);
      };
      
      const contextMenuHandler = (evt) => {
        const pt = mxUtils.convertPoint(graph.container, mxEvent.getClientX(evt), mxEvent.getClientY(evt));
        const cell = graph.getCellAt(pt.x, pt.y);
        
        const eventObj = {
          getProperty: (prop) => {
            if (prop === 'event') return evt;
            if (prop === 'cell') return cell;
            return null;
          },
          consume: () => {
            mxEvent.consume(evt);
          },
          preventDefault: () => {
            evt.preventDefault();
          }
        };
        
        handleContextMenu(graph, eventObj);
      };
      
      mxEvent.addListener(graph.container, 'contextmenu', contextMenuHandler);
      
      // Node click handler - shape selection için
      graph.addListener(mxEvent.CLICK, function(sender, evt) {
        const cell = evt.getProperty('cell');
        if (cell && graph.getModel().isVertex(cell) && setSelectedShape) {
          pushDebugLog('design_shape_left_click', {
            clickedShapeId: String(cell.getId()),
          });
          setSelectedShape(cell.getId());
          
          // Seçili şeklin rengini al
          const style = cell.getStyle() || '';
          const fillColorMatch = style.match(/fillColor=([^;]+)/);
          if (fillColorMatch) {
            setSelectedShapeColor(fillColorMatch[1]);
          } else {
            setSelectedShapeColor('#FFFF00');
          }
        }
      });
      
      // Helper function: Bir source'dan çıkan edge'lerin en yüksek kosul değerini bul
      const getMaxKosulForSource = (source, excludeEdge = null) => {
        if (!source) return 0;
        
        const model = graph.getModel();
        const sourceId = source.getId ? String(source.getId()) : String(source);
        const kosulValues = [];
        
        // 1. Graph'taki edge'lerin label'larından kosul değerlerini çıkar
        const allCells = model.getChildCells(model.getRoot());
        const allEdges = allCells.filter(c => model.isEdge(c));
        
        const sourceEdges = allEdges.filter(edge => {
          // Exclude edilecek edge'i atla
          if (excludeEdge && edge === excludeEdge) return false;
          
          const edgeSource = edge.getTerminal(true);
          if (!edgeSource) return false;
          
          const edgeSourceId = edgeSource.getId ? String(edgeSource.getId()) : String(edgeSource);
          
          // Source ID'lerini karşılaştır
          return sourceId === edgeSourceId;
        });
        
        // Graph'taki edge'lerin label'larından kosul çıkar
        sourceEdges.forEach(edge => {
          const label = edge.getValue() ? String(edge.getValue()).trim() : '';
          if (label && label.includes(':Next')) {
            const kosulMatch = label.match(/(\d+):Next/);
            if (kosulMatch) {
              const numValue = parseInt(kosulMatch[1], 10);
              if (!isNaN(numValue) && numValue > 0) {
                kosulValues.push(numValue);
              }
            }
          }
        });
        
        // 2. Workflow data'daki edge'lerin kosul değerlerini kontrol et
        const currentWorkflowData = workflowDataRef.current || workflowData;
        if (currentWorkflowData && currentWorkflowData.contents) {
          currentWorkflowData.contents.forEach(item => {
            // Edge kontrolü
            if (item && (item.edge === "1" || item.edge === 1 || item.edge === true)) {
              const edgeSourceId = item.source ? String(item.source) : '';
              
              // Aynı source'tan çıkan edge mi?
              if (edgeSourceId === sourceId) {
                // Exclude edilecek edge'i atla (eğer ID'si varsa)
                if (excludeEdge) {
                  const excludeEdgeId = excludeEdge.getId ? String(excludeEdge.getId()) : '';
                  const itemId = item.id ? String(item.id) : '';
                  if (excludeEdgeId && itemId === excludeEdgeId) {
                    return; // Skip this edge
                  }
                }
                
                // Kosul değerini kontrol et
                if (item.kosul) {
                  const kosulValue = parseInt(String(item.kosul), 10);
                  if (!isNaN(kosulValue) && kosulValue > 0) {
                    kosulValues.push(kosulValue);
                  }
                }
                
                // Label'dan da kosul çıkarmayı dene (eğer kosul field'ı yoksa)
                if (!item.kosul && item.definition) {
                  const label = String(item.definition).trim();
                  if (label && label.includes(':Next')) {
                    const kosulMatch = label.match(/(\d+):Next/);
                    if (kosulMatch) {
                      const numValue = parseInt(kosulMatch[1], 10);
                      if (!isNaN(numValue) && numValue > 0) {
                        kosulValues.push(numValue);
                      }
                    }
                  }
                }
              }
            }
          });
        }
        
        // En yüksek kosul değerini bul (eğer yoksa 0)
        const maxKosul = kosulValues.length > 0 ? Math.max(...kosulValues) : 0;
        
        return maxKosul;
      };
      
      // Edge connection handler - kosul değeri ekle (10'un katları: 10, 20, 30, ...)
      graph.addListener(mxEvent.CONNECT, function(sender, evt) {
        const edge = evt.getProperty('edge');
        const source = edge ? edge.getTerminal(true) : null;
        
        if (edge && source && graph.getModel().isVertex(source)) {
          // Edge model'e eklendikten sonra label'ı set et
          setTimeout(() => {
            try {
              const model = graph.getModel();
              
              // Edge ID'sini UUID formatına çevir (eğer zaten UUID formatında değilse)
              const currentId = edge.getId() ? String(edge.getId()) : '';
              const isUUIDFormat = /^[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}$/i.test(currentId);
              
              if (!isUUIDFormat) {
                const newId = generateUUID();
                model.beginUpdate();
                try {
                  // mxGraph'te cell ID'sini değiştirmek için doğrudan cell.id kullan
                  edge.id = newId;
                  // Model'in cell dictionary'sini güncelle
                  if (model.cells) {
                    delete model.cells[currentId];
                    model.cells[newId] = edge;
                  }
                } finally {
                  model.endUpdate();
                }
              }
              
              // Edge'in mevcut label'ını kontrol et
              const currentLabel = edge.getValue() ? String(edge.getValue()).trim() : '';
              
              // Eğer label yoksa veya :Next ile bitmiyorsa, label'ı hesapla ve set et
              if (!currentLabel || currentLabel === '' || !currentLabel.includes(':Next')) {
                
                // Aynı source'dan çıkan edge'lerin en yüksek kosul değerini bul
                // Edge zaten model'e eklenmiş, bu yüzden excludeEdge olarak geç
                const maxKosul = getMaxKosulForSource(source, edge);
                // Eğer hiç edge yoksa maxKosul = 0, yeni kosul = 10
                // Eğer edge varsa, en yüksek kosul + 10
                const newKosul = maxKosul === 0 ? 10 : maxKosul + 10;
                
                // Debug log
                
                
                // Edge label'ını ve style'ını güncelle
                model.beginUpdate();
                try {
                  // Edge value'sunu set et
                  edge.setValue(`${newKosul}:Next`);
                  
                  // Edge style'ını güncelle - label'ın görünmesi ve editable olması için
                  let style = edge.getStyle() || '';
                  
                  // Label görünürlüğü için
                  if (!style.includes('labelBackgroundColor')) {
                    style += 'labelBackgroundColor=#fff;';
                  }
                  if (!style.includes('labelBorderColor')) {
                    style += 'labelBorderColor=#e0e0e0;';
                  }
                  if (!style.includes('fontColor')) {
                    style += 'fontColor=#000000;';
                  }
                  
                  // Editable olması için
                  if (!style.includes('editable=1')) {
                    style = style.replace(/editable=[^;]*;?/g, '');
                    style += 'editable=1;';
                  }
                  
                  edge.setStyle(style);
                  
                  // Graph'i refresh et
                  graph.refresh(edge);
                  graph.view.validate();
                  
                } finally {
                  model.endUpdate();
                }
              } else {
              }
            } catch (error) {
            }
          }, 150);
        } else {
        }
      });
      
      // CELLS_ADDED event'ini de dinle - yedek olarak label'ı kontrol et
      graph.addListener(mxEvent.CELLS_ADDED, function(sender, evt) {
        const cells = evt.getProperty('cells');
        
        if (cells && Array.isArray(cells)) {
          cells.forEach(cell => {
            if (graph.getModel().isEdge(cell)) {
              
              // Edge ID'sini UUID formatına çevir (eğer zaten UUID formatında değilse)
              const currentId = cell.getId() ? String(cell.getId()) : '';
              const isUUIDFormat = /^[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}$/i.test(currentId);
              
              if (!isUUIDFormat) {
                const model = graph.getModel();
                const newId = generateUUID();
                model.beginUpdate();
                try {
                  // mxGraph'te cell ID'sini değiştirmek için doğrudan cell.id kullan
                  cell.id = newId;
                  // Model'in cell dictionary'sini güncelle
                  if (model.cells) {
                    delete model.cells[currentId];
                    model.cells[newId] = cell;
                  }
                } finally {
                  model.endUpdate();
                }
              }
              
              // Edge'in label'ı var mı kontrol et
              const currentValue = cell.getValue() ? String(cell.getValue()).trim() : '';
              
              // Label yoksa veya :Next ile bitmiyorsa, yeni label hesapla ve set et
              if (!currentValue || currentValue === '' || !currentValue.includes(':Next')) {
                
                setTimeout(() => {
                  try {
                    const source = cell.getTerminal(true);
                    if (source && graph.getModel().isVertex(source)) {
                      const model = graph.getModel();
                      
                      // Aynı source'dan çıkan edge'lerin en yüksek kosul değerini bul
                      const maxKosul = getMaxKosulForSource(source, cell);
                      // Eğer hiç edge yoksa maxKosul = 0, yeni kosul = 10
                      // Eğer edge varsa, en yüksek kosul + 10
                      const newKosul = maxKosul === 0 ? 10 : maxKosul + 10;
                      
                      // Debug log
                      
                      
                      model.beginUpdate();
                      try {
                        cell.setValue(`${newKosul}:Next`);
                        let style = cell.getStyle() || '';
                        if (!style.includes('labelBackgroundColor')) {
                          style += 'labelBackgroundColor=#fff;';
                        }
                        if (!style.includes('labelBorderColor')) {
                          style += 'labelBorderColor=#e0e0e0;';
                        }
                        if (!style.includes('fontColor')) {
                          style += 'fontColor=#000000;';
                        }
                        if (!style.includes('editable=1')) {
                          style = style.replace(/editable=[^;]*;?/g, '');
                          style += 'editable=1;';
                        }
                        cell.setStyle(style);
                        graph.refresh(cell);
                        graph.view.validate();
                        
                      } finally {
                        model.endUpdate();
                      }
                    }
                  } catch (error) {
                  }
                }, 200);
              } else {
              }
            }
          });
        }
      });
      
      // Render workflow data - aktif tab'ın workflowData'sını kullan
      const activeTab = tabs.find(tab => tab.id === activeTabId);
      const currentWorkflowData = activeTab?.workflowData || workflowDataRef.current;
      if (currentWorkflowData && currentWorkflowData.contents) {
        const parent = graph.getDefaultParent();
        const nodes = {};
        graph.getModel().beginUpdate();
        try {
          currentWorkflowData.contents
            .filter(item => item && (item.vertex === "1" || item.vertex === 1 || item.vertex === true))
            .forEach(node => {
              const nodeId = node.id ? String(node.id) : '';
              const nodeStyle = node.style ? String(node.style) : '';
              const nodeDefinition = node.definition ? String(node.definition) : '';
              
              let style = nodeStyle || '';
              if (style && !style.endsWith(';')) {
                style += ';';
              }
              
              // Shape bilgisini style'dan çıkar
              let shapeNameFromStyle = '';
              const shapeMatch = style.match(/shape=([^;]+)/);
              if (shapeMatch) {
                shapeNameFromStyle = shapeMatch[1];
              }
              
              // Eğer style'da shape bilgisi yoksa, node'un definition'ından veya başka bir yerden shape name'i bulmaya çalış
              // Ancak genellikle style'da shape bilgisi olmalı
              // Image'ları kaldır, sadece mxGraph shape'lerini kullan
              style = style.replace(/image=[^;]*;?/g, '');
              style = style.replace(/imageWidth=[^;]*;?/g, '');
              style = style.replace(/imageHeight=[^;]*;?/g, '');
              style = style.replace(/imageAspect=[^;]*;?/g, '');
              style = style.replace(/imageAlign=[^;]*;?/g, '');
              style = style.replace(/imageVerticalAlign=[^;]*;?/g, '');
              
              // Shape'i mxGraph'in built-in shape'lerine map et
              let mxShape = 'rectangle'; // default
              if (shapeNameFromStyle === 'ellipse' || shapeNameFromStyle === 'startCircle' || shapeNameFromStyle === 'stopCircle') {
                mxShape = 'ellipse';
              } else if (shapeNameFromStyle === 'rhombus') {
                mxShape = 'rhombus';
              } else if (shapeNameFromStyle === 'triangle') {
                mxShape = 'triangle'; // Custom triangle shape
              } else if (shapeNameFromStyle === 'hexagon') {
                mxShape = 'hexagon';
              } else if (shapeNameFromStyle === 'cylinder') {
                mxShape = 'cylinder';
              } else if (shapeNameFromStyle === 'preProcess' || shapeNameFromStyle === 'predefinedProcess') {
                mxShape = 'predefinedProcess'; // Custom predefined process (double border)
              } else if (shapeNameFromStyle === 'rectangle') {
                mxShape = 'rectangle';
              } else if (!shapeNameFromStyle || shapeNameFromStyle === 'image') {
                // Eğer shape belirtilmemişse veya image ise, rectangle kullan
                mxShape = 'rectangle';
              } else {
                // Mevcut shape'i koru (eğer geçerli bir mxGraph shape'i ise)
                mxShape = shapeNameFromStyle;
              }
              
              // Shape'i güncelle
              style = style.replace(/shape=[^;]*;?/g, '');
              // fillColor sabit kalmalı - şekil tipine göre shapes array'indeki color kullanılmalı
              // fillColor'ı değiştirmiyoruz, mevcut fillColor korunuyor
              if (!style.includes('fillColor')) {
                // Eğer fillColor yoksa, şekil tipine göre default renk ekle
                const shapeColor = shapes.find(s => s.mxShape === mxShape)?.color || '#FFFF00';
                style += `fillColor=${shapeColor};`;
              }
              // Font rengini ekle - tüm renklerde belirgin olması için
              if (!style.includes('fontColor')) {
                style += 'fontColor=#000000;';
              }
              style = style.replace(/selectable=[^;]*;?/g, '');
              style = style.replace(/movable=[^;]*;?/g, '');
              style = style.replace(/resizable=[^;]*;?/g, '');
              style = style.replace(/connectable=[^;]*;?/g, '');
              style = style.replace(/;;+/g, ';');
              if (!style.endsWith(';')) style += ';';
              
              const x = parseInt(node.GeometryX) || 40;
              const y = parseInt(node.GeometryY) || 40;
              let w = parseInt(node.GeometryWidth) || 60;
              let h = parseInt(node.GeometryHeight) || 60; // Default boyutlar küçültüldü
              
              // Başlangıç ve bitiş şekilleri için tam yuvarlak (width = height)
              if (mxShape === 'ellipse' && (shapeNameFromStyle === 'startCircle' || shapeNameFromStyle === 'stopCircle')) {
                // Width ve height'ın ortalamasını al veya daha büyük olanı kullan
                const avgSize = Math.max(w, h);
                w = avgSize;
                h = avgSize;
              }
              
              const label = nodeDefinition;
              
              // mxGraph shape'ini ekle
              style += `shape=${mxShape};`;
              // Label taşmasını önle
              if (!style.includes('overflow=')) {
                style += 'overflow=hidden;';
              }
              if (!style.includes('whiteSpace=')) {
                style += 'whiteSpace=wrap;html=1;';
              }
              
              if (isDesignMode) {
                style += 'selectable=1;movable=1;resizable=1;connectable=1;';
              } else {
                style += 'selectable=0;movable=0;resizable=0;';
              }
              
              const vertex = graph.insertVertex(parent, nodeId, label, x, y, w, h, style);
              nodes[nodeId] = vertex;
              
              // Eğer bu şeklin include'u varsa overlay ekle
              if (node.include && node.include !== '' && node.include !== 'null' && node.include !== null) {
                // Include edilmiş şekiller listesine ekle
                setShapesWithInclude(prev => new Set([...prev, nodeId]));
                
                // Overlay ekle - setTimeout ile graph render tamamlandıktan sonra
                setTimeout(() => {
                  try {
                    const attachIcon = createAttachIcon();
                    const overlay = new mxCellOverlay(
                      attachIcon,
                      'Include edildi',
                      mxConstants.ALIGN_RIGHT,
                      mxConstants.ALIGN_BOTTOM,
                      new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                    );
                    graph.addCellOverlay(vertex, overlay);
                    graph.getView().invalidate(vertex);
                    graph.getView().validate();
                    graph.refresh();
                  } catch (error) {
                    console.error('Overlay ekleme hatası:', error);
                  }
                }, 50);
              }
            });

          currentWorkflowData.contents
            .filter(item => item && (item.edge === "1" || item.edge === 1 || item.edge === true))
            .forEach(edge => {
              const edgeId = edge.id ? String(edge.id) : '';
              const edgeStyle = edge.style ? String(edge.style) : '';
              const edgeDefinition = edge.definition ? String(edge.definition) : '';
              const edgeKosul = edge.kosul ? String(edge.kosul) : '';
              const sourceId = edge.source ? String(edge.source) : '';
              const targetId = edge.target ? String(edge.target) : '';
              
              const from = nodes[sourceId];
              const to = nodes[targetId];
              if (from && to) {
                let style = edgeStyle;
                if (!style.includes('endArrow')) style += 'endArrow=block;';
                style += 'edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;';
                // perimeter=1 ile edge'ler shape'in border'ına tam temas eder
                if (!style.includes('perimeter')) style += 'perimeter=1;';
                style = style.replace(/bendable=[^;]*;?/g, '');
                style = style.replace(/editable=[^;]*;?/g, '');
                style = style.replace(/selectable=[^;]*;?/g, '');
                style = style.replace(/movable=[^;]*;?/g, '');
                style = style.replace(/;;+/g, ';');
                if (!style.endsWith(';')) style += ';';
                
                if (isDesignMode) {
                  style += 'bendable=1;editable=1;selectable=1;movable=1;';
                } else {
                  style += 'bendable=0;editable=0;selectable=0;movable=0;';
                }
                style += 'labelBackgroundColor=#fff;labelBorderColor=#e0e0e0;fontColor=#000000;';
                
                let label = edgeDefinition;
                if (edgeKosul && !label.includes(':Next')) {
                  label = `${edgeKosul}:Next`;
                }
                
                const e = graph.insertEdge(parent, edgeId, label, from, to, style);
                
                if (edge.GeometryPoints) {
                  try {
                    const points = JSON.parse(edge.GeometryPoints);
                    if (Array.isArray(points) && points.length > 0) {
                      const geo = e.getGeometry().clone();
                      geo.points = points.map(pt => new window.mxPoint(pt.x, pt.y));
                      e.setGeometry(geo);
                    }
                  } catch (err) {
                    // ignore
                  }
                }
              }
            });
        } finally {
          graph.getModel().endUpdate();
        }
        
        graph.view.validate();
        graph.refresh();
        
        // Include edilmiş şekillere overlay ekle (workflowData'dan)
        setTimeout(() => {
          if (currentWorkflowData && currentWorkflowData.contents) {
            currentWorkflowData.contents
              .filter(item => item && (item.vertex === "1" || item.vertex === 1 || item.vertex === true))
              .forEach(node => {
                const nodeId = node.id ? String(node.id) : '';
                if (node.include && node.include !== '' && node.include !== 'null' && node.include !== null) {
                  const cell = graph.getModel().getCell(nodeId);
                  if (cell && graph.getModel().isVertex(cell)) {
                    // Mevcut overlay'i kaldır (varsa)
                    graph.removeCellOverlay(cell);
                    
                    // Yeni overlay ekle
                    const attachIcon = createAttachIcon();
                    const overlay = new mxCellOverlay(
                      attachIcon,
                      'Include edildi',
                      mxConstants.ALIGN_RIGHT,
                      mxConstants.ALIGN_BOTTOM,
                      new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                    );
                    overlay.addListener(mxEvent.CLICK, function(sender, evt) {
                      const cell = evt.getProperty('cell');
                      if (cell) {
                        openIncludeDetails(cell.getId(), { showPopup: false });
                      }
                    });
                    graph.addCellOverlay(cell, overlay);
                    graph.getView().invalidate(cell);
                    graph.getView().validate();
                    graph.refresh();
                    
                    // Include edilmiş şekiller listesine ekle
                    setShapesWithInclude(prev => new Set([...prev, nodeId]));
                  }
                }
              });
            
            graph.view.validate();
            graph.refresh();
          }
        }, 150);
        
        if (isDesignMode) {
          setTimeout(() => {
            const cells = graph.getChildCells(graph.getDefaultParent());
            cells.forEach(cell => {
              if (graph.getModel().isVertex(cell)) {
                let style = cell.getStyle() || '';
                if (!style.includes('selectable=1')) {
                  style = style.replace(/selectable=[^;]*;?/g, '');
                  style = style.replace(/movable=[^;]*;?/g, '');
                  style = style.replace(/resizable=[^;]*;?/g, '');
                  cell.setStyle(style + ';selectable=1;movable=1;resizable=1;');
                }
              }
            });
            graph.view.validate();
            graph.refresh();
          }, 50);
        }
        
        workflowIdRef.current = currentWorkflowId;
      }

      return () => {
        try {
          if (container && contextMenuHandler) {
            mxEvent.removeListener(container, 'contextmenu', contextMenuHandler);
          }
          if (container && scrollHandler) {
            container.removeEventListener('scroll', scrollHandler);
          }
          if (graph && graph.view && updateRulersHandler) {
            graph.view.removeListener(mxEvent.SCALE, updateRulersHandler);
            graph.view.removeListener(mxEvent.SCALE_AND_TRANSLATE, updateRulersHandler);
            graph.view.removeListener(mxEvent.TRANSLATE, updateRulersHandler);
          }
          if (resizeObserver) {
            resizeObserver.disconnect();
          }
          if (rubberbandRef.current) {
            try {
              rubberbandRef.current.destroy();
            } catch (e) {
              // ignore
            }
            rubberbandRef.current = null;
          }
          // Cleanup middle mouse handlers
          if (graph && graph.container && graph._middleMouseHandlers) {
            graph.container.removeEventListener('mousedown', graph._middleMouseHandlers.down);
            graph.container.removeEventListener('mouseup', graph._middleMouseHandlers.up);
          }
          // Cleanup blur-editor handler
          if (graph && graph.container && graph._blurEditorHandler) {
            graph.container.removeEventListener('mousedown', graph._blurEditorHandler);
          }
          // Cleanup smooth zoom handler
          if (graph && graph.container && graph._smoothZoomHandler) {
            graph.container.removeEventListener('wheel', graph._smoothZoomHandler);
          }
          if (graph) {
            try {
              graph.destroy();
            } catch (error) {
              // ignore
            }
          }
          if (graphRef.current) {
            graphRef.current = null;
          }
          if (setSelectedShape) {
            setSelectedShape(null);
          }
        } catch (error) {
          // ignore
        }
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDesignMode, activeTabId]); // tabs → tabsRef.current ile okunuyor, dependency'e girmez

  // Update graph when design mode changes
  useEffect(() => {
    if (!graphRef.current) {
      return;
    }
    if (!graphRef.current._applyDesignModeSettings) {
      return;
    }
    
    const graph = graphRef.current;

    // Design mode değişince rubber-band canlı olmalı
    if (!rubberbandRef.current) {
      const rb2 = new mxRubberband(graph);
      rb2.setEnabled(true);
      rubberbandRef.current = rb2;
    }
    
    graph._applyDesignModeSettings(isDesignMode);
    
    if (graphContainerRef.current) {
      graphContainerRef.current.style.cursor = isDesignMode ? 'default' : 'grab';
      if (graph.container) {
        graph.container.style.cursor = isDesignMode ? 'default' : 'grab';
      }
    }
    
    graph.getModel().beginUpdate();
    try {
      const cells = graph.getChildCells(graph.getDefaultParent());
      cells.forEach((cell) => {
        if (graph.getModel().isVertex(cell)) {
          let style = cell.getStyle() || '';
          style = style.replace(/selectable=[^;]*;?/g, '');
          style = style.replace(/movable=[^;]*;?/g, '');
          style = style.replace(/resizable=[^;]*;?/g, '');
          style = style.replace(/connectable=[^;]*;?/g, '');
          style = style.replace(/;;+/g, ';');
          if (!style.endsWith(';')) style += ';';
          
          if (isDesignMode) {
            cell.setStyle(style + 'selectable=1;movable=1;resizable=1;connectable=1;');
          } else {
            cell.setStyle(style + 'selectable=0;movable=0;resizable=0;');
          }
          graph.refresh(cell);
        } else if (graph.getModel().isEdge(cell)) {
          let style = cell.getStyle() || '';
          style = style.replace(/editable=[^;]*;?/g, '');
          style = style.replace(/bendable=[^;]*;?/g, '');
          style = style.replace(/selectable=[^;]*;?/g, '');
          style = style.replace(/movable=[^;]*;?/g, '');
          style = style.replace(/;;+/g, ';');
          if (!style.endsWith(';')) style += ';';
          
          if (isDesignMode) {
            cell.setStyle(style + 'editable=1;bendable=1;selectable=1;movable=1;');
          } else {
            cell.setStyle(style + 'editable=0;bendable=0;selectable=0;movable=0;');
          }
          graph.refresh(cell);
        }
      });
    } finally {
      graph.getModel().endUpdate();
      graph.view.validate();
      graph.refresh();
    }
    
    setTimeout(() => {
      if (graph.container) {
        graph.container.style.pointerEvents = 'auto';
      }
      if (graph.view) {
        if (graph.view.canvas) {
          graph.view.canvas.style.pointerEvents = 'auto';
        }
        if (graph.view.backgroundPane) {
          graph.view.backgroundPane.style.pointerEvents = 'none';
        }
        if (graph.view.drawPane) {
          graph.view.drawPane.style.pointerEvents = 'auto';
        }
        if (graph.view.overlayPane) {
          graph.view.overlayPane.style.pointerEvents = 'auto';
        }
      }
    }, 10);
  }, [isDesignMode]);

  // ========== KEYBOARD SHORTCUTS ==========
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Monaco editör veya form inputu fokuslu mu?
      let isEditorFocused = false;
      let isGlobalHotkeyIgnored = false;
      let isMxLabelEditing = false;
      let isMonacoFocused = false;
      try {
        const ae = document.activeElement;
        const tag = ae?.tagName ? String(ae.tagName).toUpperCase() : '';
        isGlobalHotkeyIgnored = !!ae?.closest?.('[data-ignore-global-hotkeys="true"]');
        isMonacoFocused =
          !!ae?.closest?.('.monaco-editor') ||
          !!ae?.closest?.('.monaco-inputbox');
        isEditorFocused =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          !!ae?.isContentEditable ||
          isGlobalHotkeyIgnored ||
          isMonacoFocused;
        // mxGraph inline label editörü aktif mi?
        isMxLabelEditing = !!(graphRef.current?.cellEditor?.editingCell);
      } catch (_e) { /* ignore */ }

      if (isGlobalHotkeyIgnored) {
        return;
      }

      // Graph'ta seçili hücre var mı?
      const hasGraphSelection = !!(
        graphRef.current &&
        graphRef.current.getSelectionCells?.()?.length > 0
      );

      // Odak yönetimi:
      // 1. mxGraph label editörü aktifken → Escape dışında tüm tuşları editöre bırak
      //    (ok tuşları cursor'ı hareket ettirmeli, şekli değil)
      // 2. Monaco editörü odaklıyken → Ctrl+C/V'yi editöre bırak (graph'a verme)
      // 3. Diğer input/editörler → graph kısayollarını yalnızca seçim varken uygula
      if (isEditorFocused) {
        if (e.key === 'Escape') {
          // Escape her zaman işlenir
        } else if (isMxLabelEditing) {
          // Label düzenleme modunda: tüm tuşlar editöre ait
          return;
        } else {
          const isCopyPaste = (e.ctrlKey || e.metaKey) && ['c', 'v'].includes(e.key);
          const isGraphShortcut =
            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(e.key) ||
            isCopyPaste ||
            ((e.ctrlKey || e.metaKey) && e.key === 'k');

          if (isGraphShortcut && hasGraphSelection && !(isMonacoFocused && isCopyPaste)) {
            // Graph seçimi varken graph kısayollarına izin ver,
            // ama Monaco odaklıyken copy/paste editöre bırak
          } else {
            return; // Editöre bırak
          }
        }
      }
      
      // Space tuşu basılı tutulduğunda pan yapılabilir
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsSpacePressed(true);
        if (graphRef.current && graphRef.current.panningHandler) {
          // Space tuşu basılıyken sadece boş alanda pan yapılabilir
          graphRef.current.panningHandler.useLeftButtonForPanning = true;
          graphRef.current.panningHandler.ignoreCell = false; // Cell'lerin üzerinde pan yapma
        }
        if (graphRef.current && graphRef.current.container) {
          // Space pan modunu işaretle
          graphRef.current.container.dataset.spacePan = 'true';
          graphRef.current.container.style.cursor = 'grab';
          // Tüm graph container'ına grab cursor'ı uygula
          if (graphRef.current.view && graphRef.current.view.canvas) {
            graphRef.current.view.canvas.style.cursor = 'grab';
          }
        }
        return;
      }
      // Ctrl+K veya Cmd+K ile AI Prompt aç
      if ((e.ctrlKey || e.metaKey) && e.key === 'k' && showCodeEditor) {
        e.preventDefault();
        handleOpenAIPrompt();
        return;
      }
      
      if (e.key === 'Escape') {
        if (showAIPrompt) {
          setShowAIPrompt(false);
          setAiPrompt('');
        } else if (showCodeEditor) {
          setShowCodeEditor(false);
        } else if (onClose) {
          onClose();
        }
      } else if (e.key === 'Delete' && isDesignMode && graphRef.current) {
        const graph = graphRef.current;
        const cells = graph.getSelectionCells();
        if (cells && cells.length > 0) {
          const model = graph.getModel();
          const vertexCount = cells.filter(c => model.isVertex(c)).length;
          const edgeCount = cells.filter(c => model.isEdge(c)).length;
          let message = 'Silmek istediğinize emin misiniz? ';
          if (vertexCount > 0 && edgeCount > 0) {
            message += `${vertexCount} şekil ve ${edgeCount} bağlantı`;
          } else if (vertexCount > 0) {
            message += `${vertexCount} şekil`;
          } else {
            message += `${edgeCount} bağlantı`;
          }
          if (window.confirm(message)) {
            graph.removeCells(cells);
          }
        }

      // Ok tuşlarıyla seçili şekilleri grid adımıyla hareket ettir (Shift = 5x adım)
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && isDesignMode && graphRef.current) {
        const graph = graphRef.current;
        const cells = graph.getSelectionCells().filter(c => graph.getModel().isVertex(c));
        if (cells.length > 0) {
          e.preventDefault();
          const step = (e.shiftKey ? 5 : 1) * (graph.gridSize || 20);
          const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
          const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
          graph.getModel().beginUpdate();
          try {
            cells.forEach((cell) => {
              const geo = cell.getGeometry().clone();
              geo.x += dx;
              geo.y += dy;
              graph.getModel().setGeometry(cell, geo);
            });
          } finally {
            graph.getModel().endUpdate();
          }
        }

      // Ctrl+C: seçili vertex'leri clipboard'a kopyala
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'c' && isDesignMode && graphRef.current) {
        const graph = graphRef.current;
        const cells = graph.getSelectionCells().filter(c => graph.getModel().isVertex(c));
        if (cells.length > 0) {
          e.preventDefault();
          clipboardRef.current = cells;
        }

      // Ctrl+V: clipboard'daki şekilleri grid-hizalı offset ile yapıştır
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v' && isDesignMode && graphRef.current) {
        if (clipboardRef.current.length > 0) {
          e.preventDefault();
          const graph = graphRef.current;
          const gridSize = graph.gridSize || 20;
          try {
            const clones = graph.cloneCells(clipboardRef.current, true);
            const parent = graph.getDefaultParent();
            graph.getModel().beginUpdate();
            try {
              clones.forEach((clone) => {
                if (graph.getModel().isVertex(clone)) {
                  const geo = clone.getGeometry().clone();
                  geo.x = Math.round((geo.x + gridSize * 2) / gridSize) * gridSize;
                  geo.y = Math.round((geo.y + gridSize * 2) / gridSize) * gridSize;
                  graph.getModel().setGeometry(clone, geo);
                }
                graph.getModel().add(parent, clone);
              });
            } finally {
              graph.getModel().endUpdate();
            }
            graph.setSelectionCells(clones);
            // Art arda paste için klonları yeni clipboard yap (kayarak yerleşir)
            clipboardRef.current = clones.filter(c => graph.getModel().isVertex(c));
          } catch (err) {
            console.warn('Paste hatası:', err);
          }
        }
      }
    };
    
    const handleKeyUp = (e) => {
      // Space keyUp için editör kontrolüne gerek yok — sadece Space'i işle
      // Editör fokuslu iken Space keyUp gelirse yine de pan modunu kapat
      try {
        const ae = document.activeElement;
        const isGlobalHotkeyIgnored = !!ae?.closest?.('[data-ignore-global-hotkeys="true"]');
        if (isGlobalHotkeyIgnored) return;
        const tag = ae?.tagName ? String(ae.tagName).toUpperCase() : '';
        const isEditorEl =
          tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
          !!ae?.isContentEditable ||
          !!ae?.closest?.('[data-ignore-global-hotkeys="true"]') ||
          !!ae?.closest?.('.monaco-editor') ||
          !!ae?.closest?.('.monaco-inputbox');
        // keyUp'ta sadece Space önemli — editör fokuslu iken diğer keyUp'lar editöre bırak
        if (isEditorEl && e.code !== 'Space') return;
      } catch (_e) { /* ignore */ }
      
      // Space tuşu bırakıldığında pan modunu kapat
      const isSpacePanActive = !!(graphRef.current && graphRef.current.container && graphRef.current.container.dataset.spacePan === 'true');
      if (e.code === 'Space' && isSpacePanActive) {
        e.preventDefault();
        setIsSpacePressed(false);
        if (graphRef.current && graphRef.current.panningHandler) {
          graphRef.current.panningHandler.useLeftButtonForPanning = false;
          graphRef.current.panningHandler.ignoreCell = false;
        }
        if (graphRef.current && graphRef.current.container) {
          // Space pan modunu kaldır
          delete graphRef.current.container.dataset.spacePan;
          graphRef.current.container.style.cursor = 'default';
          if (graphRef.current.view && graphRef.current.view.canvas) {
            graphRef.current.view.canvas.style.cursor = 'default';
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.body.style.overflow = 'auto';
    };
  }, [showCodeEditor, onClose, isDesignMode, setIsSpacePressed]);

  // ── mxRubberband seçim dikdörtgeni görünür hale getir ──
  // mxGraph inline style'ı her defasında sıfırlayabilir — !important ile override.
  useEffect(() => {
    const STYLE_ID = 'mxRubberband-override';
    if (!document.getElementById(STYLE_ID)) {
      const styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      styleEl.textContent = `
        .mxRubberband {
          position: absolute !important;
          z-index: 99999 !important;
          border: 1.5px solid rgba(59, 130, 246, 0.9) !important;
          background: rgba(59, 130, 246, 0.12) !important;
          border-radius: 2px !important;
          pointer-events: none !important;
          box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.3) !important;
        }
      `;
      document.head.appendChild(styleEl);
    }
    return () => {
      const el = document.getElementById('mxRubberband-override');
      if (el) el.remove();
    };
  }, []);

  // ── Mac trackpad geri/ileri navigation'ını KALICI olarak engelle ──
  // 2 katmanlı koruma (eskiden 3'tü — wheel handler kaldırıldı).
  // Global `{ passive: false, capture: true }` wheel handler Chrome'un threaded scrolling
  // optimizasyonunu devre dışı bırakıyor ve her scroll'u main thread'e düşürüyordu.
  // Bu TÜM scroll hareketlerinde belirgin takılmaya sebep oluyordu.
  // CSS `overscroll-behavior-x:none` yatay bounce/nav gesture'ını zaten render
  // seviyesinde kesiyor, bu yüzden JS handler'ı gereksiz.
  useEffect(() => {
    const htmlEl = document.documentElement;

    // KATMAN 1 — CSS: overscroll-behavior-x:none
    htmlEl.style.overscrollBehaviorX = 'none';
    document.body.style.overscrollBehaviorX = 'none';

    // KATMAN 2 — History API guard buffer (hızlı swipe'a karşı ek güvence)
    const GUARD_DEPTH = 8;
    const guardState = { _flowchartNavGuard: true };
    let isUnmounted = false;

    const pushGuards = () => {
      for (let i = 0; i < GUARD_DEPTH; i++) {
        window.history.pushState(guardState, '');
      }
    };
    pushGuards();

    const handlePopState = () => {
      if (isUnmounted) return;
      pushGuards();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      isUnmounted = true;
      window.removeEventListener('popstate', handlePopState);
      htmlEl.style.overscrollBehaviorX = '';
      document.body.style.overscrollBehaviorX = '';
      try { window.history.replaceState(null, ''); } catch (_) {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========== RESIZE HANDLER ==========
  useEffect(() => {
    if (!isResizing) return;
    
    // mxGraph container'ını tamamen devre dışı bırak
    if (graphContainerRef.current) {
      graphContainerRef.current.style.pointerEvents = 'none';
    }
    
    // mxGraph'in panningHandler'ını devre dışı bırak
    let originalUseLeftButtonForPanning = null;
    let originalIgnoreCell = null;
    if (graphRef.current && graphRef.current.panningHandler) {
      originalUseLeftButtonForPanning = graphRef.current.panningHandler.useLeftButtonForPanning;
      originalIgnoreCell = graphRef.current.panningHandler.ignoreCell;
      graphRef.current.panningHandler.useLeftButtonForPanning = false;
      graphRef.current.panningHandler.ignoreCell = true;
    }
    
    const handleMouseMove = (e) => {
      // Mouse'un başlangıç pozisyonundan ne kadar hareket ettiğini hesapla
      const deltaX = resizeStartXRef.current - e.clientX; // Sola giderse pozitif, sağa giderse negatif
      
      // Window genişliğini al
      const windowWidth = window.innerWidth;
      
      // Delta'yı yüzdeye çevir (sola giderse code panel genişler, sağa giderse küçülür)
      const deltaPercent = (deltaX / windowWidth) * 100;
      
      // Yeni genişliği hesapla
      const newWidth = resizeStartWidthRef.current + deltaPercent;
      
      // Sınırlar: min 30%, max 70%
      const clampedWidth = Math.max(30, Math.min(70, newWidth));
      setCodePanelWidth(clampedWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      
      // mxGraph container'ını tekrar etkinleştir
      if (graphContainerRef.current) {
        graphContainerRef.current.style.pointerEvents = 'auto';
      }
      
      // mxGraph'in panningHandler'ını geri yükle
      if (graphRef.current && graphRef.current.panningHandler) {
        if (originalUseLeftButtonForPanning !== null) {
          graphRef.current.panningHandler.useLeftButtonForPanning = originalUseLeftButtonForPanning;
        }
        if (originalIgnoreCell !== null) {
          graphRef.current.panningHandler.ignoreCell = originalIgnoreCell;
        }
      }
    };
    
    // Window'a event listener ekle - mxGraph'in event'lerinden önce yakala
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      // Cleanup - mxGraph'i geri yükle
      if (graphContainerRef.current) {
        graphContainerRef.current.style.pointerEvents = 'auto';
      }
      if (graphRef.current && graphRef.current.panningHandler) {
        if (originalUseLeftButtonForPanning !== null) {
          graphRef.current.panningHandler.useLeftButtonForPanning = originalUseLeftButtonForPanning;
        }
        if (originalIgnoreCell !== null) {
          graphRef.current.panningHandler.ignoreCell = originalIgnoreCell;
        }
      }
    };
  }, [isResizing]);

  // ========== RENDER ==========
  const modalContent = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#1e1e1e',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 99999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      overscrollBehavior: 'none',
      touchAction: 'none',
    }}>
      <FlowChartToolbar
        workflowData={workflowData}
        isDesignMode={isDesignMode}
        setIsDesignMode={setIsDesignMode}
        showCodeEditor={showCodeEditor}
        setShowCodeEditor={setShowCodeEditor}
        selectedShape={selectedShape}
        onBulkUpdateScriptsFromComments={handleBulkUpdateScriptsFromComments}
        isBulkUpdatingScriptsFromComments={isBulkAiGenerating || isBulkAiApplying}
        onCompileScriptsFromJsonPrompts={handleCompileScriptsFromJsonPrompts}
        isCompilingScriptsFromJsonPrompts={isCompileAiGenerating || isCompileAiApplying}
        onGenerateFlowFromScenario={handleOpenScenarioFlowModal}
        isGeneratingFlowFromScenario={isScenarioFlowGenerating}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        onSave={handleSave}
        isSaving={isSaving}
        onFetchFxGroups={fetchFxGroups}
        isLoadingFxGroups={isLoadingFxGroups}
        onMoveRequest={handleMoveRequest}
        onClose={onClose}
        showTerminal={showTerminal}
        onToggleTerminal={() => setShowTerminal((prev) => !prev)}
        shapes={shapes}
        addShape={addShape}
        onCreateCmd={handleCreateCmd}
        selectedShapeColor={selectedShapeColor}
        onChangeShapeColor={handleChangeShapeColor}
      />

      {showScenarioFlowModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100004,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isScenarioFlowGenerating) {
              setShowScenarioFlowModal(false);
            }
          }}
        >
          <div
            style={{
              width: '92%',
              maxWidth: 980,
              maxHeight: '86vh',
              background: '#2d2d2d',
              border: '1px solid #3e3e42',
              borderRadius: 10,
              boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid #3e3e42',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                  AI: Senaryodan Flowchart Uret
                </div>
                <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                  Senaryoyu semantic graph'a cevirir, dogrular, gerekirse auto-repair yapar ve mxGraph'e uygular.
                </div>
              </div>
              <button
                onClick={() => setShowScenarioFlowModal(false)}
                disabled={isScenarioFlowGenerating}
                style={{
                  background: 'transparent',
                  border: '1px solid #3e3e42',
                  color: '#cccccc',
                  padding: '6px 10px',
                  borderRadius: 8,
                  cursor: isScenarioFlowGenerating ? 'not-allowed' : 'pointer',
                  opacity: isScenarioFlowGenerating ? 0.6 : 1,
                }}
                title="Kapat"
              >
                Kapat
              </button>
            </div>

            <div style={{ padding: 14, display: 'grid', gap: 10 }}>
              <div style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600 }}>
                Senaryo
              </div>
              <textarea
                value={scenarioFlowInput}
                onChange={(e) => setScenarioFlowInput(e.target.value)}
                data-ignore-global-hotkeys="true"
                placeholder="Ornek: Baslangicta kullanici siparis numarasi girer. Sistem siparis var mi kontrol eder. Varsa stok kontrolu yapar; stok yeterliyse onay ekranina, degilse tedarik surecine gider..."
                style={{
                  width: '100%',
                  minHeight: 220,
                  maxHeight: '48vh',
                  padding: 12,
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: 8,
                  color: '#c9d1d9',
                  fontSize: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isScenarioFlowGenerating) {
                    e.preventDefault();
                    handleGenerateFlowFromScenario();
                  }
                }}
              />
              {scenarioFlowError && (
                <div
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    background: 'rgba(231,76,60,0.12)',
                    border: '1px solid rgba(231,76,60,0.35)',
                    color: 'rgba(255,255,255,0.92)',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {scenarioFlowError}
                </div>
              )}
              <div style={{ color: '#8b949e', fontSize: 11 }}>
                Ipucu: Ctrl/Cmd + Enter ile generate edebilirsin.
              </div>
            </div>

            <div
              style={{
                padding: 12,
                borderTop: '1px solid #3e3e42',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <button
                onClick={handleGenerateFlowFromScenario}
                disabled={isScenarioFlowGenerating || !scenarioFlowInput.trim()}
                style={{
                  background: '#7c3aed',
                  border: 'none',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: 8,
                  cursor: isScenarioFlowGenerating || !scenarioFlowInput.trim() ? 'not-allowed' : 'pointer',
                  opacity: isScenarioFlowGenerating || !scenarioFlowInput.trim() ? 0.6 : 1,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {isScenarioFlowGenerating ? 'Uretiliyor...' : 'Generate + Uygula'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkAiModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100003,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isBulkAiGenerating && !isBulkAiApplying) {
              setShowBulkAiModal(false);
            }
          }}
        >
          <div
            style={{
              width: '94%',
              maxWidth: 1100,
              maxHeight: '86vh',
              background: '#2d2d2d',
              border: '1px solid #3e3e42',
              borderRadius: 10,
              boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '14px 16px',
                borderBottom: '1px solid #3e3e42',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>
                  AI: Yorum satırlarına göre script güncelle
                </div>
                <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                  Bulunan şekil: {bulkAiItems.length} · Hazır: {bulkAiItems.filter((x) => x.status === 'ready').length} ·
                  Hatasız: {bulkAiItems.filter((x) => x.status !== 'error').length}
                </div>
              </div>
              <button
                onClick={() => setShowBulkAiModal(false)}
                disabled={isBulkAiGenerating || isBulkAiApplying}
                style={{
                  background: 'transparent',
                  border: '1px solid #3e3e42',
                  color: '#cccccc',
                  padding: '6px 10px',
                  borderRadius: 8,
                  cursor: isBulkAiGenerating || isBulkAiApplying ? 'not-allowed' : 'pointer',
                  opacity: isBulkAiGenerating || isBulkAiApplying ? 0.6 : 1,
                }}
                title="Kapat"
              >
                Kapat
              </button>
            </div>

            {bulkAiError && (
              <div
                style={{
                  padding: 12,
                  margin: 12,
                  borderRadius: 10,
                  background: 'rgba(231,76,60,0.12)',
                  border: '1px solid rgba(231,76,60,0.35)',
                  color: 'rgba(255,255,255,0.92)',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {bulkAiError}
              </div>
            )}

            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {bulkAiItems.length === 0 ? (
                <div style={{ padding: 18, color: '#9aa0a6', fontSize: 13 }}>Kayıt yok.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {bulkAiItems.map((item) => {
                    const expanded = !!bulkAiExpanded[item.shapeId];
                    const checked = bulkAiSelected[item.shapeId] !== false;
                    const statusColor =
                      item.status === 'ready'
                        ? '#22c55e'
                        : item.status === 'saved'
                          ? '#60a5fa'
                          : item.status === 'error'
                            ? '#ef4444'
                            : item.status === 'running' || item.status === 'saving'
                              ? '#f59e0b'
                              : '#9aa0a6';
                    return (
                      <div
                        key={item.shapeId}
                        style={{
                          border: '1px solid #3e3e42',
                          borderRadius: 10,
                          background: '#1e1e1e',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderBottom: expanded ? '1px solid #2a2a2a' : 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isBulkAiGenerating || isBulkAiApplying}
                            onChange={(e) =>
                              setBulkAiSelected((prev) => ({ ...prev, [item.shapeId]: e.target.checked }))
                            }
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                color: '#e6e6e6',
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.label}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>({item.shapeId})</span>
                              <span
                                style={{
                                  color: statusColor,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                }}
                              >
                                {item.status}
                              </span>
                              <span style={{ color: '#9aa0a6', fontSize: 11 }}>lang: {item.lang}</span>
                            </div>
                            {!!item.instructions?.length && (
                              <div style={{ color: '#9aa0a6', fontSize: 11, marginTop: 4 }}>
                                {item.instructions.slice(0, 2).join(' · ')}
                                {item.instructions.length > 2 ? ' …' : ''}
                              </div>
                            )}
                            {item.error && (
                              <div style={{ color: '#ff8a8a', fontSize: 11, marginTop: 4 }}>{item.error}</div>
                            )}
                          </div>
                          <button
                            onClick={() =>
                              setBulkAiExpanded((prev) => ({ ...prev, [item.shapeId]: !prev[item.shapeId] }))
                            }
                            style={{
                              background: 'transparent',
                              border: '1px solid #3e3e42',
                              color: '#cccccc',
                              padding: '6px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 12,
                            }}
                          >
                            {expanded ? 'Gizle' : 'Detay'}
                          </button>
                        </div>

                        {expanded && (
                          <div style={{ padding: 12, display: 'grid', gap: 10 }}>
                            <div style={{ color: '#cccccc', fontSize: 12, fontWeight: 700 }}>Talimatlar</div>
                            <div
                              style={{
                                background: '#0d1117',
                                border: '1px solid #2a2a2a',
                                borderRadius: 8,
                                padding: 10,
                                color: '#c9d1d9',
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {(item.instructions || []).map((x) => `- ${x}`).join('\n') || '—'}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Eski</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.oldCode || ''}
                                </pre>
                              </div>
                              <div>
                                <div style={{ color: '#9aa0a6', fontSize: 11, marginBottom: 6 }}>Yeni</div>
                                <pre
                                  style={{
                                    margin: 0,
                                    background: '#0d1117',
                                    border: '1px solid #2a2a2a',
                                    borderRadius: 8,
                                    padding: 10,
                                    color: '#c9d1d9',
                                    fontSize: 11,
                                    overflow: 'auto',
                                    maxHeight: 240,
                                  }}
                                >
                                  {item.newCode || ''}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              style={{
                padding: 12,
                borderTop: '1px solid #3e3e42',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ color: '#9aa0a6', fontSize: 11 }}>
                Not: “ready” durumundakiler değişiklik üretti. “nochange” kodu aynı döndürdü.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleApplyBulkAiUpdates}
                  disabled={isBulkAiGenerating || isBulkAiApplying}
                  style={{
                    background: '#0e639c',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: isBulkAiGenerating || isBulkAiApplying ? 'not-allowed' : 'pointer',
                    opacity: isBulkAiGenerating || isBulkAiApplying ? 0.6 : 1,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                  title="Seçili satırları uygular ve kaydeder"
                >
                  {isBulkAiApplying ? 'Kaydediliyor...' : 'Uygula + Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCompileAiModal && (() => {
        /* ── helpers ─────────────────────────────────────────────────── */
        const computeLineDiff = (oldStr, newStr) => {
          const a = (oldStr || '').split('\n');
          const b = (newStr || '').split('\n');
          const m = a.length, n = b.length;
          if (m > 800 || n > 800) {
            return [...a.map(l => ({ type: 'remove', line: l })), ...b.map(l => ({ type: 'add', line: l }))];
          }
          const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
          for (let i = 1; i <= m; i++)
            for (let j = 1; j <= n; j++)
              dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
          const diff = [];
          let i = m, j = n;
          while (i > 0 || j > 0) {
            if (i > 0 && j > 0 && a[i-1] === b[j-1]) { diff.unshift({ type: 'equal',  line: a[i-1] }); i--; j--; }
            else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { diff.unshift({ type: 'add',    line: b[j-1] }); j--; }
            else { diff.unshift({ type: 'remove', line: a[i-1] }); i--; }
          }
          return diff;
        };

        const copyCode = (text, id) => {
          navigator.clipboard?.writeText(text).then(() => {
            setCompileAiCopiedId(id);
            setTimeout(() => setCompileAiCopiedId(null), 1800);
          });
        };

        const CodeBlock = ({ code, blockId, lang, highlightType }) => {
          const copied = compileAiCopiedId === blockId;
          const borderColor = highlightType === 'old' ? 'rgba(239,68,68,0.45)' : highlightType === 'new' ? 'rgba(34,197,94,0.45)' : '#21262d';
          const headerBg   = highlightType === 'old' ? 'rgba(239,68,68,0.08)' : highlightType === 'new' ? 'rgba(34,197,94,0.08)' : '#161b22';
          return (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: headerBg, padding: '5px 12px', borderBottom: `1px solid ${borderColor}` }}>
                <span style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace', userSelect: 'none' }}>{lang || 'php'}</span>
                <button
                  onClick={() => copyCode(code || '', blockId)}
                  style={{ background: copied ? 'rgba(34,197,94,0.15)' : 'transparent', border: `1px solid ${copied ? '#22c55e55' : '#30363d'}`, borderRadius: 5, padding: '2px 9px', cursor: 'pointer', color: copied ? '#4ade80' : '#8b949e', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s' }}
                >
                  {copied
                    ? <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8"><polyline points="20 6 9 17 4 12"/></svg>Kopyalandı</>
                    : <><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Kopyala</>
                  }
                </button>
              </div>
              <pre style={{ margin: 0, background: '#0d1117', padding: '12px 14px', color: '#c9d1d9', fontSize: 11.5, lineHeight: 1.65, overflow: 'auto', maxHeight: 440, fontFamily: "'JetBrains Mono','Fira Code',Consolas,monospace", tabSize: 2, whiteSpace: 'pre' }}>
                {code || '// (boş)'}
              </pre>
            </div>
          );
        };

        const DiffView = ({ oldCode, newCode, lang, diffId }) => {
          const diff = computeLineDiff(oldCode, newCode);
          const addCount    = diff.filter(d => d.type === 'add').length;
          const removeCount = diff.filter(d => d.type === 'remove').length;
          const diffText    = diff.map(d => (d.type === 'add' ? '+' : d.type === 'remove' ? '-' : ' ') + d.line).join('\n');
          const copyDiffId  = 'diff-copy-' + diffId;
          return (
            <div style={{ border: '1px solid #21262d', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#161b22', padding: '5px 12px', borderBottom: '1px solid #21262d' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace', userSelect: 'none' }}>{lang || 'php'} · unified diff</span>
                  <span style={{ fontSize: 10, color: '#4ade80', fontFamily: 'monospace' }}>+{addCount}</span>
                  <span style={{ fontSize: 10, color: '#f87171', fontFamily: 'monospace' }}>-{removeCount}</span>
                </div>
                <button
                  onClick={() => copyCode(diffText, copyDiffId)}
                  style={{ background: compileAiCopiedId === copyDiffId ? 'rgba(34,197,94,0.15)' : 'transparent', border: `1px solid ${compileAiCopiedId === copyDiffId ? '#22c55e55' : '#30363d'}`, borderRadius: 5, padding: '2px 9px', cursor: 'pointer', color: compileAiCopiedId === copyDiffId ? '#4ade80' : '#8b949e', fontSize: 10 }}
                >
                  {compileAiCopiedId === copyDiffId ? '✓ Kopyalandı' : 'Diff kopyala'}
                </button>
              </div>
              <div style={{ overflow: 'auto', maxHeight: 460, background: '#0d1117', fontFamily: "'JetBrains Mono','Fira Code',Consolas,monospace", fontSize: 11.5, lineHeight: 1.65 }}>
                {diff.length === 0 && (
                  <div style={{ padding: '16px 18px', color: '#7d8590', fontSize: 12 }}>Değişiklik yok — kod aynı.</div>
                )}
                {diff.map((chunk, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      background: chunk.type === 'add' ? 'rgba(46,160,67,0.10)' : chunk.type === 'remove' ? 'rgba(248,81,73,0.10)' : 'transparent',
                      borderLeft: `3px solid ${chunk.type === 'add' ? '#2ea043' : chunk.type === 'remove' ? '#f85149' : 'transparent'}`,
                    }}
                  >
                    <span style={{ width: 24, flexShrink: 0, textAlign: 'center', color: chunk.type === 'add' ? '#2ea043' : chunk.type === 'remove' ? '#f85149' : '#3e3e42', fontSize: 11, userSelect: 'none', paddingTop: 1 }}>
                      {chunk.type === 'add' ? '+' : chunk.type === 'remove' ? '−' : ' '}
                    </span>
                    <span style={{ paddingLeft: 6, paddingRight: 16, whiteSpace: 'pre', color: chunk.type === 'add' ? '#7ee787' : chunk.type === 'remove' ? '#ffa198' : '#c9d1d9', flex: 1 }}>
                      {chunk.line}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        };

        /* ── data ─────────────────────────────────────────────────────── */
        const itemsToShow = compileAiItems.filter(x => x.status !== 'cached');
        const activeItem  = itemsToShow.find(x => x.shapeId === compileAiActiveItemId) || itemsToShow[0] || null;

        const statusMeta = (status) => {
          if (status === 'ready')    return { color: '#2ea043', bg: 'rgba(46,160,67,0.13)',   label: 'Hazır' };
          if (status === 'saved')    return { color: '#388bfd', bg: 'rgba(56,139,253,0.13)',  label: 'Kaydedildi' };
          if (status === 'error')    return { color: '#f85149', bg: 'rgba(248,81,73,0.13)',   label: 'Hata' };
          if (status === 'running')  return { color: '#d29922', bg: 'rgba(210,153,34,0.13)',  label: 'Üretiliyor…' };
          if (status === 'saving')   return { color: '#d29922', bg: 'rgba(210,153,34,0.13)',  label: 'Kaydediliyor…' };
          if (status === 'nochange') return { color: '#8b949e', bg: 'rgba(139,148,158,0.10)', label: 'Değişmedi' };
          return { color: '#8b949e', bg: 'rgba(139,148,158,0.08)', label: status };
        };

        const readyCount   = compileAiItems.filter(x => x.status === 'ready').length;
        const runningCount = compileAiItems.filter(x => ['running','saving'].includes(x.status)).length;

        const TAB = (id, label, active) => (
          <button
            key={id}
            onClick={() => setCompileAiViewTab(id)}
            style={{
              padding: '6px 14px',
              border: 'none',
              borderBottom: active ? '2px solid #388bfd' : '2px solid transparent',
              background: 'transparent',
              color: active ? '#e6edf3' : '#7d8590',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: active ? 600 : 400,
              transition: 'color 0.12s',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        );

        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(1,4,9,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100003 }}
            onClick={(e) => { if (e.target === e.currentTarget && !isCompileAiGenerating && !isCompileAiApplying) setShowCompileAiModal(false); }}
          >
            <div
              style={{ width: '97vw', maxWidth: 1520, height: '94vh', background: '#0d1117', border: '1px solid #30363d', borderRadius: 12, boxShadow: '0 28px 100px rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* ── HEADER ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: '1px solid #21262d', background: '#161b22', flexShrink: 0, gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#818cf8 0%,#60a5fa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#e6edf3', fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>AI Compile</div>
                    <div style={{ color: '#7d8590', fontSize: 11, lineHeight: 1.3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span>{compileAiItems.length} adım</span>
                      {readyCount  > 0 && <span style={{ color: '#3fb950' }}>● {readyCount} hazır</span>}
                      {runningCount > 0 && <span style={{ color: '#e3b341' }}>● {runningCount} çalışıyor</span>}
                      {isCompileAiGenerating && <span style={{ color: '#e3b341' }}>üretiliyor…</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                  {!isCompileAiGenerating && itemsToShow.some(x => !['ready','saved'].includes(x.status)) && (
                    <button
                      onClick={handleStartCompileAiGeneration}
                      disabled={isCompileAiApplying}
                      style={{ background: '#238636', border: '1px solid #2ea043', color: '#fff', padding: '6px 18px', borderRadius: 6, cursor: isCompileAiApplying ? 'not-allowed' : 'pointer', opacity: isCompileAiApplying ? 0.5 : 1, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      Generate
                    </button>
                  )}
                  <button
                    onClick={handleApplyCompileAiUpdates}
                    disabled={isCompileAiGenerating || isCompileAiApplying || readyCount === 0}
                    style={{ background: '#1f6feb', border: '1px solid #388bfd', color: '#fff', padding: '6px 18px', borderRadius: 6, cursor: (isCompileAiGenerating || isCompileAiApplying || readyCount === 0) ? 'not-allowed' : 'pointer', opacity: (isCompileAiGenerating || isCompileAiApplying || readyCount === 0) ? 0.45 : 1, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    {isCompileAiApplying ? 'Kaydediliyor…' : `Uygula + Kaydet${readyCount > 0 ? ` (${readyCount})` : ''}`}
                  </button>
                  <button
                    onClick={() => setShowCompileAiModal(false)}
                    disabled={isCompileAiGenerating || isCompileAiApplying}
                    style={{ background: 'transparent', border: '1px solid #30363d', color: '#7d8590', padding: '6px 11px', borderRadius: 6, cursor: (isCompileAiGenerating || isCompileAiApplying) ? 'not-allowed' : 'pointer', opacity: (isCompileAiGenerating || isCompileAiApplying) ? 0.45 : 1, fontSize: 14, lineHeight: 1 }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* ── ERROR BAR ── */}
              {compileAiError && (
                <div style={{ padding: '9px 18px', background: 'rgba(248,81,73,0.09)', borderBottom: '1px solid rgba(248,81,73,0.25)', color: '#ffa198', fontSize: 12, whiteSpace: 'pre-wrap', flexShrink: 0 }}>
                  {compileAiError}
                </div>
              )}

              {/* ── BODY ── */}
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

                {/* SIDEBAR */}
                <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #21262d', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#0d1117' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #21262d', flexShrink: 0 }}>
                    <div style={{ fontSize: 10, color: '#7d8590', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, fontWeight: 600 }}>Adımlar ({itemsToShow.length})</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setCompileAiSelected(Object.fromEntries(itemsToShow.map(x => [x.shapeId, true])))}
                        style={{ flex: 1, background: '#21262d', border: '1px solid #30363d', borderRadius: 5, color: '#8b949e', padding: '3px 0', cursor: 'pointer', fontSize: 10 }}
                      >Tümünü seç</button>
                      <button
                        onClick={() => setCompileAiSelected(Object.fromEntries(itemsToShow.map(x => [x.shapeId, false])))}
                        style={{ flex: 1, background: '#21262d', border: '1px solid #30363d', borderRadius: 5, color: '#8b949e', padding: '3px 0', cursor: 'pointer', fontSize: 10 }}
                      >Temizle</button>
                    </div>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                    {itemsToShow.length === 0 && (
                      <div style={{ padding: 20, color: '#7d8590', fontSize: 12, textAlign: 'center' }}>Gösterilecek adım yok</div>
                    )}
                    {itemsToShow.map((item) => {
                      const isActive = activeItem?.shapeId === item.shapeId;
                      const sm = statusMeta(item.status);
                      const checked = compileAiSelected[item.shapeId] !== false;
                      return (
                        <div
                          key={item.shapeId}
                          onClick={() => setCompileAiActiveItemId(item.shapeId)}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 10px', borderRadius: 8, marginBottom: 2, cursor: 'pointer', background: isActive ? '#161b22' : 'transparent', border: `1px solid ${isActive ? '#388bfd55' : 'transparent'}`, transition: 'background 0.1s, border 0.1s' }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isCompileAiGenerating || isCompileAiApplying}
                            onChange={(e) => { e.stopPropagation(); setCompileAiSelected(prev => ({ ...prev, [item.shapeId]: e.target.checked })); }}
                            style={{ marginTop: 3, flexShrink: 0, accentColor: '#388bfd', cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? '#e6edf3' : '#c9d1d9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.label || item.shapeId}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: sm.bg, color: sm.color, borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 600, letterSpacing: '0.02em' }}>
                                {['running','saving'].includes(item.status) && (
                                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: sm.color, display: 'inline-block', animation: 'pulse 0.9s infinite' }} />
                                )}
                                {sm.label}
                              </span>
                              <span style={{ color: '#7d8590', fontSize: 10 }}>{item.lang}</span>
                            </div>
                            {item.error && (
                              <div style={{ color: '#ffa198', fontSize: 10, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.error}</div>
                            )}
                            {item.aiPrompt && (
                              <div style={{ color: '#7d8590', fontSize: 10, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.aiPrompt}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* DETAIL PANEL */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                  {!activeItem ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#7d8590' }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#30363d" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17" strokeWidth="2.5"/></svg>
                      <span style={{ fontSize: 14 }}>Soldan bir adım seçin</span>
                    </div>
                  ) : (
                    <>
                      {/* detail header */}
                      <div style={{ padding: '6px 16px 0', borderBottom: '1px solid #21262d', background: '#161b22', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 6, gap: 12 }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ color: '#e6edf3', fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {activeItem.label}
                              <span style={{ color: '#7d8590', fontSize: 11, marginLeft: 8, fontWeight: 400 }}>({activeItem.shapeId})</span>
                              <span style={{ marginLeft: 10, ...statusMeta(activeItem.status), display: 'inline-flex', alignItems: 'center', gap: 4, background: statusMeta(activeItem.status).bg, padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                                {statusMeta(activeItem.status).label}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 0, flexShrink: 0 }}>
                            {TAB('diff',   '⟷ Diff',   compileAiViewTab === 'diff')}
                            {TAB('old',    '← Eski',   compileAiViewTab === 'old')}
                            {TAB('new',    '→ Yeni',   compileAiViewTab === 'new')}
                            {TAB('prompt', '✦ Prompt', compileAiViewTab === 'prompt')}
                          </div>
                        </div>
                      </div>

                      {/* detail content */}
                      <div style={{ flex: 1, overflowY: 'auto', padding: 18, minHeight: 0 }}>

                        {compileAiViewTab === 'prompt' && (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                              <span style={{ color: '#7d8590', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>ai_prompt</span>
                              <button
                                onClick={() => copyCode(activeItem.aiPrompt || '', 'prompt-' + activeItem.shapeId)}
                                style={{ background: 'transparent', border: '1px solid #30363d', borderRadius: 5, padding: '2px 9px', cursor: 'pointer', color: compileAiCopiedId === 'prompt-' + activeItem.shapeId ? '#4ade80' : '#7d8590', fontSize: 10 }}
                              >
                                {compileAiCopiedId === 'prompt-' + activeItem.shapeId ? '✓ Kopyalandı' : 'Kopyala'}
                              </button>
                            </div>
                            <div style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8, padding: '14px 16px', color: '#c9d1d9', fontSize: 13, lineHeight: 1.75, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                              {activeItem.aiPrompt || '— (boş)'}
                            </div>
                            {activeItem.error && (
                              <div style={{ marginTop: 14, background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.22)', borderRadius: 8, padding: '12px 14px', color: '#ffa198', fontSize: 12 }}>
                                <strong>Hata:</strong> {activeItem.error}
                              </div>
                            )}
                          </div>
                        )}

                        {compileAiViewTab === 'old' && (
                          <div>
                            <div style={{ color: '#7d8590', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Mevcut Kod (Eski)</div>
                            <CodeBlock code={activeItem.oldCode} blockId={'old-' + activeItem.shapeId} lang={activeItem.lang} highlightType="old" />
                          </div>
                        )}

                        {compileAiViewTab === 'new' && (
                          <div>
                            <div style={{ color: '#7d8590', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                              {['running','saving'].includes(activeItem.status) ? '⏳ Üretiliyor…' : 'Üretilen Kod (Yeni)'}
                            </div>
                            {activeItem.newCode ? (
                              <CodeBlock code={activeItem.newCode} blockId={'new-' + activeItem.shapeId} lang={activeItem.lang} highlightType="new" />
                            ) : (
                              <div style={{ padding: 32, textAlign: 'center', color: '#7d8590', fontSize: 13, border: '1px dashed #21262d', borderRadius: 8 }}>
                                {['running','saving'].includes(activeItem.status) ? 'Kod üretiliyor…' : 'Henüz üretilmedi. Generate çalıştırın.'}
                              </div>
                            )}
                          </div>
                        )}

                        {compileAiViewTab === 'diff' && (
                          <div>
                            {!activeItem.newCode ? (
                              <div style={{ padding: 32, textAlign: 'center', color: '#7d8590', fontSize: 13, border: '1px dashed #21262d', borderRadius: 8 }}>
                                {['running','saving'].includes(activeItem.status) ? '⏳ Kod üretiliyor…' : 'Diff için önce Generate çalıştırın.'}
                              </div>
                            ) : (
                              <>
                                {activeItem.aiPrompt && (
                                  <div style={{ marginBottom: 14, background: '#161b22', border: '1px solid #21262d', borderLeft: '3px solid #388bfd', borderRadius: 6, padding: '8px 14px' }}>
                                    <span style={{ color: '#7d8590', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Talimat: </span>
                                    <span style={{ color: '#adbac7', fontSize: 12, lineHeight: 1.5 }}>{String(activeItem.aiPrompt).slice(0, 260)}{activeItem.aiPrompt.length > 260 ? '…' : ''}</span>
                                  </div>
                                )}
                                <DiffView oldCode={activeItem.oldCode} newCode={activeItem.newCode} lang={activeItem.lang} diffId={activeItem.shapeId} />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
                                  <div>
                                    <div style={{ color: '#f85149', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>← Eski</div>
                                    <CodeBlock code={activeItem.oldCode} blockId={'sbs-old-' + activeItem.shapeId} lang={activeItem.lang} highlightType="old" />
                                  </div>
                                  <div>
                                    <div style={{ color: '#3fb950', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>→ Yeni</div>
                                    <CodeBlock code={activeItem.newCode} blockId={'sbs-new-' + activeItem.shapeId} lang={activeItem.lang} highlightType="new" />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                      </div>
                    </>
                  )}
                </div>

              </div>

              {/* ── FOOTER ── */}
              <div style={{ padding: '9px 18px', borderTop: '1px solid #21262d', background: '#161b22', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 }}>
                <div style={{ color: '#7d8590', fontSize: 11 }}>
                  Step'lerin <code style={{ background: '#21262d', borderRadius: 3, padding: '1px 5px', fontSize: 10 }}>ai_prompt</code> ve edge ilişkileri kullanılarak kod üretilir.
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: '#7d8590', fontSize: 11 }}>
                    {Object.values(compileAiSelected).filter(Boolean).length} adım seçili
                  </span>
                  <button
                    onClick={handleApplyCompileAiUpdates}
                    disabled={isCompileAiGenerating || isCompileAiApplying || readyCount === 0}
                    style={{ background: '#1f6feb', border: '1px solid #388bfd', color: '#fff', padding: '7px 22px', borderRadius: 6, cursor: (isCompileAiGenerating || isCompileAiApplying || readyCount === 0) ? 'not-allowed' : 'pointer', opacity: (isCompileAiGenerating || isCompileAiApplying || readyCount === 0) ? 0.4 : 1, fontSize: 12, fontWeight: 700 }}
                  >
                    {isCompileAiApplying ? 'Kaydediliyor…' : `Uygula + Kaydet${readyCount > 0 ? ` (${readyCount})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
        {/* Main Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <FlowChartSidebar
            isDesignMode={isDesignMode}
            requestFiles={requestFiles}
            fileSearchQuery={fileSearchQuery}
            setFileSearchQuery={setFileSearchQuery}
            selectedFileCategory={selectedFileCategory}
            setSelectedFileCategory={setSelectedFileCategory}
            tabs={tabs}
            activeTabId={activeTabId}
            handleFileClick={handleFileClick}
          />
          <FlowChartGraphArea
            showCodeEditor={showCodeEditor}
            codePanelWidth={codePanelWidth}
            graphContainerRef={graphContainerRef}
            gridIcon={gridIcon}
          />
          <FlowChartCodeEditorPanel
            showCodeEditor={showCodeEditor}
            codePanelWidth={codePanelWidth}
            isResizing={isResizing}
            setIsResizing={setIsResizing}
            resizeStartXRef={resizeStartXRef}
            resizeStartWidthRef={resizeStartWidthRef}
            setShowCodeEditor={setShowCodeEditor}
            selectedShape={codeEditorShapeId}
            selectedShapeLabel={codeEditorShapeLabel}
            code={code}
            handleCodeChange={handleCodeChange}
            handleEditorDidMount={handleEditorDidMount}
            aiStatus={aiStatus}
            pendingChange={pendingChange}
            onApplyPendingChange={handleApplyPendingChange}
            onDiscardPendingChange={handleDiscardPendingChange}
            isApplyingChange={isApplyingChange}
            currentPrompt={currentPrompt}
            onPromptChange={handlePromptChange}
            onRunShapeAI={handleRunShapeAI}
            isRunningShapeAI={isTerminalBusy}
            editorTabs={editorTabs}
            onTabActivate={handleTabActivate}
            onTabClose={handleCodeTabClose}
            onTabDetach={handleTabDetach}
          />
        </div>

        {/* Functions Modal */}
        {false && showFunctionsModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFunctionsModal(false);
              setFunctions([]);
              setFunctionsError(null);
              setSelectedFunction(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 800,
              maxHeight: '85vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowFunctionsModal(false);
                      setFunctions([]);
                      setFunctionsError(null);
                      setSelectedFunction(null);
                      setShowFxGroupsModal(true);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #3e3e42',
                      color: '#cccccc',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>←</span>
                    <span>Geri</span>
                  </button>
                  <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                    🔧 Functions - {selectedFxGroup}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowFunctionsModal(false);
                    setFunctions([]);
                    setFunctionsError(null);
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '0 4px',
              }}>
                {/* Loading State */}
                {isLoadingFunctions && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#8b5cf6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Functions yükleniyor...</div>
                  </div>
                )}
                
                {/* Error State */}
                {functionsError && (
                  <div style={{
                    padding: 20,
                    background: '#3a1f1f',
                    border: '1px solid #5a2a2a',
                    borderRadius: 4,
                    color: '#ff6b6b',
                    fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Hata:</div>
                    <div>{functionsError}</div>
                  </div>
                )}
                
                {/* Success State - Functions List */}
                {!isLoadingFunctions && !functionsError && functions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}>
                    {functions.map((func, index) => (
                      <div
                        key={index}
                        style={{
                          background: selectedFunction === func.fname ? '#3e3e42' : '#2d2d2d',
                          border: `1px solid ${selectedFunction === func.fname ? '#8b5cf6' : '#3e3e42'}`,
                          borderRadius: 4,
                          padding: '12px 16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                            onClick={() => {
                              fetchFunctionScript(func);
                            }}
                        onMouseEnter={(e) => {
                          if (selectedFunction !== func.fname) {
                            e.currentTarget.style.background = '#3e3e42';
                            e.currentTarget.style.borderColor = '#8b5cf6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedFunction !== func.fname) {
                            e.currentTarget.style.background = '#2d2d2d';
                            e.currentTarget.style.borderColor = '#3e3e42';
                          }
                        }}
                      >
                        <div style={{
                          color: '#cccccc',
                          fontSize: 13,
                          fontWeight: 500,
                          marginBottom: 4,
                        }}>
                          {func.fname}
                        </div>
                        {func.fdesc && (
                          <div style={{
                            color: '#888',
                            fontSize: 11,
                            fontStyle: 'italic',
                          }}>
                            {func.fdesc}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Empty State */}
                {!isLoadingFunctions && !functionsError && functions.length === 0 && (
                  <div style={{
                    padding: 20,
                    textAlign: 'center',
                    color: '#888',
                    fontSize: 13,
                  }}>
                    Functions bulunamadı.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  onClick={() => {
                    setShowFunctionsModal(false);
                    setFunctions([]);
                    setFunctionsError(null);
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast bildirimi */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 16px',
              borderRadius: 8,
              background: toast.type === 'success' ? '#14532d' : '#450a0a',
              border: `1px solid ${toast.type === 'success' ? '#16a34a' : '#dc2626'}`,
              color: toast.type === 'success' ? '#86efac' : '#fca5a5',
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              maxWidth: 420,
              animation: 'fadeIn 0.2s ease',
            }}
          >
            <span style={{ fontSize: 16, flexShrink: 0 }}>
              {toast.type === 'success' ? '✅' : '❌'}
            </span>
            <span>{toast.message}</span>
            {toast.details && (
              <button
                onClick={() => {
                  setDetailsModalData(toast.details);
                  setShowDetailsModal(true);
                  setToast(null);
                }}
                style={{
                  marginLeft: 4, padding: '2px 8px', background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4,
                  color: 'inherit', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >See Details</button>
            )}
            <button
              onClick={() => setToast(null)}
              style={{ marginLeft: 4, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', opacity: 0.6, fontSize: 16, lineHeight: 1, flexShrink: 0 }}
            >×</button>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && detailsModalData && createPortal(
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 999999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1e1e2e', border: '1px solid #313244', borderRadius: 10,
                width: 540, maxHeight: '75vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              <div style={{
                padding: '14px 18px', borderBottom: '1px solid #313244',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ color: '#cdd6f4', fontWeight: 600, fontSize: 13 }}>
                  {detailsModalData.title || 'Details'}
                </span>
                <button onClick={() => setShowDetailsModal(false)} style={{
                  background: 'none', border: 'none', color: '#6c7086', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                }}>×</button>
              </div>
              <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
                {/* Save log entries */}
                {detailsModalData.logEntries && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: '#a6adc8', fontSize: 10, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>İşlemler</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #313244' }}>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: '#a6adc8', fontWeight: 500 }}>Tür</th>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: '#a6adc8', fontWeight: 500 }}>Hedef</th>
                          <th style={{ textAlign: 'center', padding: '5px 8px', color: '#a6adc8', fontWeight: 500 }}>Durum</th>
                          <th style={{ textAlign: 'left', padding: '5px 8px', color: '#a6adc8', fontWeight: 500 }}>Detay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsModalData.logEntries.map((entry, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '4px 8px', color: entry.type === 'workflow' ? '#89b4fa' : entry.type === 'script' ? '#a6e3a1' : '#f38ba8' }}>
                              {entry.type === 'workflow' ? 'Flow' : entry.type === 'script' ? 'Script' : 'Hata'}
                            </td>
                            <td style={{ padding: '4px 8px', color: '#cdd6f4', fontFamily: "'Fira Mono', monospace", fontSize: 10 }}>
                              {entry.name === 'active' ? '(aktif tab)' : entry.name}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                              {entry.success ? <span style={{ color: '#a6e3a1' }}>✓</span> : <span style={{ color: '#f38ba8' }}>✗</span>}
                            </td>
                            <td style={{ padding: '4px 8px', color: entry.success ? '#6c7086' : '#f38ba8', fontSize: 10 }}>
                              {entry.success ? 'OK' : (entry.error || 'Hata')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Request/Response for Move Request etc. */}
                {detailsModalData.request && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: '#89b4fa', fontSize: 10, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>Request</div>
                    <pre style={{
                      margin: 0, padding: '8px 10px', background: '#181825', borderRadius: 6,
                      fontSize: 10, color: '#cdd6f4', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      fontFamily: "'Fira Mono', monospace", lineHeight: 1.5, border: '1px solid #313244',
                    }}>{JSON.stringify(detailsModalData.request, null, 2)}</pre>
                  </div>
                )}
                {detailsModalData.response && (
                  <div>
                    <div style={{ color: '#a6e3a1', fontSize: 10, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>Response</div>
                    <pre style={{
                      margin: 0, padding: '8px 10px', background: '#181825', borderRadius: 6,
                      fontSize: 10, color: '#cdd6f4', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                      fontFamily: "'Fira Mono', monospace", lineHeight: 1.5, border: '1px solid #313244',
                      maxHeight: 300, overflowY: 'auto',
                    }}>{JSON.stringify(detailsModalData.response, null, 2)}</pre>
                  </div>
                )}
              </div>
              <div style={{ padding: '10px 18px', borderTop: '1px solid #313244', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    padding: '6px 16px', background: '#313244', border: '1px solid #45475a',
                    borderRadius: 6, color: '#cdd6f4', fontSize: 11, cursor: 'pointer',
                  }}
                >Kapat</button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── Save Confirm / Preview Modal ── */}
        {showSaveConfirmModal && pendingSavePreview && createPortal(
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 999999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowSaveConfirmModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#111827',
                border: '1px solid #374151',
                borderRadius: 10,
                width: 920,
                maxWidth: '94vw',
                maxHeight: '82vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              }}
            >
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #374151',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ color: '#e5e7eb', fontWeight: 700, fontSize: 13 }}>
                  Save Önizleme · onayla ve kaydet
                </div>
                <button
                  onClick={() => setShowSaveConfirmModal(false)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 18 }}
                >×</button>
              </div>

              <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
                <div style={{
                  padding: 10, marginBottom: 12, borderRadius: 6,
                  background: '#0b1220', border: '1px solid #374151',
                  fontSize: 11, color: '#e5e7eb',
                  display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 14px',
                }}>
                  <div style={{ color: '#9ca3af' }}>Aktif File</div>
                  <div>
                    {pendingSavePreview.activeFileLabel || '-'}
                    <span style={{ color: '#6b7280' }}> ({String(pendingSavePreview.activeTabId || '-')})</span>
                  </div>
                  <div style={{ color: '#9ca3af' }}>Seçili Shape (design)</div>
                  <div>{String(pendingSavePreview.selectedShape || '-')}</div>
                  <div style={{ color: '#9ca3af' }}>Code Editor Shape</div>
                  <div>{String(pendingSavePreview.codeEditorShapeId || '-')}</div>
                  <div style={{ color: '#9ca3af' }}>Editor Script Uzunluğu</div>
                  <div>{pendingSavePreview.codeLength}</div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: '#93c5fd', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                    Editor Snapshot
                  </div>
                  <pre style={{
                    margin: 0, padding: '8px 10px', borderRadius: 6, border: '1px solid #374151',
                    background: '#0b1220', color: '#e5e7eb', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>{pendingSavePreview.currentEditorPreview || ''}</pre>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: '#a7f3d0', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                    Kaydedilecek Script Değişiklikleri ({pendingSavePreview.queuedScripts.length})
                  </div>
                  {pendingSavePreview.queuedScripts.length === 0 ? (
                    <div style={{ color: '#9ca3af', fontSize: 11 }}>Değişiklik algılanmadı.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #374151' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af' }}>File</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af' }}>Tab Id</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af' }}>Shape Id</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af' }}>Length</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#9ca3af' }}>Script Preview</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingSavePreview.queuedScripts.map((s, idx) => (
                          <tr key={`${s.tabId}-${s.shapeId}-${idx}`} style={{ borderBottom: '1px solid #1f2937' }}>
                            <td style={{ padding: '6px 8px', color: '#cbd5e1' }}>
                              {s.tabLabel || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', color: '#cbd5e1', fontFamily: "'Fira Mono', monospace" }}>{s.tabId}</td>
                            <td style={{ padding: '6px 8px', color: '#e5e7eb', fontFamily: "'Fira Mono', monospace" }}>{s.shapeId}</td>
                            <td style={{ padding: '6px 8px', color: '#e5e7eb' }}>{s.length}</td>
                            <td style={{ padding: '6px 8px', color: '#93c5fd', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.preview}>
                              {s.preview}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div>
                  <div style={{ color: '#fca5a5', fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase' }}>
                    Son Debug Logları ({pendingSavePreview.debugTail.length})
                  </div>
                  <pre style={{
                    margin: 0, padding: '8px 10px', borderRadius: 6, border: '1px solid #374151',
                    background: '#0b1220', color: '#e5e7eb', fontSize: 10, maxHeight: 220, overflowY: 'auto',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>{JSON.stringify(pendingSavePreview.debugTail, null, 2)}</pre>
                </div>
              </div>

              <div style={{
                padding: '10px 14px',
                borderTop: '1px solid #374151',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}>
                <button
                  onClick={() => {
                    pushDebugLog('save_preview_cancelled', {
                      queuedScriptsCount: pendingSavePreview.queuedScripts.length,
                    });
                    setShowSaveConfirmModal(false);
                  }}
                  style={{
                    background: '#1f2937', border: '1px solid #374151', color: '#d1d5db',
                    borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer',
                  }}
                >
                  İptal
                </button>
                <button
                  onClick={async () => {
                    const queuedCount = pendingSavePreview.queuedScripts.length;
                    setShowSaveConfirmModal(false);
                    pushDebugLog('save_preview_confirmed', { queuedScriptsCount: queuedCount });
                    await handleSave({ showToastFeedback: true, force: true, source: 'confirm-modal' });
                  }}
                  style={{
                    background: '#2563eb', border: '1px solid #1d4ed8', color: 'white',
                    borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  Onayla ve Kaydet
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ── Save Log Modal ── */}
        {showSaveLog && createPortal(
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 99999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            }}
            onClick={() => setShowSaveLog(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#1e1e2e', border: '1px solid #313244', borderRadius: 10,
                width: 520, maxHeight: '70vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              }}
            >
              <div style={{
                padding: '14px 18px', borderBottom: '1px solid #313244',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ color: '#cdd6f4', fontWeight: 600, fontSize: 13 }}>
                  Kaydetme Sonuçları
                </span>
                <button onClick={() => setShowSaveLog(false)} style={{
                  background: 'none', border: 'none', color: '#6c7086', cursor: 'pointer', fontSize: 18, lineHeight: 1,
                }}>×</button>
              </div>
              <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1 }}>
                {saveLogEntries.length === 0 ? (
                  <div style={{ color: '#6c7086', fontSize: 12, textAlign: 'center', padding: 20 }}>
                    Kayıt yok
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #313244' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#a6adc8', fontWeight: 500 }}>Tür</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#a6adc8', fontWeight: 500 }}>Hedef</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', color: '#a6adc8', fontWeight: 500 }}>Durum</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#a6adc8', fontWeight: 500 }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saveLogEntries.map((entry, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e1e2e' }}>
                          <td style={{ padding: '5px 8px', color: entry.type === 'workflow' ? '#89b4fa' : entry.type === 'script' ? '#a6e3a1' : '#f38ba8' }}>
                            {entry.type === 'workflow' ? '📂 Flow' : entry.type === 'script' ? '📝 Script' : '⚠️ Hata'}
                          </td>
                          <td style={{ padding: '5px 8px', color: '#cdd6f4', fontFamily: "'Fira Mono', monospace", fontSize: 10 }}>
                            {entry.name === 'active' ? '(aktif tab)' : entry.name}
                          </td>
                          <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                            {entry.success
                              ? <span style={{ color: '#a6e3a1', fontWeight: 600 }}>✓</span>
                              : <span style={{ color: '#f38ba8', fontWeight: 600 }}>✗</span>
                            }
                          </td>
                          <td style={{ padding: '5px 8px', color: entry.success ? '#6c7086' : '#f38ba8', fontSize: 10 }}>
                            {entry.success ? 'Başarılı' : (entry.error || 'Hata')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div style={{ marginTop: 12, padding: '8px 10px', background: '#181825', borderRadius: 6, fontSize: 10, color: '#6c7086' }}>
                  Toplam: {saveLogEntries.length} işlem — {saveLogEntries.filter(e => e.success).length} başarılı, {saveLogEntries.filter(e => !e.success).length} başarısız
                </div>
              </div>
              <div style={{ padding: '10px 18px', borderTop: '1px solid #313244', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowSaveLog(false)}
                  style={{
                    padding: '6px 16px', background: '#313244', border: '1px solid #45475a',
                    borderRadius: 6, color: '#cdd6f4', fontSize: 11, cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        <FlowChartModals
          showAIPrompt={showAIPrompt}
          setShowAIPrompt={setShowAIPrompt}
          aiPrompt={aiPrompt}
          setAiPrompt={setAiPrompt}
          aiMode={aiMode}
          setAiMode={setAiMode}
          selectedCode={selectedCode}
          setSelectedCode={setSelectedCode}
          handleGenerateCode={handleGenerateCode}
          isGeneratingCode={isGeneratingCode}
          aiStatus={aiStatus}
          showFunctionLibraryModal={showFunctionLibraryModal}
          setShowFunctionLibraryModal={setShowFunctionLibraryModal}
          functionLibrary={functionLibrary}
          setFunctionLibrary={setFunctionLibrary}
          isLoadingFunctionLibrary={isLoadingFunctionLibrary}
          functionLibraryError={functionLibraryError}
          setFunctionLibraryError={setFunctionLibraryError}
          showFxGroupsModal={showFxGroupsModal}
          setShowFxGroupsModal={setShowFxGroupsModal}
          fxGroups={fxGroups}
          setFxGroups={setFxGroups}
          fxGroupsError={fxGroupsError}
          setFxGroupsError={setFxGroupsError}
          selectedFxGroup={selectedFxGroup}
          setSelectedFxGroup={setSelectedFxGroup}
          functions={functions}
          setFunctions={setFunctions}
          isLoadingFxGroups={isLoadingFxGroups}
          isLoadingFunctions={isLoadingFunctions}
          functionsError={functionsError}
          setFunctionsError={setFunctionsError}
          selectedFunction={selectedFunction}
          setSelectedFunction={setSelectedFunction}
          showFunctionsModal={showFunctionsModal}
          setShowFunctionsModal={setShowFunctionsModal}
          fetchFunctions={fetchFunctions}
          fetchFunctionScript={fetchFunctionScript}
          showIncludeModal={showIncludeModal}
          setShowIncludeModal={setShowIncludeModal}
          selectedIncludeShape={selectedIncludeShape}
          setSelectedIncludeShape={setSelectedIncludeShape}
          includeFoldersData={includeFoldersData}
          setIncludeFoldersData={setIncludeFoldersData}
          isLoadingIncludeData={isLoadingIncludeData}
          includeError={includeError}
          setIncludeError={setIncludeError}
          selectedIncludePath={selectedIncludePath}
          setSelectedIncludePath={setSelectedIncludePath}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          includeFilesData={includeFilesData}
          setIncludeFilesData={setIncludeFilesData}
          isLoadingFiles={isLoadingFiles}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          activeTabId={activeTabId}
          setTabs={setTabs}
          workflowDataRef={workflowDataRef}
          graphRef={graphRef}
          createAttachIcon={createAttachIcon}
          mxCellOverlay={mxCellOverlay}
          mxConstants={mxConstants}
          mxPoint={mxPoint}
          mxEvent={mxEvent}
          openIncludeDetails={openIncludeDetails}
          shapeIncludePaths={shapeIncludePaths}
          setShapeIncludePaths={setShapeIncludePaths}
          shapeIncludeFolders={shapeIncludeFolders}
          setShapeIncludeFolders={setShapeIncludeFolders}
          shapeOriginalLabels={shapeOriginalLabels}
          setShapeOriginalLabels={setShapeOriginalLabels}
          shapesWithInclude={shapesWithInclude}
          setShapesWithInclude={setShapesWithInclude}
          fetchWrapper={fetchWrapper}
          navigate={navigate}
          showIncludeInfoModal={showIncludeInfoModal}
          setShowIncludeInfoModal={setShowIncludeInfoModal}
          includeInfoData={includeInfoData}
          setIncludeInfoData={setIncludeInfoData}
        />
        <FlowChartFunctionWindows
          functionWindows={functionWindows}
          setFunctionWindows={setFunctionWindows}
          draggingWindowId={draggingWindowId}
          setDraggingWindowId={setDraggingWindowId}
          setDragOffset={setDragOffset}
          setResizingWindowId={setResizingWindowId}
          setResizeStart={setResizeStart}
          setResizeDirection={setResizeDirection}
          saveFunctionWindow={saveFunctionWindow}
          closeFunctionWindow={closeFunctionWindow}
          toggleMinimizeWindow={toggleMinimizeWindow}
          functionWindowEditorRefs={functionWindowEditorRefs}
        />
        <FlowChartPromptWindows
          promptWindows={promptWindows}
          setPromptWindows={setPromptWindows}
          draggingWindowId={draggingPromptWindowId}
          setDraggingWindowId={setDraggingPromptWindowId}
          setDragOffset={setPromptDragOffset}
          setResizingWindowId={setResizingPromptWindowId}
          setResizeStart={setPromptResizeStart}
          setResizeDirection={setPromptResizeDirection}
          closePromptWindow={closePromptWindow}
          toggleMinimizeWindow={toggleMinimizePromptWindow}
          onSavePromptWindow={savePromptWindow}
          onChangePrompt={onChangePromptWindow}
        />

        {/* Function Script Modal - DEPRECATED - Draggable windows kullanılıyor */}
        {false && showFunctionScriptModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100003,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFunctionScriptModal(false);
              setFunctionScript('');
              setSelectedFunction(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 1200,
              height: '85vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowFunctionScriptModal(false);
                      setFunctionScript('');
                      setShowFunctionsModal(true);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid #3e3e42',
                      color: '#cccccc',
                      padding: '6px 12px',
                      borderRadius: 4,
                      fontSize: 12,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>←</span>
                    <span>Geri</span>
                  </button>
                  <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                    📝 Function Script - {selectedFunction}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowFunctionScriptModal(false);
                    setFunctionScript('');
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflow: 'hidden',
                background: '#1e1e1e',
                borderRadius: 4,
                border: '1px solid #3e3e42',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}>
                {/* Loading State */}
                {isLoadingFunctionScript && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                    height: '100%',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#8b5cf6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Function script yükleniyor...</div>
                  </div>
                )}
                
                {/* Editor */}
                {!isLoadingFunctionScript && (
                  <div style={{ 
                    flex: 1, 
                    minHeight: 0, 
                    width: '100%', 
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <Editor
                      height="100%"
                      width="100%"
                      defaultLanguage="php"
                      value={functionScript}
                      theme="vs-dark"
                      options={{
                        readOnly: true,
                        fontSize: 13,
                        minimap: { enabled: true },
                        wordWrap: 'on',
                        automaticLayout: true,
                        lineNumbers: 'on',
                        roundedSelection: true,
                        cursorStyle: 'line',
                        folding: true,
                        scrollBeyondLastLine: true,
                        renderWhitespace: 'selection',
                        tabSize: 4,
                      }}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexShrink: 0 }}>
                <button
                  onClick={() => {
                    setShowFunctionScriptModal(false);
                    setFunctionScript('');
                    setSelectedFunction(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Include Modal */}
        {false && showIncludeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100001,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowIncludeModal(false);
              setSelectedIncludeShape(null);
              setSelectedIncludePath(null);
              setSelectedFolder(null);
              setIncludeFilesData(null);
              setSelectedFile(null);
              setIncludeFoldersData(null);
              setIncludeError(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 24,
              width: '90%',
              maxWidth: 800,
              maxHeight: '80vh',
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}>
                <h3 style={{ color: '#cccccc', margin: 0, fontSize: 18, fontWeight: 600 }}>
                  🔗 Add Include
                </h3>
                <button
                  onClick={() => {
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                    setSelectedIncludePath(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setIncludeFoldersData(null);
                    setIncludeError(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 24,
                    cursor: 'pointer',
                    padding: 0,
                    width: 30,
                    height: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                flex: 1,
                overflow: 'auto',
                background: '#1e1e1e',
                borderRadius: 4,
                padding: 16,
                marginBottom: 16,
              }}>
                <div style={{
                  color: '#888',
                  fontSize: 13,
                  marginBottom: 12,
                }}>
                  Seçili Şekil: {selectedIncludeShape}
                </div>
                
                {/* Loading State */}
                {isLoadingIncludeData && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px 20px',
                    color: '#888',
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid #3e3e42',
                      borderTopColor: '#0e639c',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16,
                    }} />
                    <div style={{ fontSize: 13 }}>Folder ve file listesi yükleniyor...</div>
                  </div>
                )}
                
                {/* Error State */}
                {includeError && (
                  <div style={{
                    padding: 20,
                    background: '#5a1e1e',
                    borderRadius: 4,
                    color: '#ff7070',
                    fontSize: 13,
                    marginBottom: 16,
                  }}>
                    ❌ {includeError}
                  </div>
                )}
                
                {/* Folder Listesi - result array'inden folderId ve folderName kullanarak */}
                {!isLoadingIncludeData && !includeError && includeFoldersData && !selectedFolder && (
                <div style={{
                  color: '#cccccc',
                  fontSize: 14,
                }}>
                    {(() => {
                      try {
                        // Response'dan gelen data'yı kontrol et
                        let foldersList = [];
                        
                        // Eğer direkt array ise (result array'i)
                        if (Array.isArray(includeFoldersData)) {
                          foldersList = includeFoldersData;
                        }
                        // Eğer object ise ve result key'i varsa
                        else if (includeFoldersData.result && Array.isArray(includeFoldersData.result)) {
                          foldersList = includeFoldersData.result;
                        }
                        // Eğer object ise ve data key'i varsa
                        else if (includeFoldersData.data && Array.isArray(includeFoldersData.data)) {
                          foldersList = includeFoldersData.data;
                        }
                        // Eğer string ise parse et
                        else if (typeof includeFoldersData === 'string') {
                          try {
                            const parsed = JSON.parse(includeFoldersData);
                            if (Array.isArray(parsed)) {
                              foldersList = parsed;
                            } else if (parsed.result && Array.isArray(parsed.result)) {
                              foldersList = parsed.result;
                            }
                          } catch (e) {
                            return (
                  <div style={{
                                padding: 12,
                                background: '#2d2d2d',
                                borderRadius: 4,
                                color: '#cccccc',
                                fontSize: 12,
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                  }}>
                                {includeFoldersData}
                  </div>
                            );
                          }
                        }
                        
                        // Folder listesi boşsa
                        if (foldersList.length === 0) {
                          return (
                  <div style={{
                              padding: 20,
                              textAlign: 'center',
                              color: '#888',
                              fontSize: 13,
                            }}>
                              Folder bulunamadı.
                            </div>
                          );
                        }
                        
                        // Folder listesini render et
                        return (
                          <div style={{
                            display: 'grid',
                            gap: 8,
                          }}>
                            {foldersList.map((folder, index) => {
                              // folderId ve folderName kullan
                              const folderId = folder.folderId || folder.id || String(index);
                              const folderName = folder.folderName || folder.name || 'Unnamed';
                              const isSelected = selectedIncludePath === folderId;
                              
                              return (
                                <div
                                  key={folderId}
                                  onClick={() => {
                                    // Folder seçildiğinde folder bilgisini kaydet ve file listesini getir
                                    setSelectedIncludePath(folderId);
                                    setSelectedFolder(folder);
                                    setSelectedFile(null); // File seçimini temizle
                                  }}
                                  style={{
                                    padding: '12px 16px',
                                    background: isSelected ? '#0e639c' : 'transparent',
                                    border: isSelected ? '2px solid #3e9fdb' : '1px solid #3e3e42',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = '#3e3e42';
                                      e.currentTarget.style.borderColor = '#555';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.borderColor = '#3e3e42';
                                    }
                                  }}
                                >
                                  <div style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 6,
                                    background: isSelected ? '#3e9fdb' : '#3e3e42',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 20,
                                    flexShrink: 0,
                                  }}>
                                    📁
                                  </div>
                                  <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 4,
                                  }}>
                                    <div style={{
                                      fontSize: 15,
                                      fontWeight: 600,
                                      color: '#cccccc',
                                    }}>
                                      {folderName}
                                    </div>
                                    <div style={{
                    fontSize: 12,
                                      color: '#888',
                                      fontFamily: 'monospace',
                  }}>
                                      ID: {folderId}
                  </div>
                </div>
                                  {isSelected && (
                                    <div style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      background: '#4caf50',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}>
                                      <span style={{ fontSize: 14, color: 'white' }}>✓</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      } catch (error) {
                        console.error('Include data render hatası:', error);
                        return (
                          <div style={{
                            padding: 20,
                            textAlign: 'center',
                            color: '#ff7070',
                            fontSize: 13,
                          }}>
                            Veri render edilemedi: {error.message}
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}

                {/* Seçili Folder ve File Listesi */}
                {selectedFolder && (
                  <div style={{
                    color: '#cccccc',
                    fontSize: 14,
                  }}>
                    {/* Seçili Folder Bilgisi */}
                    <div style={{
                      padding: '12px 16px',
                      background: '#2d2d2d',
                      borderRadius: 6,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}>
                      <button
                        onClick={() => {
                          // Geri dön - folder seçimini temizle
                          setSelectedFolder(null);
                          setSelectedIncludePath(null);
                          setIncludeFilesData(null);
                          setSelectedFile(null);
                        }}
                        style={{
                          background: '#3e3e42',
                          border: 'none',
                          color: '#cccccc',
                          padding: '6px 12px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        ← Geri
                      </button>
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: '#0e639c',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                      }}>
                        📁
                      </div>
                      <div>
                        <div style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#cccccc',
                        }}>
                          {selectedFolder.folderName || selectedFolder.foldername || selectedFolder.name}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#888',
                          fontFamily: 'monospace',
                        }}>
                          ID: {selectedFolder.folderId || selectedFolder.id}
                        </div>
                      </div>
                    </div>

                    {/* File Listesi Loading */}
                    {isLoadingFiles && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 20px',
                        color: '#888',
                      }}>
                        <div style={{
                          width: 32,
                          height: 32,
                          border: '3px solid #3e3e42',
                          borderTopColor: '#0e639c',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginBottom: 12,
                        }} />
                        <div style={{ fontSize: 12 }}>File listesi yükleniyor...</div>
                      </div>
                    )}

                    {/* File Listesi */}
                    {!isLoadingFiles && includeFilesData && (
                      <div>
                        {(() => {
                          try {
                            let filesList = [];
                            
                            if (Array.isArray(includeFilesData)) {
                              filesList = includeFilesData;
                            } else if (includeFilesData.result && Array.isArray(includeFilesData.result)) {
                              filesList = includeFilesData.result;
                            } else if (includeFilesData.data && Array.isArray(includeFilesData.data)) {
                              filesList = includeFilesData.data;
                            }
                            
                            if (filesList.length === 0) {
                              return (
                                <div style={{
                                  padding: 20,
                                  textAlign: 'center',
                                  color: '#888',
                                  fontSize: 13,
                                }}>
                                  Bu folder'da file bulunamadı.
                                </div>
                              );
                            }
                            
                            return (
                              <div style={{
                                display: 'grid',
                                gap: 8,
                              }}>
                                {filesList.map((file, index) => {
                                  // Response yapısına göre: diagrun_id ve filename
                                  const fileId = file.diagrun_id || file.fileId || file.id || String(index);
                                  const fileName = file.filename || file.fileName || file.name || 'Unnamed';
                                  const lastEdit = file.last_edit || file.lastEdit || null;
                                  const lastCompileDate = file.last_compile_date || file.lastCompileDate || null;
                                  const isSelected = selectedFile === fileId;
                                  
                                  return (
                                    <div
                                      key={fileId}
                                      onClick={() => {
                                        setSelectedFile(fileId);
                                      }}
                                      style={{
                                        padding: '12px 16px',
                                        background: isSelected ? '#0e639c' : 'transparent',
                                        border: isSelected ? '2px solid #3e9fdb' : '1px solid #3e3e42',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        transition: 'all 0.2s',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background = '#3e3e42';
                                          e.currentTarget.style.borderColor = '#555';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.background = 'transparent';
                                          e.currentTarget.style.borderColor = '#3e3e42';
                                        }
                                      }}
                                    >
                                      <div style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 6,
                                        background: isSelected ? '#3e9fdb' : '#3e3e42',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 20,
                                        flexShrink: 0,
                                      }}>
                                        📄
                                      </div>
                                      <div style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                      }}>
                                        <div style={{
                                          fontSize: 15,
                                          fontWeight: 600,
                                          color: '#cccccc',
                                        }}>
                                          {fileName}
                                        </div>
                                        <div style={{
                                          fontSize: 11,
                                          color: '#888',
                                          fontFamily: 'monospace',
                                          display: 'flex',
                                          gap: 12,
                                          flexWrap: 'wrap',
                                        }}>
                                          <span>ID: {fileId}</span>
                                          {lastEdit && (
                                            <span>Son Düzenleme: {lastEdit}</span>
                                          )}
                                          {lastCompileDate && (
                                            <span>Son Derleme: {lastCompileDate}</span>
                                          )}
                                        </div>
                                      </div>
                                      {isSelected && (
                                        <div style={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          background: '#4caf50',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0,
                                        }}>
                                          <span style={{ fontSize: 14, color: 'white' }}>✓</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          } catch (error) {
                            console.error('File list render hatası:', error);
                            return (
                              <div style={{
                                padding: 20,
                                textAlign: 'center',
                                color: '#ff7070',
                                fontSize: 13,
                              }}>
                                File listesi render edilemedi: {error.message}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Empty State */}
                {!isLoadingIncludeData && !includeError && !includeFoldersData && (
                  <div style={{
                    padding: 20,
                    textAlign: 'center',
                    color: '#888',
                    fontSize: 13,
                  }}>
                    Veri bulunamadı.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                    setSelectedIncludePath(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setSelectedFolder(null);
                    setIncludeFilesData(null);
                    setSelectedFile(null);
                    setIncludeFoldersData(null);
                    setIncludeError(null);
                  }}
                  style={{
                    background: '#3e3e42',
                    border: 'none',
                    color: '#cccccc',
                    padding: '8px 16px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (!selectedFile) {
                      alert('Lütfen bir file seçin!');
                      return;
                    }
                    
                    // Seçili file bilgisini bul
                    let filesList = [];
                    if (Array.isArray(includeFilesData)) {
                      filesList = includeFilesData;
                    } else if (includeFilesData?.result && Array.isArray(includeFilesData.result)) {
                      filesList = includeFilesData.result;
                    } else if (includeFilesData?.data && Array.isArray(includeFilesData.data)) {
                      filesList = includeFilesData.data;
                    }
                    
                    const selectedFileData = filesList.find(f => 
                      (f.diagrun_id || f.fileId || f.id || f.name) === selectedFile
                    );
                    const fileName = selectedFileData?.filename || selectedFileData?.fileName || selectedFileData?.name || selectedFile;
                    const diagrunId = selectedFileData?.diagrun_id || selectedFile;
                    
                    // Include ekleme işlemi - API call
                    if (selectedIncludeShape && diagrunId) {
                      const addInclude = async () => {
                        try {
                          const processId = process.env.REACT_APP_ADD_INCLUDABLE_LAST_SELECT;
                          if (!processId) {
                            throw new Error('REACT_APP_ADD_INCLUDABLE_LAST_SELECT environment variable tanımlı değil!');
                          }
                          
                          const bodyParams = {
                            stepid: selectedIncludeShape,
                            include: diagrunId,
                            process: processId,
                          };
                          
                          const response = await fetchWrapper(processId, bodyParams, navigate);
                          
                          // Response kontrolü - nested result yapısı
                          // Response: { result: { result: 1, message: "Success" } }
                          const resultData = response?.result;
                          const isSuccess = resultData && 
                                           (resultData.result === 1 || resultData.result === "1") && 
                                           (resultData.message === 'success' || resultData.message === 'Success');
                          
                          if (response && isSuccess) {
                            // Başarılı - Include path'i kaydet ve icon ekle
                            const includePath = diagrunId;
                            const foldername = selectedFolder?.folderName || selectedFolder?.foldername || '';

                            // Aktif tab'ın workflowData'sına include bilgisini yaz
                            if (activeTabId) {
                              setTabs(prev => prev.map(tab => {
                                if (tab.id !== activeTabId) return tab;
                                const currentData = tab.workflowData || workflowDataRef.current;
                                if (!currentData?.contents) return tab;
                                const updatedContents = currentData.contents.map(item => {
                                  if (!item || String(item.id) !== String(selectedIncludeShape)) {
                                    return item;
                                  }
                                  return {
                                    ...item,
                                    include: includePath,
                                    filename: fileName || item.filename || '',
                                    foldername: foldername || item.foldername || item.folderName || '',
                                  };
                                });
                                return {
                                  ...tab,
                                  workflowData: {
                                    ...currentData,
                                    contents: updatedContents,
                                  },
                                };
                              }));
                            }
                            
                            setShapeIncludePaths(prev => ({
                              ...prev,
                              [selectedIncludeShape]: includePath
                            }));
                            
                            // Foldername'i kaydet (Remove Include için)
                            setShapeIncludeFolders(prev => ({
                              ...prev,
                              [selectedIncludeShape]: foldername
                            }));
                            
                            // Include edilmiş şekiller listesine ekle
                            setShapesWithInclude(prev => new Set([...prev, selectedIncludeShape]));
                            
                            // mxGraph'te şeklin sağ alt köşesine icon ekle ve label'ı güncelle
                            if (graphRef.current) {
                              const graph = graphRef.current;
                              const model = graph.getModel();
                              const cell = model.getCell(selectedIncludeShape);
                              
                              if (cell && model.isVertex(cell)) {
                                // Önce mevcut label'ı sakla (Remove Include için)
                                const currentLabel = cell.getValue() ? String(cell.getValue()) : '';
                                setShapeOriginalLabels(prev => ({
                                  ...prev,
                                  [selectedIncludeShape]: currentLabel
                                }));
                                
                                // Şeklin label'ını filename ile güncelle
                                model.beginUpdate();
                                try {
                                  cell.setValue(fileName);
                                  graph.refresh(cell);
                                } finally {
                                  model.endUpdate();
                                }
                                
                                // Önce mevcut overlay'i kaldır (varsa)
                                graph.removeCellOverlay(cell);
                                
                                // Yeni overlay oluştur - attach icon
                                const attachIcon = createAttachIcon();
                                
                                // Sağ alt köşe için overlay oluştur
                                // mxPoint offset: x negatif = sağa, y negatif = aşağı
                                const overlay = new mxCellOverlay(
                                  attachIcon,
                                  'Include edildi',
                                  mxConstants.ALIGN_RIGHT,
                                  mxConstants.ALIGN_BOTTOM,
                                  new mxPoint(-2, -2) // Sağ alt köşede, şeklin içinde
                                );
                                
                                // Overlay'e click event ekle
                                overlay.addListener(mxEvent.CLICK, function(sender, evt) {
                                  const cell = evt.getProperty('cell');
                                  if (cell) {
                                    openIncludeDetails(cell.getId(), { showPopup: false });
                                  }
                                });
                                
                                // Overlay'i ekle
                                graph.addCellOverlay(cell, overlay);
                                
                                // Graph'i refresh et ve validate et - overlay'in görünmesi için
                                graph.getView().invalidate(cell);
                                graph.getView().validate();
                                graph.refresh();
                                
                                // Cell'in pozisyonunu ve boyutunu console'da göster
                                const geo = cell.getGeometry();
                              }
                            }
                            
                          } else {
                            throw new Error(response?.message || 'Include ekleme başarısız');
                          }
                        } catch (error) {
                          alert(`Include eklenirken hata oluştu: ${error.message}`);
                          return; // Hata durumunda modal'ı kapatma
                        }
                      };
                      
                      // Async işlemi başlat
                      addInclude().then(() => {
                        // Başarılı olduysa modal'ı kapat
                    setShowIncludeModal(false);
                    setSelectedIncludeShape(null);
                        setSelectedIncludePath(null);
                        setSelectedFolder(null);
                        setIncludeFilesData(null);
                        setSelectedFile(null);
                        setIncludeFoldersData(null);
                        setIncludeError(null);
                      }).catch(() => {
                        // Hata durumunda modal açık kalsın
                      });
                    } else {
                      // Modal'ı kapat
                      setShowIncludeModal(false);
                      setSelectedIncludeShape(null);
                      setSelectedIncludePath(null);
                      setSelectedFolder(null);
                      setIncludeFilesData(null);
                      setSelectedFile(null);
                      setIncludeFoldersData(null);
                      setIncludeError(null);
                    }
                  }}
                  disabled={!selectedFile}
                  style={{
                    background: selectedFile ? '#0e639c' : '#3e3e42',
                    border: 'none',
                    color: 'white',
                    padding: '8px 20px',
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: selectedFile ? 'pointer' : 'not-allowed',
                    fontWeight: 500,
                    opacity: selectedFile ? 1 : 0.5,
                  }}
                >
                  Include Ekle {selectedFile && `(${selectedFile})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {false && showIncludeInfoModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100002,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowIncludeInfoModal(false);
              setIncludeInfoData(null);
            }
          }}
          >
            <div style={{
              background: '#2d2d2d',
              borderRadius: 8,
              padding: 20,
              width: '90%',
              maxWidth: 520,
              border: '1px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}>
                <h3 style={{ color: '#cccccc', margin: 0, fontSize: 16, fontWeight: 600 }}>
                  🔎 Include Bilgisi
                </h3>
                <button
                  onClick={() => {
                    setShowIncludeInfoModal(false);
                    setIncludeInfoData(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 22,
                    cursor: 'pointer',
                    padding: 0,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{
                background: '#1e1e1e',
                borderRadius: 4,
                padding: 12,
                color: '#cccccc',
                fontSize: 13,
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                rowGap: 10,
                columnGap: 10,
              }}>
                <div style={{ color: '#888' }}>Shape ID</div>
                <div>{includeInfoData?.shapeId || '—'}</div>
                <div style={{ color: '#888' }}>Include ID</div>
                <div>{includeInfoData?.include || '—'}</div>
                <div style={{ color: '#888' }}>Folder</div>
                <div>{includeInfoData?.foldername || '—'}</div>
                <div style={{ color: '#888' }}>Filename</div>
                <div>{includeInfoData?.filename || '—'}</div>
              </div>
            </div>
          </div>
        )}
      
      <FlowChartTerminalDock
        isOpen={showTerminal}
        onToggle={() => setShowTerminal(false)}
        lines={terminalLines}
        onSubmit={handleTerminalSubmit}
        onClear={handleTerminalClear}
        statusLabel={isTerminalBusy ? 'Working' : selectedShape ? 'Connected' : 'Idle'}
        isBusy={isTerminalBusy}
        inputValue={terminalInput}
        onInputChange={setTerminalInput}
      />
      <FlowChartStatusBar
        saveStatus={saveStatus}
        isDesignMode={isDesignMode}
        showCodeEditor={showCodeEditor}
        selectedShape={selectedShape}
        moveRequestStatus={moveRequestStatus}
      />

      {/* ── Yüzer (floating) code editor panelleri ── */}
      {floatingEditors.map((f) => (
        <FloatingCodeEditor
          key={f.id}
          id={f.id}
          shapeId={f.shapeId}
          shapeLabel={f.shapeLabel}
          initialCode={f.code}
          initialPrompt={f.prompt}
          onCodeChange={handleFloatingCodeChange}
          onPromptChange={handleFloatingPromptChange}
          onClose={() => handleFloatingClose(f.id)}
          saveScript={handleFloatingSave}
          defaultX={f.x}
          defaultY={f.y}
          defaultW={f.w}
          defaultH={f.h}
        />
      ))}
    </div>
  );
  
  return createPortal(modalContent, document.body);
};

export default FlowChartDevelopmentScreen;